/**
 * Location Tracking Admin Panel - Main Entry Point
 */

import { onAuthChange, loginWithEmail, logout, getCurrentUser, subscribeToPresence, subscribeToDeviceStatus, subscribeToDeviceNotifications, subscribeToRecoveryStatus, getIdToken } from './config/firebase.js';
import * as api from './services/api.js';
import toast from './services/toast.js';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import {
    formatDuration,
    formatDate,
    formatRelativeTime,
    getTimeRemaining,
    parseUserIds,
    isValidUserId,
    getActiveModeName,
    getEffectiveInterval,
    escapeHtml,
    debounce
} from './utils/helpers.js';

// ============================================================================
// APP STATE
// ============================================================================
const STORAGE_KEY = 'adminPanelState';

// Load persisted state from localStorage
function loadPersistedState() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Convert date strings back to Date objects
            if (parsed.mapDateRange) {
                if (parsed.mapDateRange.start) {
                    parsed.mapDateRange.start = new Date(parsed.mapDateRange.start);
                }
                if (parsed.mapDateRange.end) {
                    parsed.mapDateRange.end = new Date(parsed.mapDateRange.end);
                }
            }
            // Load geofence state
            if (parsed.geofenceState) {
                geofenceTargetUserId = parsed.geofenceState.targetUserId || null;
                geofenceDraft = parsed.geofenceState.draft || { name: '', description: '', expiry: '' };
                geofenceDraftModalOpen = parsed.geofenceState.draftModalOpen || false;
                geofenceDraftMinimized = parsed.geofenceState.draftMinimized || false;
                geofenceMapLocked = parsed.geofenceState.mapLocked || false;
                geofenceDrawingMode = parsed.geofenceState.drawingMode || false;
                geofencePreviewMode = parsed.geofenceState.previewMode || false;
                geofenceViewOnlyMode = parsed.geofenceState.viewOnlyMode || false;
                geofenceUserDataLoaded = parsed.geofenceState.userDataLoaded || false;
                geofencePencilMode = parsed.geofenceState.pencilMode || false;
            }
            return parsed;
        }
    } catch (e) {
        console.error('Failed to load persisted state:', e);
    }
    return {};
}

// Save state to localStorage
function saveState() {
    try {
        const toSave = {
            currentPage: state.currentPage,
            emailPage: state.emailPage,
            mapDateRange: {
                start: state.mapDateRange.start instanceof Date ? state.mapDateRange.start.toISOString() : state.mapDateRange.start,
                end: state.mapDateRange.end instanceof Date ? state.mapDateRange.end.toISOString() : state.mapDateRange.end
            },
            selectedMapUser: state.selectedMapUser,
            usersPageInput: state.usersPageInput,
            geofenceState: {
                targetUserId: geofenceTargetUserId,
                draft: geofenceDraft,
                draftModalOpen: geofenceDraftModalOpen,
                draftMinimized: geofenceDraftMinimized,
                mapLocked: geofenceMapLocked,
                drawingMode: geofenceDrawingMode,
                previewMode: geofencePreviewMode,
                viewOnlyMode: geofenceViewOnlyMode,
                userDataLoaded: geofenceUserDataLoaded,
                pencilMode: geofencePencilMode
            }
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
        console.error('Failed to save state:', e);
    }
}

const persistedState = loadPersistedState();

// Ensure mapDateRange has valid Date objects
const defaultMapDateRange = {
    start: new Date(Date.now() - 24 * 60 * 60 * 1000), // 24 hours ago
    end: new Date()
};

const validMapDateRange = persistedState.mapDateRange &&
    persistedState.mapDateRange.start instanceof Date &&
    persistedState.mapDateRange.end instanceof Date &&
    !isNaN(persistedState.mapDateRange.start.getTime()) &&
    !isNaN(persistedState.mapDateRange.end.getTime())
    ? persistedState.mapDateRange
    : defaultMapDateRange;

const state = {
    user: null,
    currentPage: persistedState.currentPage || 'dashboard',
    mobileMenuOpen: false, // Track sidebar state across re-renders
    loadedUsers: new Map(), // userId -> settings data
    geofences: [],
    geofencesLoaded: false,
    defaults: null,
    loading: false,
    // Map-related state (Google Maps Embed)
    mapLocations: [],
    selectedMapUser: persistedState.selectedMapUser || null,
    mapDateRange: validMapDateRange,
    // Users page state
    usersPageInput: persistedState.usersPageInput || '',
    // Email page state
    emailPage: persistedState.emailPage || {
        userId: '',
        userInfo: null,
        selectedTemplate: '',
        senderEmail: '',
        subject: '',
        bodyText: '',
        isEditing: false
    },
    // FCM page state
    fcmPage: {
        userId: '',
        deviceStatus: null,
        wakeHistory: [],
        responseLogs: [],
        notifications: [],
        statusLogs: [],
        isLoading: false,
        instantWakeEnabled: true, // Default ON so admin can always send FCM
        continuousCycleEnabled: false, // Default OFF - single FCM mode. When ON, enables 12-hour cycle
        autoRefreshInterval: null,
        fcmStatusUnsubscribe: null, // Firebase realtime listener cleanup
        lastRefreshTime: null,
        presence: { online: false, lastSeen: null }, // Realtime DB presence data
        recoveryStatus: { active: false, status: 'none' }, // Recovery state from backend
        backendMonitorInterval: null // 1-second ticker for countdowns
    }
};


// ============================================================================
// ICONS
// ============================================================================
const icons = {
    dashboard: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>`,
    users: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>`,
    settings: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>`,
    broadcast: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"></path><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"></path><circle cx="12" cy="12" r="2"></circle><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"></path><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"></path></svg>`,
    history: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>`,
    logout: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>`,
    location: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`,
    map: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>`,
    clock: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>`,
    zap: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>`,
    alert: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`,
    shield: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>`,
    check: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`,
    x: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`,
    refresh: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>`,
    save: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>`,
    search: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>`,
    play: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`,
    pause: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>`,
    calendar: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`,
    navigation: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>`,
    chevronDown: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`,
    email: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`,
    send: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`,
    edit: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`,
    bell: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>`,
    phone: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>`,
    battery: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" ry="2"></rect><line x1="23" y1="13" x2="23" y2="11"></line></svg>`,
    wifi: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"></path><path d="M1.42 9a16 16 0 0 1 21.16 0"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>`,
    wifiOff: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path><path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>`,
    signal: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path><path d="M7 20v-4"></path><path d="M12 20v-8"></path><path d="M17 20V8"></path><path d="M22 4v16"></path></svg>`
};

// ============================================================================
// INITIALIZATION
// ============================================================================
document.addEventListener('DOMContentLoaded', () => {
    // Handle hash-based routing on page load
    const hash = window.location.hash.replace('#', '');
    const validPages = ['dashboard', 'map', 'users', 'geofences', 'broadcast', 'email', 'defaults', 'fcm'];
    if (hash && validPages.includes(hash)) {
        state.currentPage = hash;
    }

    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', () => {
        const newHash = window.location.hash.replace('#', '');
        if (newHash && validPages.includes(newHash) && newHash !== state.currentPage) {
            state.currentPage = newHash;
            saveState();
            if (state.user) {
                renderApp();
            }
        }
    });

    // Listen for auth state changes
    onAuthChange(async (user) => {
        if (user) {
            // Check if user has admin role by fetching their profile from backend
            try {
                console.log('Checking admin role for user:', user.uid);
                const profileResponse = await api.getUserProfile(user.uid);
                console.log('Profile response:', profileResponse);

                // Backend returns: { success: true, data: { ...profile, role: "admin" } }
                const userRole = profileResponse?.data?.role || null;
                console.log('User role:', userRole);

                if (userRole === 'admin') {
                    console.log('✅ Admin access granted');
                    state.user = user;
                    // Load defaults
                    try {
                        const defaultsResponse = await api.getDefaultSettings();
                        state.defaults = defaultsResponse.defaults;
                    } catch (e) {
                        console.error('Failed to load defaults:', e);
                    }
                    hideLoading();
                    renderApp();
                } else {
                    // Not admin, sign out and show login page
                    console.log('❌ Access denied - User role:', userRole);
                    await logout();
                    state.user = null;
                    hideLoading();
                    renderLoginPage();
                    toast.error('Access denied. Admin privileges required.');
                }
            } catch (error) {
                console.error('Error checking admin role:', error);
                // If can't verify role, sign out
                await logout();
                state.user = null;
                hideLoading();
                renderLoginPage();
                toast.error('Unable to verify admin access.');
            }
        } else {
            state.user = null;
            hideLoading();
            renderLoginPage();
        }
    });
});

function showLoading() {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
        loadingScreen.classList.remove('hidden');
    }
}

function hideLoading() {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
        loadingScreen.classList.add('hidden');
    }
}

// ============================================================================
// RENDER LOGIN PAGE
// ============================================================================
function renderLoginPage() {
    const app = document.getElementById('app');
    app.innerHTML = `
        <div class="login-container">
            <div class="login-card">
                <div class="login-header">
                    <img src="/favicon.svg" alt="Logo" class="login-logo">
                    <h1 class="login-title">Admin Panel</h1>
                    <p class="login-subtitle">Location Tracking Settings Management</p>
                </div>
                <form id="login-form">
                    <div class="form-group">
                        <label class="form-label">Email Address</label>
                        <input type="email" id="login-email" class="form-input" placeholder="admin@example.com" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Password</label>
                        <input type="password" id="login-password" class="form-input" placeholder="••••••••" required>
                    </div>
                    <div id="login-error" class="form-error hidden"></div>
                    <button type="submit" id="login-btn" class="btn btn-primary btn-block btn-lg">
                        Sign In
                    </button>
                </form>
            </div>
        </div>
    `;

    // Attach login handler
    document.getElementById('login-form').addEventListener('submit', handleLogin);
}

async function handleLogin(e) {
    e.preventDefault();

    const email = document.getElementById('login-email').value;
    const password = document.getElementById('login-password').value;
    const btn = document.getElementById('login-btn');
    const errorDiv = document.getElementById('login-error');

    btn.disabled = true;
    btn.textContent = 'Verifying credentials...';
    errorDiv.classList.add('hidden');

    const result = await loginWithEmail(email, password);

    if (!result.success) {
        errorDiv.textContent = result.error || 'Incorrect details or not an admin user.';
        errorDiv.classList.remove('hidden');
        btn.disabled = false;
        btn.textContent = 'Sign In';
    }
    // If success, onAuthChange will handle the rest (checking admin role and rendering app)
}

// ============================================================================
// RENDER MAIN APP
// ============================================================================
function renderApp() {
    const app = document.getElementById('app');
    app.innerHTML = `
        <div class="main-layout">
            <!-- Mobile Menu Toggle Button -->
            <button class="mobile-menu-toggle" id="mobile-menu-toggle" aria-label="Toggle menu">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
            </button>
            <!-- Sidebar Overlay for Mobile -->
            <div class="sidebar-overlay" id="sidebar-overlay"></div>
            ${renderSidebar()}
            <main class="main-content">
                ${renderHeader()}
                <div class="content" id="page-content">
                    ${renderPageContent()}
                </div>
            </main>
        </div>
        ${renderModals()}
    `;

    // Restore mobile menu state after re-render
    if (state.mobileMenuOpen && window.innerWidth <= 992) {
        const sidebar = document.querySelector('.sidebar');
        const overlay = document.getElementById('sidebar-overlay');
        const menuToggle = document.getElementById('mobile-menu-toggle');
        if (sidebar) sidebar.classList.add('open');
        if (overlay) overlay.classList.add('active');
        if (menuToggle) {
            menuToggle.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            `;
        }
    }

    attachEventListeners();
    attachMobileMenuListeners();
    if (state.currentPage === 'geofences') {
        attachGeofencePageListeners();
        if (!state.geofencesLoaded) {
            loadGeofences();
        }
    }
}

function renderSidebar() {
    const user = state.user;
    const initial = user?.email ? user.email.charAt(0).toUpperCase() : 'A';

    return `
        <aside class="sidebar">
            <div class="sidebar-header">
                <div class="sidebar-logo">
                    <img src="/favicon.svg" alt="Logo">
                    <span class="sidebar-logo-text">Tracking Admin</span>
                </div>
            </div>
            <nav class="sidebar-nav">
                <div class="nav-section">
                    <div class="nav-section-title">Main</div>
                    <button class="nav-item ${state.currentPage === 'dashboard' ? 'active' : ''}" data-page="dashboard">
                        ${icons.dashboard}
                        <span>Dashboard</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'map' ? 'active' : ''}" data-page="map">
                        ${icons.map}
                        <span>Location Map</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'users' ? 'active' : ''}" data-page="users">
                        ${icons.users}
                        <span>User Settings</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'geofences' ? 'active' : ''}" data-page="geofences">
                        ${icons.location}
                        <span>Geofence</span>
                    </button>
                </div>
                <div class="nav-section">
                    <div class="nav-section-title">Admin Actions</div>
                    <button class="nav-item ${state.currentPage === 'fcm' ? 'active' : ''}" data-page="fcm">
                        ${icons.bell}
                        <span>FCM/Notifications</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'broadcast' ? 'active' : ''}" data-page="broadcast">
                        ${icons.broadcast}
                        <span>Broadcast</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'email' ? 'active' : ''}" data-page="email">
                        ${icons.email}
                        <span>Email</span>
                    </button>
                    <button class="nav-item ${state.currentPage === 'defaults' ? 'active' : ''}" data-page="defaults">
                        ${icons.settings}
                        <span>Default Settings</span>
                    </button>
                </div>
            </nav>
            <div class="sidebar-footer">
                <div class="user-info">
                    <div class="user-avatar">${initial}</div>
                    <div class="user-details">
                        <div class="user-name">${escapeHtml(user?.email || 'Admin')}</div>
                        <div class="user-role">Administrator</div>
                    </div>
                    <button class="btn btn-icon" id="logout-btn" title="Logout">
                        ${icons.logout}
                    </button>
                </div>
            </div>
        </aside>
    `;
}

function renderHeader() {
    const titles = {
        dashboard: 'Dashboard',
        map: 'Location Map',
        users: 'User Settings',
        geofences: 'Geofence Management',
        broadcast: 'Broadcast Settings',
        email: 'Email Management',
        defaults: 'Default Settings',
        fcm: 'FCM / Notifications'
    };

    return `
        <header class="header">
            <div class="header-left">
                <h1 class="page-title">${titles[state.currentPage] || 'Dashboard'}</h1>
            </div>
            <div class="header-right">
                <button class="btn btn-ghost btn-sm" id="clear-session-btn" title="Clear all saved form data">
                    ${icons.x} Clear Session
                </button>
                <button class="btn btn-secondary btn-sm" id="refresh-btn">
                    ${icons.refresh} Refresh
                </button>
            </div>
        </header>
    `;
}

function renderPageContent() {
    switch (state.currentPage) {
        case 'dashboard':
            return renderDashboardPage();
        case 'map':
            return renderMapPage();
        case 'users':
            return renderUsersPage();
        case 'geofences':
            return renderGeofencesPage();
        case 'broadcast':
            return renderBroadcastPage();
        case 'email':
            return renderEmailPage();
        case 'defaults':
            return renderDefaultsPage();
        case 'fcm':
            return renderFCMPage();
        default:
            return renderDashboardPage();
    }
}

// ============================================================================
// DASHBOARD PAGE
// ============================================================================
function renderDashboardPage() {
    return `
        <div class="alert alert-info mb-3">
            <span>${icons.alert}</span>
            <div>
                <strong>Welcome to Location Tracking Admin Panel</strong><br>
                Use this panel to manage location tracking settings for users. You can set intervals, enable special modes, and broadcast settings to all users.
            </div>
        </div>

        <div class="stats-grid">
            <div class="stat-card">
                <div class="stat-label">Default Update Interval</div>
                <div class="stat-value">${state.defaults ? formatDuration(state.defaults.updateInterval) : 'Loading...'}</div>
            </div>
            <div class="stat-card">
                <div class="stat-label">Realtime Interval</div>
                <div class="stat-value">${state.defaults ? formatDuration(state.defaults.realtimeInterval) : 'Loading...'}</div>
            </div>
            <div class="stat-card">
                <div class="stat-label">Force Check Interval</div>
                <div class="stat-value">${state.defaults ? formatDuration(state.defaults.forceCheckInterval) : 'Loading...'}</div>
            </div>
            <div class="stat-card">
                <div class="stat-label">Emergency Interval</div>
                <div class="stat-value">${state.defaults ? formatDuration(state.defaults.emergencyInterval) : 'Loading...'}</div>
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div>
                    <h3 class="card-title">Quick Actions</h3>
                    <p class="card-subtitle">Common administrative tasks</p>
                </div>
            </div>
            <div class="card-body">
                <div class="d-flex gap-2" style="flex-wrap: wrap;">
                    <button class="btn btn-primary" data-page="users">
                        ${icons.users} Manage User Settings
                    </button>
                    <button class="btn btn-warning" data-page="geofences">
                        ${icons.location} Geofence Management
                    </button>
                    <button class="btn btn-warning" data-page="broadcast">
                        ${icons.broadcast} Broadcast to All
                    </button>
                    <button class="btn btn-secondary" data-page="defaults">
                        ${icons.settings} View Defaults
                    </button>
                </div>
            </div>
        </div>
    `;
}

// ============================================================================
// GEOFENCE PAGE
// ============================================================================
let geofenceMap = null;
let geofencePolygonLayer = null;
let geofenceTargetUserId = null;
let geofenceUserLastLocation = null;
let geofenceDrawingMode = false;
let geofencePreviewMode = false;
let geofenceViewOnlyMode = false;
let geofencePreviewGeometry = null;
let geofenceUserDataLoaded = false;
let geofenceMapLocked = false;
let geofencePencilMode = false;
let geofencePencilDrawing = false;
let geofencePencilStrokePoints = [];
let geofencePencilInkLayer = null;
let geofenceActivePointerId = null;
let geofencePointerCleanup = null;
let geofenceEdgeGlowLayer = null;
let geofenceEdgeShineLayer = null;
let geofenceEdgeShineTimer = null;
let geofenceDraft = { name: '', description: '', expiry: '' };
let geofenceDraftModalOpen = false;
let geofenceDraftMinimized = false;

function clearGeofenceEdgeEffects() {
    if (geofenceEdgeShineTimer) {
        clearInterval(geofenceEdgeShineTimer);
        geofenceEdgeShineTimer = null;
    }

    if (geofenceMap && geofenceEdgeGlowLayer) {
        geofenceMap.removeLayer(geofenceEdgeGlowLayer);
    }
    if (geofenceMap && geofenceEdgeShineLayer) {
        geofenceMap.removeLayer(geofenceEdgeShineLayer);
    }

    geofenceEdgeGlowLayer = null;
    geofenceEdgeShineLayer = null;
}

function getGeofenceLayerBounds(layer) {
    if (!layer) return null;
    if (typeof layer.getBounds === 'function') {
        return layer.getBounds();
    }
    return null;
}

function fitGeofenceLayerInView(layer) {
    if (!geofenceMap || !layer) return;
    const bounds = getGeofenceLayerBounds(layer);
    if (bounds && bounds.isValid && bounds.isValid()) {
        geofenceMap.fitBounds(bounds, {
            padding: [48, 48],
            maxZoom: 17
        });
    }
}

function applyGeofenceEdgeEffects(layer) {
    if (!geofenceMap || !layer || typeof layer.toGeoJSON !== 'function') return;

    clearGeofenceEdgeEffects();

    const geometry = layer.toGeoJSON();
    geofenceEdgeGlowLayer = L.geoJSON(geometry, {
        interactive: false,
        style: {
            color: '#ffffff',
            weight: 10,
            opacity: 0.42,
            fillOpacity: 0,
            lineCap: 'round',
            lineJoin: 'round'
        }
    }).addTo(geofenceMap);

    geofenceEdgeShineLayer = L.geoJSON(geometry, {
        interactive: false,
        style: {
            color: '#ffffff',
            weight: 5,
            opacity: 1,
            fillOpacity: 0,
            dashArray: '12 12',
            dashOffset: '0',
            lineCap: 'round',
            lineJoin: 'round'
        }
    }).addTo(geofenceMap);

    let dashOffset = 0;
    geofenceEdgeShineTimer = setInterval(() => {
        if (!geofenceEdgeShineLayer) return;
        dashOffset = (dashOffset + 2) % 120;
        geofenceEdgeShineLayer.setStyle({
            dashOffset: `${dashOffset}`
        });
    }, 30);
}

function styleGeofencePolygonLayer(layer, { focus = false, disablePm = true, animate = false } = {}) {
    if (!layer) return;

    if (typeof layer.setStyle === 'function') {
        layer.setStyle({
            color: '#7C3AED',
            fillColor: 'rgba(124, 58, 237, 0.12)',
            fillOpacity: 0.12,
            weight: 3
        });
    }

    if (disablePm && layer.pm) {
        layer.pm.disable();
    }

    if (animate) {
        applyGeofenceEdgeEffects(layer);
    } else {
        clearGeofenceEdgeEffects();
    }

    if (focus) {
        fitGeofenceLayerInView(layer);
    }
}

function syncGeofenceLockButton() {
    const btn = document.getElementById('lock-map-btn');
    if (!btn) return;

    if (geofenceMapLocked) {
        btn.textContent = '🔓 Unlock Map';
        btn.classList.remove('btn-secondary');
        btn.classList.add('btn-primary');
    } else {
        btn.textContent = '🔒 Lock Map';
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-secondary');
    }
}

function applyGeofenceMapLockState() {
    if (!geofenceMap) return;

    if (geofenceMapLocked) {
        geofenceMap.dragging.disable();
        geofenceMap.scrollWheelZoom.disable();
        geofenceMap.doubleClickZoom.disable();
        geofenceMap.touchZoom.disable();
        geofenceMap.boxZoom.disable();
        geofenceMap.keyboard.disable();
    } else {
        geofenceMap.dragging.enable();
        geofenceMap.scrollWheelZoom.enable();
        geofenceMap.doubleClickZoom.enable();
        geofenceMap.touchZoom.enable();
        geofenceMap.boxZoom.enable();
        geofenceMap.keyboard.enable();
    }

    const mapContainer = geofenceMap.getContainer();
    if (mapContainer) {
        mapContainer.style.touchAction = 'none';
    }

    if (!geofenceMapLocked && geofencePencilDrawing) {
        geofencePencilDrawing = false;
        geofenceActivePointerId = null;
        geofencePencilStrokePoints = [];
        if (geofencePencilInkLayer) {
            geofenceMap.removeLayer(geofencePencilInkLayer);
            geofencePencilInkLayer = null;
        }
        mapContainer?.classList.remove('geofence-pencil-drawing');
    }
}

function updateGeofenceDrawButtonState() {
    const drawBtn = document.getElementById('enable-draw-btn');
    if (drawBtn) {
        const active = Boolean(geofencePencilMode);
        drawBtn.classList.toggle('is-active', active);
        drawBtn.classList.toggle('btn-primary', active);
        drawBtn.classList.toggle('btn-secondary', !active);
        drawBtn.setAttribute('aria-pressed', String(active));
    }
}

function enableGeofencePencilDrawing() {
    if (!geofenceMap) return;

    geofenceMap.pm.disableDraw();
    geofencePencilMode = true;
    bindGeofencePencilPointerEvents();
    geofenceMap.getContainer().style.cursor = 'crosshair';
    updateGeofenceDrawButtonState();
}

function disableGeofencePencilDrawing() {
    geofencePencilMode = false;
    geofencePencilDrawing = false;
    geofencePencilStrokePoints = [];
    geofenceActivePointerId = null;

    if (geofenceMap) {
        geofenceMap.pm.disableDraw();
        geofenceMap.getContainer().classList.remove('geofence-pencil-drawing');
        if (geofencePencilInkLayer) {
            geofenceMap.removeLayer(geofencePencilInkLayer);
            geofencePencilInkLayer = null;
        }
    }

    if (geofencePointerCleanup) {
        geofencePointerCleanup();
        geofencePointerCleanup = null;
    }

    updateGeofenceDrawButtonState();
}

function bindGeofencePencilPointerEvents() {
    if (!geofenceMap) return;

    if (geofencePointerCleanup) {
        geofencePointerCleanup();
        geofencePointerCleanup = null;
    }

    const container = geofenceMap.getContainer();
    if (!container) return;

    const toLatLng = (event) => {
        const rect = container.getBoundingClientRect();
        const point = L.point(event.clientX - rect.left, event.clientY - rect.top);
        return geofenceMap.containerPointToLatLng(point);
    };

    const finishStroke = () => {
        if (!geofenceMap || !geofencePencilDrawing) return;

        geofencePencilDrawing = false;
        geofenceActivePointerId = null;
        container.classList.remove('geofence-pencil-drawing');

        if (!geofencePencilStrokePoints || geofencePencilStrokePoints.length < 3) {
            if (geofencePencilInkLayer) {
                geofenceMap.removeLayer(geofencePencilInkLayer);
                geofencePencilInkLayer = null;
            }
            geofencePencilStrokePoints = [];
            toast.error('Stroke too short. Press and drag to draw a full boundary.');
            return;
        }

        const closedPoints = [...geofencePencilStrokePoints];
        const first = closedPoints[0];
        const last = closedPoints[closedPoints.length - 1];
        if (first && last && !first.equals(last)) {
            closedPoints.push(first);
        }

        if (geofencePencilInkLayer) {
            geofenceMap.removeLayer(geofencePencilInkLayer);
            geofencePencilInkLayer = null;
        }

        if (geofencePolygonLayer) {
            geofenceMap.removeLayer(geofencePolygonLayer);
            geofencePolygonLayer = null;
        }

        geofencePolygonLayer = L.polygon(closedPoints, {
            color: '#7C3AED',
            fillColor: 'rgba(124, 58, 237, 0.12)',
            fillOpacity: 0.12,
            weight: 3
        }).addTo(geofenceMap);

        styleGeofencePolygonLayer(geofencePolygonLayer, { focus: true, disablePm: true });

        // Disable PM completely to prevent vertex markers from appearing
        if (geofencePolygonLayer && geofencePolygonLayer.pm) {
            geofencePolygonLayer.pm.disable();
        }

        geofencePencilStrokePoints = [];
        toast.success('Boundary captured from pencil stroke.');
    };

    const onPointerDown = (event) => {
        if (!geofencePencilMode || !geofenceMapLocked) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        event.preventDefault();
        geofencePencilDrawing = true;
        geofenceActivePointerId = event.pointerId;
        geofencePencilStrokePoints = [toLatLng(event)];
        container.classList.add('geofence-pencil-drawing');

        if (geofencePencilInkLayer) {
            geofenceMap.removeLayer(geofencePencilInkLayer);
            geofencePencilInkLayer = null;
        }

        geofencePencilInkLayer = L.polyline(geofencePencilStrokePoints, {
            color: '#EF4444',
            weight: 4,
            lineCap: 'round',
            lineJoin: 'round'
        }).addTo(geofenceMap);

        try {
            container.setPointerCapture(event.pointerId);
        } catch (error) {
            console.warn('Pointer capture not available:', error);
        }
    };

    const onPointerMove = (event) => {
        if (!geofencePencilDrawing) return;
        if (geofenceActivePointerId !== null && event.pointerId !== geofenceActivePointerId) return;

        event.preventDefault();
        const latLng = toLatLng(event);
        geofencePencilStrokePoints.push(latLng);
        if (geofencePencilInkLayer) {
            geofencePencilInkLayer.setLatLngs(geofencePencilStrokePoints);
        }
    };

    const onPointerUp = (event) => {
        if (!geofencePencilDrawing) return;
        if (geofenceActivePointerId !== null && event.pointerId !== geofenceActivePointerId) return;

        event.preventDefault();
        finishStroke();
    };

    container.addEventListener('pointerdown', onPointerDown, { passive: false });
    container.addEventListener('pointermove', onPointerMove, { passive: false });
    container.addEventListener('pointerup', onPointerUp, { passive: false });
    container.addEventListener('pointercancel', onPointerUp, { passive: false });

    geofencePointerCleanup = () => {
        container.removeEventListener('pointerdown', onPointerDown);
        container.removeEventListener('pointermove', onPointerMove);
        container.removeEventListener('pointerup', onPointerUp);
        container.removeEventListener('pointercancel', onPointerUp);
        container.classList.remove('geofence-pencil-drawing');
    };
}

function getGeofenceDraftName() {
    return (geofenceDraft.name || '').trim();
}

function getGeofenceDraftDescription() {
    return (geofenceDraft.description || '').trim();
}

function syncGeofenceDraftFromModalInputs() {
    const nameInput = document.getElementById('geofence-draft-name-input');
    const descriptionInput = document.getElementById('geofence-draft-description-input');
    const expiryInput = document.getElementById('geofence-draft-expiry-input');

    if (nameInput) {
        geofenceDraft.name = nameInput.value.slice(0, 80);
    }
    if (descriptionInput) {
        geofenceDraft.description = descriptionInput.value;
    }
    if (expiryInput) {
        geofenceDraft.expiry = expiryInput.value;
    }
    saveState();
}

function setGeofenceDraftModalOpen(open, { preserveMinimized = false } = {}) {
    const modal = document.getElementById('geofence-draft-modal');
    if (!modal) {
        geofenceDraftModalOpen = open;
        saveState();
        return;
    }

    geofenceDraftModalOpen = open;
    if (!preserveMinimized) {
        geofenceDraftMinimized = !open;
    }
    saveState();

    if (open) {
        modal.classList.remove('hidden');
        const nameInput = document.getElementById('geofence-draft-name-input');
        const descriptionInput = document.getElementById('geofence-draft-description-input');
        const expiryInput = document.getElementById('geofence-draft-expiry-input');
        if (nameInput) {
            nameInput.value = geofenceDraft.name || '';
            nameInput.focus();
            nameInput.setSelectionRange(nameInput.value.length, nameInput.value.length);
        }
        if (descriptionInput) {
            descriptionInput.value = geofenceDraft.description || '';
        }
        if (expiryInput) {
            expiryInput.value = geofenceDraft.expiry || '';
        }
    } else {
        modal.classList.add('hidden');
    }

    refreshGeofenceDraftUI();
}

function refreshGeofenceDraftUI() {
    const draftNameEl = document.getElementById('geofence-draft-name');
    const draftDescriptionEl = document.getElementById('geofence-draft-description');
    const draftStatusEl = document.getElementById('geofence-draft-status');
    const draftBarEl = document.getElementById('geofence-draft-bar');

    const name = getGeofenceDraftName();
    const description = getGeofenceDraftDescription();
    const hasDraft = Boolean(name || description);

    if (draftNameEl) {
        draftNameEl.textContent = name || 'Untitled geofence';
    }
    if (draftDescriptionEl) {
        draftDescriptionEl.textContent = description || 'No description yet';
    }
    if (draftStatusEl) {
        draftStatusEl.textContent = hasDraft ? 'Draft saved' : 'Draft empty';
    }
    if (draftBarEl) {
        draftBarEl.classList.toggle('hidden', !(geofenceDrawingMode && !geofencePreviewMode && (geofenceDraftMinimized || hasDraft)));
    }
}

function getGeofenceModeActionsMarkup() {
    if (geofenceViewOnlyMode) {
        return `
            <button class="btn btn-secondary" id="close-view-btn">${icons.x} Close View</button>
        `;
    }
    
    if (geofencePreviewMode) {
        return `
            <button class="btn btn-primary" id="save-geofence-btn">${icons.save} Save Geofence</button>
            <button class="btn btn-secondary" id="edit-geofence-btn">${icons.edit} Edit Boundary</button>
            <button class="btn btn-danger" id="cancel-geofence-btn">Cancel</button>
        `;
    }

    return `
        <button class="btn btn-primary" id="preview-geofence-btn">${icons.check} Preview & Save</button>
        <button class="btn btn-secondary" id="clear-geofence-btn">Clear Drawing</button>
        <button class="btn btn-danger" id="cancel-drawing-btn">Cancel</button>
    `;
}

function bindGeofenceModeActionListeners() {
    const closeViewBtn = document.getElementById('close-view-btn');
    if (closeViewBtn) closeViewBtn.onclick = exitGeofenceDrawingMode;

    const previewBtn = document.getElementById('preview-geofence-btn');
    if (previewBtn) previewBtn.onclick = handlePreviewGeofence;

    const editBtn = document.getElementById('edit-geofence-btn');
    if (editBtn) editBtn.onclick = handleEditGeofence;

    const clearBtn = document.getElementById('clear-geofence-btn');
    if (clearBtn) {
        clearBtn.onclick = () => {
            resetGeofenceMap();
            saveState();
            toast.info('Geofence drawing cleared');
        };
    }

    const cancelDrawingBtn = document.getElementById('cancel-drawing-btn');
    if (cancelDrawingBtn) cancelDrawingBtn.onclick = exitGeofenceDrawingMode;

    const cancelGeofenceBtn = document.getElementById('cancel-geofence-btn');
    if (cancelGeofenceBtn) cancelGeofenceBtn.onclick = exitGeofenceDrawingMode;

    const saveBtn = document.getElementById('save-geofence-btn');
    if (saveBtn) saveBtn.onclick = handleGeofenceSave;
}

function updateGeofenceModeUI() {
    const mapActions = document.getElementById('geofence-map-actions');
    const shapesSidebar = document.getElementById('geofence-shapes-sidebar');
    const statusAlert = document.getElementById('geofence-draw-status');
    const modeActions = document.getElementById('geofence-mode-actions');

    if (mapActions) {
        mapActions.classList.toggle('hidden', geofencePreviewMode);
    }
    if (shapesSidebar) {
        shapesSidebar.classList.toggle('hidden', geofencePreviewMode);
    }
    if (statusAlert) {
        statusAlert.classList.toggle('alert-success', geofencePreviewMode);
        statusAlert.classList.toggle('alert-info', !geofencePreviewMode);
        statusAlert.innerHTML = geofencePreviewMode
            ? '<strong>Polygon Preview:</strong> Review the boundary exactly as selected before saving.'
            : '<strong>Freehand Drawing:</strong> Click and hold to draw around the area you want to allow. Release to complete the shape. Lock the map to prevent accidental movement.';
    }
    if (modeActions) {
        modeActions.innerHTML = getGeofenceModeActionsMarkup();
    }

    bindGeofenceModeActionListeners();
}

function formatTimeRemaining(expiresAt) {
    if (!expiresAt) return 'Never';
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
        return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
}

let geofenceExpiryInterval = null;

function startGeofenceExpiryCountdown() {
    if (geofenceExpiryInterval) {
        clearInterval(geofenceExpiryInterval);
    }

    // Location check is done once when user is loaded - no continuous interval needed
}

function stopGeofenceExpiryCountdown() {
    if (geofenceExpiryInterval) {
        clearInterval(geofenceExpiryInterval);
        geofenceExpiryInterval = null;
    }
}

function renderGeofencesPage() {
    const geofences = state.geofences || [];
    // Backend now filters by target user ID, so no need to filter here
    const activeGeofence = geofences.find(g => g.status === 'ACTIVE');

    // If user data not loaded yet, show ONLY user ID input page
    if (!geofenceUserDataLoaded) {
        return `
            <!-- Step 3: User ID Input (First Page Only) -->
            <div class="card mb-3">
                <div class="card-header">
                    <h3 class="card-title">${icons.search} Target User</h3>
                </div>
                <div class="card-body">
                    <div class="form-group">
                        <label class="form-label">User ID (to enforce geofence for specific user)</label>
                        <input type="text" id="geofence-user-id" class="form-input" placeholder="Enter User ID (optional - defaults to admin)" value="${escapeHtml(geofenceTargetUserId || '')}">
                    </div>
                    <button class="btn btn-secondary" id="load-geofence-user-btn">${icons.search} Load User Data</button>
                </div>
            </div>
        `;
    }

    // After user data loaded, show ONLY geofence UI (hide user ID input)
    return `
        <!-- Back Arrow at Top Left -->
        <div class="mb-3">
            <button class="btn btn-secondary" id="change-user-btn">
                ← Back to User Selection
            </button>
        </div>

        <!-- Step 3: Current Geofence Section -->
        <div class="card mb-3 geofence-active-card">
            <div class="card-header geofence-active-header">
                <div class="geofence-active-header-content">
                    <div class="geofence-active-icon">${icons.location}</div>
                    <div class="geofence-active-title-group">
                        <h3 class="card-title">Current Geofence</h3>
                        <p class="card-subtitle">Active boundary enforcement</p>
                    </div>
                </div>
                ${activeGeofence ? `<div class="geofence-active-badge">${icons.check} Active</div>` : ''}
            </div>
            <div class="card-body">
                ${activeGeofence ? `
                    <div class="geofence-active-details">
                        <div class="geofence-active-name">${escapeHtml(activeGeofence.name)}</div>
                        <div class="geofence-active-description">${escapeHtml(activeGeofence.description || 'No description provided')}</div>

                        <div class="geofence-active-meta">
                            <div class="geofence-meta-item">
                                <span class="geofence-meta-icon">📅</span>
                                <span class="geofence-meta-label">Created</span>
                                <span class="geofence-meta-value">${formatDate(activeGeofence.createdAt)}</span>
                            </div>
                            <div class="geofence-meta-item">
                                <span class="geofence-meta-icon">⏰</span>
                                <span class="geofence-meta-label">Expires</span>
                                <span class="geofence-meta-value">${activeGeofence.expiresAt ? formatTimeRemaining(activeGeofence.expiresAt) : 'Never'}</span>
                            </div>
                        </div>

                        <div class="geofence-active-actions">
                            <button class="btn btn-primary geofence-action-btn" data-geofence-action="view" data-geofence-id="${escapeHtml(activeGeofence.id)}">
                                ${icons.map} View
                            </button>
                            <button class="btn btn-danger geofence-action-btn" data-geofence-action="cancel" data-geofence-id="${escapeHtml(activeGeofence.id)}">
                                Cancel Geofence
                            </button>
                        </div>
                    </div>
                ` : `
                    <div class="geofence-empty-state">
                        <div class="geofence-empty-icon">${icons.location}</div>
                        <div class="geofence-empty-title">No Active Geofence</div>
                        <div class="geofence-empty-subtitle">Create a geofence to enforce location boundaries</div>
                        <button class="btn btn-primary geofence-create-btn" id="create-geofence-btn">
                            ${icons.location} + Create Geofence
                        </button>
                    </div>
                `}
            </div>
        </div>

        <!-- Step 4: Creating a Geofence -->
        ${geofenceDrawingMode || geofencePreviewMode ? `
        <div class="card mb-3">
            <div class="card-header">
                <h3 class="card-title">${geofencePreviewMode ? 'Review & Save' : 'Draw Boundary'}</h3>
            </div>
            <div class="card-body">
                <div class="geofence-drawing-layout">
                    <!-- Left: Map -->
                    <div class="geofence-map-container">
                        ${!geofencePreviewMode ? `
                        <div class="geofence-draft-bar ${geofenceDraftMinimized || getGeofenceDraftName() || getGeofenceDraftDescription() ? '' : 'hidden'}" id="geofence-draft-bar">
                            <div class="geofence-draft-meta">
                                <span class="geofence-draft-pill" id="geofence-draft-status">Draft saved</span>
                                <div class="geofence-draft-title" id="geofence-draft-name">${escapeHtml(getGeofenceDraftName() || 'Untitled geofence')}</div>
                                <div class="geofence-draft-subtitle" id="geofence-draft-description">${escapeHtml(getGeofenceDraftDescription() || 'No description yet')}</div>
                            </div>
                            <div class="geofence-draft-actions">
                                <button class="btn btn-sm btn-secondary" id="open-geofence-draft-modal-btn">${icons.edit} Edit Details</button>
                                <button class="btn btn-sm btn-ghost" id="clear-geofence-draft-btn">Clear Draft</button>
                            </div>
                        </div>
                        ` : ''}

                        <!-- Step 5 & 7: Map with Leaflet and Leaflet-Geoman -->
                        <div class="geofence-map-shell">
                            <div id="geofence-map" class="geofence-map-surface"></div>
                            <div class="geofence-map-actions ${geofencePreviewMode || geofenceViewOnlyMode ? 'hidden' : ''}" id="geofence-map-actions">
                                <button id="lock-map-btn" class="btn btn-secondary geofence-map-action-btn" type="button">
                                    🔒 Lock Map
                                </button>
                                <button id="enable-draw-btn" class="btn btn-secondary geofence-map-action-btn geofence-draw-toggle-btn" type="button" aria-pressed="false">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                                    </svg>
                                    <span>Draw</span>
                                </button>
                                <button id="clear-geofence-map-btn" class="btn btn-secondary geofence-map-action-btn geofence-clear-btn" type="button">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path d="M3 6h18"></path>
                                        <path d="M8 6V4h8v2"></path>
                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                        <path d="M10 11v6"></path>
                                        <path d="M14 11v6"></path>
                                    </svg>
                                    <span>Clear</span>
                                </button>
                            </div>
                        </div>

                        <div class="alert ${geofencePreviewMode ? 'alert-success' : geofenceViewOnlyMode ? 'alert-secondary' : 'alert-info'} geofence-draw-hint mt-3" id="geofence-draw-status">
                            ${geofencePreviewMode
                                ? '<strong>Polygon Preview:</strong> Review the boundary exactly as selected before saving.'
                                : geofenceViewOnlyMode
                                ? '<strong>View Only:</strong> This geofence cannot be edited. You can only view the boundary.'
                                : '<strong>Freehand Drawing:</strong> Click and hold to draw around the area you want to allow. Release to complete the shape. Lock the map to prevent accidental movement.'
                            }
                        </div>

                        <div class="modal-overlay ${geofenceDraftModalOpen ? '' : 'hidden'} geofence-draft-modal-overlay" id="geofence-draft-modal">
                            <div class="modal geofence-draft-modal-card">
                                <div class="modal-header">
                                    <h3 class="modal-title">Geofence Details</h3>
                                    <button class="modal-close" id="close-geofence-draft-modal-btn">&times;</button>
                                </div>
                                <div class="modal-body">
                                    <div class="form-group">
                                        <label class="form-label">Geofence Name</label>
                                        <input type="text" id="geofence-draft-name-input" class="form-input" placeholder="Example: Home, Office, Warehouse" maxlength="80" value="${escapeHtml(geofenceDraft.name || '')}">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">Description</label>
                                        <textarea id="geofence-draft-description-input" class="form-input" rows="3" placeholder="Optional notes">${escapeHtml(geofenceDraft.description || '')}</textarea>
                                    </div>
                                    <div class="form-group mb-0">
                                        <label class="form-label">Expiry Time (Optional)</label>
                                        <select id="geofence-draft-expiry-input" class="form-input">
                                            <option value="">Never expires</option>
                                            <optgroup label="Minutes">
                                                <option value="5m">5 minutes</option>
                                                <option value="10m">10 minutes</option>
                                                <option value="15m">15 minutes</option>
                                                <option value="30m">30 minutes</option>
                                                <option value="45m">45 minutes</option>
                                            </optgroup>
                                            <optgroup label="Hours">
                                                <option value="1h">1 hour</option>
                                                <option value="2h">2 hours</option>
                                                <option value="3h">3 hours</option>
                                                <option value="6h">6 hours</option>
                                                <option value="12h">12 hours</option>
                                                <option value="24h">24 hours</option>
                                            </optgroup>
                                            <optgroup label="Days">
                                                <option value="3d">3 days</option>
                                                <option value="7d">7 days</option>
                                                <option value="30d">30 days</option>
                                            </optgroup>
                                        </select>
                                        <small class="form-hint">Geofence will automatically expire after this time</small>
                                    </div>
                                </div>
                                <div class="modal-footer">
                                    <button class="btn btn-secondary" id="minimize-geofence-draft-modal-btn">Minimize</button>
                                    <button class="btn btn-primary" id="save-geofence-draft-btn">Save Draft</button>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex gap-2 mt-3" id="geofence-mode-actions">
                            ${geofencePreviewMode ? `
                                <button class="btn btn-primary" id="save-geofence-btn">${icons.save} Save Geofence</button>
                                <button class="btn btn-secondary" id="edit-geofence-btn">${icons.edit} Edit Boundary</button>
                                <button class="btn btn-danger" id="cancel-geofence-btn">Cancel</button>
                            ` : `
                                <button class="btn btn-primary" id="preview-geofence-btn">${icons.check} Preview & Save</button>
                                <button class="btn btn-secondary" id="clear-geofence-btn">Clear Drawing</button>
                                <button class="btn btn-danger" id="cancel-drawing-btn">Cancel</button>
                            `}
                        </div>
                    </div>

                    <!-- Step 4: Common shapes on right side -->
                    <div class="geofence-shapes-sidebar ${geofencePreviewMode || geofenceViewOnlyMode ? 'hidden' : ''}" id="geofence-shapes-sidebar">
                        <h4 class="shapes-title">Common Shapes</h4>
                        <p class="shapes-subtitle">Click to add, then drag to move</p>

                        <div class="shapes-grid">
                            <button class="shape-btn" data-shape="circle" title="Circle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="12" cy="12" r="10"/>
                                </svg>
                                <span>Circle</span>
                            </button>

                            <button class="shape-btn" data-shape="rectangle" title="Rectangle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                </svg>
                                <span>Rectangle</span>
                            </button>

                            <button class="shape-btn" data-shape="polygon" title="Polygon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polygon points="12,2 22,20 2,20"/>
                                </svg>
                                <span>Triangle</span>
                            </button>

                            <button class="shape-btn" data-shape="hexagon" title="Hexagon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polygon points="12,2 21,7 21,17 12,22 3,17 3,7"/>
                                </svg>
                                <span>Hexagon</span>
                            </button>
                        </div>

                        <div class="shapes-info">
                            <strong>Tip:</strong> After adding a shape, drag to move it. Use freehand drawing for custom boundaries.
                        </div>
                    </div>
                </div>
            </div>
        </div>
        ` : ''}

        <!-- Step 18: Recent Geofences (Expired/Inactive only for target user) -->
        ${(() => {
            const inactiveGeofences = geofences.filter(g => g.status !== 'ACTIVE');
            if (inactiveGeofences.length === 0) return '';
            return `
        <div class="card geofence-history-card">
            <div class="card-header geofence-history-header">
                <div class="geofence-history-header-content">
                    <div class="geofence-history-icon">${icons.history}</div>
                    <div class="geofence-history-title-group">
                        <h3 class="card-title">Geofence History</h3>
                        <p class="card-subtitle">Expired and inactive geofences</p>
                    </div>
                </div>
                <div class="geofence-history-badge">${inactiveGeofences.length} item${inactiveGeofences.length !== 1 ? 's' : ''}</div>
            </div>
            <div class="card-body">
                <div class="geofence-history-list">
                    ${inactiveGeofences.map((item) => `
                        <div class="geofence-history-item">
                            <div class="geofence-history-item-icon">
                                ${item.status === 'EXPIRED' ? '⏰' : '📴'}
                            </div>
                            <div class="geofence-history-item-content">
                                <div class="geofence-history-item-name">${escapeHtml(item.name)}</div>
                                <div class="geofence-history-item-status">${item.status || 'Inactive'}</div>
                                <div class="geofence-history-item-description">${escapeHtml(item.description || 'No description')}</div>
                                <div class="geofence-history-item-meta">
                                    <span>Created: ${formatDate(item.createdAt)}</span>
                                    ${item.expiresAt ? `<span>• Expired: ${formatDate(item.expiresAt)}</span>` : ''}
                                </div>
                            </div>
                            <div class="geofence-history-item-actions">
                                <button class="btn btn-sm btn-secondary geofence-history-action-btn" data-geofence-action="reuse" data-geofence-id="${escapeHtml(item.id)}">
                                    ${icons.refresh} Reuse
                                </button>
                                <button class="btn btn-sm btn-danger geofence-history-action-btn" data-geofence-action="delete" data-geofence-id="${escapeHtml(item.id)}">
                                    ${icons.x} Delete
                                </button>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        </div>`;
        })()}
    `;
}

async function loadGeofences() {
    try {
        const response = await api.getGeofences(geofenceTargetUserId);
        state.geofences = Array.isArray(response?.data) ? response.data : [];
        state.geofencesLoaded = true;
        if (state.currentPage === 'geofences') {
            renderApp();
        }
    } catch (error) {
        console.error('Failed to load geofences:', error);
        toast.error('Failed to load geofences');
    }
}

function setupGeofenceMap() {
    const mapContainer = document.getElementById('geofence-map');
    if (!mapContainer) {
        console.error('Map container not found');
        return;
    }

    // Destroy existing map if it exists to recreate it properly
    if (geofenceMap) {
        disableGeofencePencilDrawing();
        clearGeofenceEdgeEffects();
        geofenceMap.remove();
        geofenceMap = null;
    }

    console.log('Setting up geofence map...');
    console.log('User last location:', geofenceUserLastLocation);

    // Step 12 & 26: Show user's last location if no geofence exists
    const initialCenter = geofenceUserLastLocation
        ? [geofenceUserLastLocation.latitude, geofenceUserLastLocation.longitude]
        : [31.5204, 74.3587];
    const initialZoom = geofenceUserLastLocation ? 16 : 11;

    console.log('Initial center:', initialCenter, 'Zoom:', initialZoom);

    geofenceMap = L.map('geofence-map', {
        zoomControl: true,
        preferCanvas: false
    }).setView(initialCenter, initialZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
    }).addTo(geofenceMap);

    // Step 12 & 26: Add marker for user's last location if available
    if (geofenceUserLastLocation) {
        const locationIcon = L.divIcon({
            className: 'geofence-location-marker',
            html: `
                <div style="width: 36px; height: 36px;">
                    <svg viewBox="0 0 24 36" style="width: 100%; height: 100%; fill: #7C3AED;">
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 8.4 8.73 19.115 11.06 21.86a1.2 1.2 0 0 0 1.88 0C15.27 31.115 24 20.4 24 12 24 5.373 18.627 0 12 0Z" />
                        <circle cx="12" cy="12" r="4.2" fill="white" />
                    </svg>
                </div>
            `,
            iconSize: [36, 36],
            iconAnchor: [12, 36]
        });
        L.marker([geofenceUserLastLocation.latitude, geofenceUserLastLocation.longitude], { icon: locationIcon })
            .addTo(geofenceMap)
            .bindPopup('User Last Location')
            .openPopup();
    }

    // Step 5 & 7: Leaflet-Geoman with freehand drawing (only if not view-only mode)
    if (!geofenceViewOnlyMode) {
        geofenceMap.pm.addControls({
            position: 'topleft',
            drawCircle: false,
            drawMarker: false,
            drawPolyline: false,
            drawRectangle: false,
            drawCircleMarker: false,
            drawPolygon: true,
            // Step 6: Enable freehand drawing mode
            drawPolygonMarker: true,
            continueDrawing: false,
            editMode: true,
            dragMode: true,
            cutPolygon: false,
            removalMode: true,
            // Step 6: Freehand mode options
            positionOptions: {
                drawPolygon: 'topleft'
            }
        });
    }

    // Step 6: Enable freehand drawing mode - pencil-like with no visible vertices (only if not view-only mode)
    if (!geofenceViewOnlyMode) {
        geofenceMap.pm.setGlobalOptions({
            allowSelfIntersection: false,
            snappable: false, // Disable snapping for smoother freehand
            snapDistance: 0,
            // Step 6: Enable freehand sketching
            freehand: true,
            // Hide all markers/vertices for pencil-like drawing
            markerStyle: {
                color: 'transparent',
                fillColor: 'transparent',
                fillOpacity: 0,
                radius: 0,
                weight: 0
            },
            // Solid red line for drawing (no dashes)
            templineStyle: {
                color: '#EF4444',
                weight: 3,
                dashArray: null
            },
        hintlineStyle: {
            color: '#EF4444',
            weight: 3,
            dashArray: null
        },
        // Hide cursor markers during drawing
        cursorMarker: false
    });

        geofenceMap.getContainer().style.touchAction = 'none';
        geofenceMap.getContainer().style.cursor = 'crosshair';
    }

    applyGeofenceMapLockState();
    syncGeofenceLockButton();

    console.log('Leaflet-Geoman controls added');

    // Only enable polygon creation if not in view-only mode
    if (!geofenceViewOnlyMode) {
        geofenceMap.on('pm:create', (event) => {
            console.log('Polygon created:', event);
            clearGeofenceEdgeEffects();
            if (geofencePolygonLayer) {
                geofenceMap.removeLayer(geofencePolygonLayer);
            }
            geofencePolygonLayer = event.layer;
        styleGeofencePolygonLayer(geofencePolygonLayer, { focus: true, animate: false });
        });
    }

    // Only enable drawing event listeners if not in view-only mode
    if (!geofenceViewOnlyMode) {
        geofenceMap.on('pm:drawstart', (event) => {
            console.log('Drawing started:', event);
            geofenceMap.getContainer().classList.add('geofence-pencil-drawing');
        });

        geofenceMap.on('pm:drawend', (event) => {
            console.log('Drawing ended:', event);
            geofenceMap.getContainer().classList.remove('geofence-pencil-drawing');
        });
    }

    // Only enable remove event listener if not in view-only mode
    if (!geofenceViewOnlyMode) {
        geofenceMap.on('pm:remove', () => {
            clearGeofenceEdgeEffects();
            geofencePolygonLayer = null;
        });
    }

    // Invalidate map size to ensure it renders correctly after container becomes visible
    setTimeout(() => {
        geofenceMap.invalidateSize();
        if (geofencePolygonLayer) {
            fitGeofenceLayerInView(geofencePolygonLayer);
        } else if (geofenceUserLastLocation) {
            geofenceMap.setView([geofenceUserLastLocation.latitude, geofenceUserLastLocation.longitude], 16);
        }
    }, 100);

    // Step 10: Selected/active edge color and vertex styling
    geofenceMap.on('pm:edit', (event) => {
        if (event.layer) {
            event.layer.setStyle({
                color: '#6D28D9'  // Selected/active edge
            });
        }
    });

    geofenceMap.invalidateSize();
}

function resetGeofenceMap() {
    disableGeofencePencilDrawing();
    clearGeofenceEdgeEffects();
    if (geofencePolygonLayer && geofenceMap) {
        geofenceMap.removeLayer(geofencePolygonLayer);
        geofencePolygonLayer = null;
    }
    geofencePreviewGeometry = null;
    if (geofenceMap) {
        geofenceMap.eachLayer((layer) => {
            if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
                geofenceMap.removeLayer(layer);
            }
        });
    }
}

function exitGeofenceDrawingMode() {
    disableGeofencePencilDrawing();
    clearGeofenceEdgeEffects();
    geofenceDrawingMode = false;
    geofencePreviewMode = false;
    geofenceViewOnlyMode = false;
    geofencePreviewGeometry = null;
    geofenceDraftModalOpen = false;
    geofenceDraftMinimized = Boolean(getGeofenceDraftName() || getGeofenceDraftDescription());
    saveState();
    resetGeofenceMap();
    if (geofenceMap) {
        geofenceMap.remove();
        geofenceMap = null;
    }
    renderApp();
}

async function loadGeofenceUserData() {
    const userId = document.getElementById('geofence-user-id')?.value?.trim();
    if (!userId) {
        toast.error('Please enter a User ID');
        return;
    }

    // Show loading popup with blur
    showLoading();

    try {
        geofenceTargetUserId = userId;
        saveState();
        // Step 12: Get user's last location from existing location system
        const locationResponse = await api.getUserLatestLocation(userId);
        console.log('Location response:', locationResponse);
        if (locationResponse.success && locationResponse.location) {
            geofenceUserLastLocation = locationResponse.location;
            geofenceUserDataLoaded = true;
            hideLoading();
            toast.success('User data loaded. Map centered on last location.');
        } else {
            geofenceUserLastLocation = null;
            geofenceUserDataLoaded = true;
            hideLoading();
            toast.warning('User loaded but no location data found.');
        }
        // Load geofences for this specific user
        await loadGeofences();
        renderApp();
        if (geofenceMap) {
            setupGeofenceMap();
        }
    } catch (error) {
        console.error('Failed to load user data:', error);
        hideLoading();
        toast.error('Failed to load user data: ' + error.message);
    }
}

async function handleGeofenceSave() {
    syncGeofenceDraftFromModalInputs();
    const name = getGeofenceDraftName();
    const description = getGeofenceDraftDescription();

    if (!name) {
        toast.error('Please enter a geofence name');
        return;
    }

    const geometryToUse = geofencePreviewGeometry || (geofencePolygonLayer ? geofencePolygonLayer.toGeoJSON().geometry : null);

    if (!geometryToUse) {
        toast.error('Please draw a geofence on the map first');
        return;
    }

    try {
        // Calculate expiry time if specified
        let expiresAt = null;
        if (geofenceDraft.expiry) {
            const now = new Date();
            const expiryValue = geofenceDraft.expiry;
            const amount = parseInt(expiryValue);
            const unit = expiryValue.slice(-1);

            switch (unit) {
                case 'm':
                    expiresAt = new Date(now.getTime() + amount * 60 * 1000);
                    break;
                case 'h':
                    expiresAt = new Date(now.getTime() + amount * 60 * 60 * 1000);
                    break;
                case 'd':
                    expiresAt = new Date(now.getTime() + amount * 24 * 60 * 60 * 1000);
                    break;
            }
        }

        // Step 26: Include targetUserId if specified
        const payload = {
            name,
            description,
            geometry: geometryToUse
        };

        if (geofenceTargetUserId) {
            payload.targetUserId = geofenceTargetUserId;
        }

        if (expiresAt) {
            payload.expiresAt = expiresAt.toISOString();
        }

        // Log payload and extracted points for debugging
        try {
            let pointsArray = [];
            if (geometryToUse && geometryToUse.type === 'Polygon' && Array.isArray(geometryToUse.coordinates)) {
                pointsArray = geometryToUse.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] }));
            } else if (geometryToUse && geometryToUse.type === 'MultiPolygon' && Array.isArray(geometryToUse.coordinates)) {
                // Take first polygon ring for logging
                pointsArray = geometryToUse.coordinates[0][0].map(coord => ({ lat: coord[1], lng: coord[0] }));
            } else {
                // Fallback: log raw geometry
                pointsArray = geometryToUse;
            }
            console.log('Geofence payload:', payload);
            console.log('Geofence points:', pointsArray);
        } catch (e) {
            console.warn('Failed to compute geofence points for logging:', e);
            console.log('Raw geometry:', geometryToUse);
        }

        const response = await api.createGeofence(payload);
        console.log('createGeofence response:', response);

        toast.success('Geofence saved successfully');
        geofenceDraft = { name: '', description: '' };
        geofenceDraftModalOpen = false;
        geofenceDraftMinimized = false;
        saveState();
        exitGeofenceDrawingMode();
        await loadGeofences();
    } catch (error) {
        console.error('Geofence save failed:', error);
        // Show a clearer message when server returned non-JSON content
        toast.error(error.message || 'Unable to save geofence (see console/network for details)');
    }
}

async function handlePreviewGeofence() {
    syncGeofenceDraftFromModalInputs();
    const name = getGeofenceDraftName();

    if (!name) {
        toast.error('Please enter a geofence name first');
        return;
    }

    if (!geofencePolygonLayer) {
        toast.error('Please draw a geofence on the map first');
        return;
    }

    // Step 10: Simplification is done on backend, but we can show preview
    geofencePreviewGeometry = geofencePolygonLayer.toGeoJSON().geometry;
    geofenceDraftModalOpen = false;
    geofenceDraftMinimized = true;
    geofencePreviewMode = true;
    saveState();
    updateGeofenceModeUI();

    if (geofencePolygonLayer && geofenceMap) {
        styleGeofencePolygonLayer(geofencePolygonLayer, { focus: false, animate: true });
        geofenceMap.invalidateSize();
    }

    toast.success('Polygon preview ready for review');
}

function handleEditGeofence() {
    geofenceDraftModalOpen = false;
    geofenceDraftMinimized = true;
    geofencePreviewMode = false;
    saveState();
    updateGeofenceModeUI();
    if (geofenceMap) {
        geofenceMap.invalidateSize();
    }
    toast.info('Edit mode enabled');
}

function attachGeofencePageListeners() {
    // User ID loading
    document.getElementById('load-geofence-user-btn')?.addEventListener('click', loadGeofenceUserData);
    document.getElementById('geofence-user-id')?.addEventListener('input', (e) => {
        geofenceTargetUserId = e.target.value?.trim() || null;
        saveState();
    });

    // Change user button - go back to user ID input page
    document.getElementById('change-user-btn')?.addEventListener('click', () => {
        disableGeofencePencilDrawing();
        geofenceUserDataLoaded = false;
        geofenceTargetUserId = null;
        geofenceUserLastLocation = null;
        geofenceDrawingMode = false;
        geofencePreviewMode = false;
        geofenceDraft = { name: '', description: '' };
        geofenceDraftModalOpen = false;
        geofenceDraftMinimized = false;
        saveState();
        renderApp();
    });

    // Lock/Unlock map button
    document.getElementById('lock-map-btn')?.addEventListener('click', () => {
        if (!geofenceMap) return;

        geofenceMapLocked = !geofenceMapLocked;
        applyGeofenceMapLockState();
        syncGeofenceLockButton();
        saveState();
        toast.success(geofenceMapLocked ? 'Map locked for drawing' : 'Map unlocked');
    });

    // Enable drawing button (toggle on/off)
    document.getElementById('enable-draw-btn')?.addEventListener('click', () => {
        if (!geofenceMap) return;

        if (geofencePencilMode) {
            disableGeofencePencilDrawing();
            toast.info('Draw mode disabled');
            return;
        }

        if (!geofenceMapLocked) {
            geofenceMapLocked = true;
            applyGeofenceMapLockState();
            syncGeofenceLockButton();
        }

        enableGeofencePencilDrawing();
        toast.success('Draw mode enabled - press and drag to draw');
    });

    document.getElementById('clear-geofence-map-btn')?.addEventListener('click', () => {
        disableGeofencePencilDrawing();
        resetGeofenceMap();
        geofenceMapLocked = true;
        applyGeofenceMapLockState();
        syncGeofenceLockButton();
        updateGeofenceDrawButtonState();
        saveState();
        toast.info('Drawing cleared');
    });

    // Create geofence button
    document.getElementById('create-geofence-btn')?.addEventListener('click', () => {
        geofenceDrawingMode = true;
        geofencePreviewMode = false;
        geofenceDraftModalOpen = !getGeofenceDraftName() && !getGeofenceDraftDescription();
        geofenceDraftMinimized = !geofenceDraftModalOpen;
        saveState();
        renderApp();
        setTimeout(() => setupGeofenceMap(), 100);
    });

    // Draft modal and minimized draft bar controls
    const geofenceDraftNameInput = document.getElementById('geofence-draft-name-input');
    const geofenceDraftDescriptionInput = document.getElementById('geofence-draft-description-input');
    const geofenceDraftExpiryInput = document.getElementById('geofence-draft-expiry-input');
    const geofenceDraftModal = document.getElementById('geofence-draft-modal');

    geofenceDraftNameInput?.addEventListener('input', () => {
        geofenceDraft.name = geofenceDraftNameInput.value.slice(0, 80);
        refreshGeofenceDraftUI();
        saveState();
    });
    geofenceDraftDescriptionInput?.addEventListener('input', () => {
        geofenceDraft.description = geofenceDraftDescriptionInput.value;
        refreshGeofenceDraftUI();
        saveState();
    });
    geofenceDraftExpiryInput?.addEventListener('change', () => {
        geofenceDraft.expiry = geofenceDraftExpiryInput.value;
        saveState();
    });

    document.getElementById('open-geofence-draft-modal-btn')?.addEventListener('click', () => {
        setGeofenceDraftModalOpen(true, { preserveMinimized: false });
        saveState();
    });
    document.getElementById('save-geofence-draft-btn')?.addEventListener('click', () => {
        syncGeofenceDraftFromModalInputs();
        geofenceDraftMinimized = true;
        setGeofenceDraftModalOpen(false, { preserveMinimized: true });
        saveState();
        toast.success('Geofence draft saved');
    });
    document.getElementById('minimize-geofence-draft-modal-btn')?.addEventListener('click', () => {
        syncGeofenceDraftFromModalInputs();
        geofenceDraftMinimized = true;
        setGeofenceDraftModalOpen(false, { preserveMinimized: true });
        saveState();
    });
    document.getElementById('close-geofence-draft-modal-btn')?.addEventListener('click', () => {
        syncGeofenceDraftFromModalInputs();
        geofenceDraftMinimized = true;
        setGeofenceDraftModalOpen(false, { preserveMinimized: true });
        saveState();
    });
    document.getElementById('clear-geofence-draft-btn')?.addEventListener('click', () => {
        geofenceDraft = { name: '', description: '' };
        geofenceDraftMinimized = true;
        const nameInput = document.getElementById('geofence-draft-name-input');
        const descriptionInput = document.getElementById('geofence-draft-description-input');
        if (nameInput) nameInput.value = '';
        if (descriptionInput) descriptionInput.value = '';
        refreshGeofenceDraftUI();
        saveState();
        toast.info('Draft cleared');
    });

    geofenceDraftModal?.addEventListener('click', (event) => {
        if (event.target === geofenceDraftModal) {
            syncGeofenceDraftFromModalInputs();
            geofenceDraftMinimized = true;
            setGeofenceDraftModalOpen(false, { preserveMinimized: true });
            saveState();
        }
    });

    // Drawing mode buttons
    bindGeofenceModeActionListeners();

    // Geofence actions (view, cancel, delete, reuse)
    document.querySelectorAll('[data-geofence-action]').forEach((button) => {
        button.addEventListener('click', async () => {
            const action = button.dataset.geofenceAction;
            const geofenceId = button.dataset.geofenceId;
            if (!geofenceId) return;

            try {
                if (action === 'delete') {
                    await api.deleteGeofence(geofenceId);
                    toast.success('Geofence deleted');
                    await loadGeofences();
                } else if (action === 'cancel') {
                    await api.deleteGeofence(geofenceId);
                    toast.success('Geofence cancelled');
                    await loadGeofences();
                } else if (action === 'view') {
                    // Step 19: View geofence (read-only, no editing)
                    const geofence = state.geofences.find(item => item.id === geofenceId);
                    if (geofence && geofence.geometry) {
                        geofenceDrawingMode = true;
                        geofencePreviewMode = false;
                        geofenceViewOnlyMode = true; // View-only mode - no drawing/editing
                        geofenceTargetUserId = geofence.ownerId;
                        geofenceDraft = {
                            name: geofence.name || '',
                            description: geofence.description || ''
                        };
                        geofenceDraftModalOpen = false;
                        geofenceDraftMinimized = true;
                        saveState();
                        renderApp();
                        setTimeout(() => {
                            setupGeofenceMap();
                            // Load existing polygon
                            if (geofence.geometry && geofenceMap) {
                                const layer = L.geoJSON(geofence.geometry, {
                                    style: {
                                        color: '#7C3AED',
                                        fillColor: 'rgba(124, 58, 237, 0.12)',
                                        fillOpacity: 0.12,
                                        weight: 3
                                    }
                                }).addTo(geofenceMap);
                                geofencePolygonLayer = layer;
                                                styleGeofencePolygonLayer(geofencePolygonLayer, { focus: true, animate: false });
                            }
                        }, 100);
                    }
                } else if (action === 'reuse') {
                    // Step 19: Reuse with optional edit
                    const geofence = state.geofences.find(item => item.id === geofenceId);
                    if (!geofence || !geofence.geometry) {
                        toast.error('This geofence cannot be reused');
                        return;
                    }
                    
                    const payload = {
                        name: geofence.name,
                        description: geofence.description || '',
                        geometry: geofence.geometry
                    };
                    
                    if (geofenceTargetUserId) {
                        payload.targetUserId = geofenceTargetUserId;
                    }

                    await api.createGeofence(payload);
                    toast.success('Geofence reused and activated');
                    await loadGeofences();
                }
            } catch (error) {
                console.error('Geofence action failed:', error);
                toast.error(error.message || 'Failed to perform action');
            }
        });
    });

    // Setup map if in drawing mode
    if (geofenceDrawingMode || geofencePreviewMode) {
        setupGeofenceMap();
    }

    refreshGeofenceDraftUI();

    // Step 4: Common shapes handlers
    document.querySelectorAll('.shape-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (!geofenceMap) return;
            
            const shape = btn.dataset.shape;
            const center = geofenceMap.getCenter();
            
            let coordinates = [];
            const size = 0.005; // Approximate size in degrees
            
            switch (shape) {
                case 'circle':
                    // Create a circle as a polygon approximation
                    const circlePoints = [];
                    for (let i = 0; i <= 32; i++) {
                        const angle = (i / 32) * 2 * Math.PI;
                        circlePoints.push([
                            center.lng + size * Math.cos(angle),
                            center.lat + size * Math.sin(angle)
                        ]);
                    }
                    coordinates = [circlePoints];
                    break;
                    
                case 'rectangle':
                    coordinates = [[
                        [center.lng - size, center.lat - size * 0.7],
                        [center.lng + size, center.lat - size * 0.7],
                        [center.lng + size, center.lat + size * 0.7],
                        [center.lng - size, center.lat + size * 0.7],
                        [center.lng - size, center.lat - size * 0.7]
                    ]];
                    break;
                    
                case 'polygon':
                    coordinates = [[
                        [center.lng, center.lat - size],
                        [center.lng + size, center.lat + size * 0.7],
                        [center.lng - size, center.lat + size * 0.7],
                        [center.lng, center.lat - size]
                    ]];
                    break;
                    
                case 'hexagon':
                    const hexPoints = [];
                    for (let i = 0; i < 6; i++) {
                        const angle = (i / 6) * 2 * Math.PI - Math.PI / 2;
                        hexPoints.push([
                            center.lng + size * Math.cos(angle),
                            center.lat + size * Math.sin(angle)
                        ]);
                    }
                    hexPoints.push(hexPoints[0]); // Close the polygon
                    coordinates = [hexPoints];
                    break;
            }
            
            if (coordinates.length > 0) {
                // Remove existing polygon
                if (geofencePolygonLayer) {
                    geofenceMap.removeLayer(geofencePolygonLayer);
                }
                
                // Add new polygon
                const polygon = L.polygon(coordinates[0].map(coord => [coord[1], coord[0]]), {
                    color: '#7C3AED',
                    fillColor: 'rgba(124, 58, 237, 0.12)',
                    fillOpacity: 0.12,
                    weight: 3
                }).addTo(geofenceMap);
                
                geofencePolygonLayer = polygon;
                styleGeofencePolygonLayer(geofencePolygonLayer, { focus: true, animate: false });
                
                toast.success(`${shape.charAt(0).toUpperCase() + shape.slice(1)} added. Drag to move, use map controls to resize.`);
            }
        });
    });
}

// ============================================================================
// USERS PAGE
// ============================================================================
function renderUsersPage() {
    return `
        <div class="user-selection-panel">
            <h2 class="user-selection-title">${icons.search} Search Users</h2>
            <div class="user-id-input-group">
                <input type="text"
                       id="user-ids-input"
                       class="form-input"
                       placeholder="Enter User ID(s) - separate multiple with commas"
                       value="${escapeHtml(state.usersPageInput || '')}">
                <button class="btn btn-secondary" id="load-users-btn">
                    ${icons.search} Load Settings
                </button>
            </div>
            <p class="multi-user-hint">
                💡 Tip: You can enter multiple User IDs separated by commas to manage several users at once.
            </p>
        </div>

        <div id="users-container">
            ${state.loadedUsers.size > 0 ? renderLoadedUsers() : renderEmptyState()}
        </div>
    `;
}

function renderEmptyState() {
    return `
        <div class="empty-state">
            <div class="empty-state-icon">👤</div>
            <h3 class="empty-state-title">No Users Loaded</h3>
            <p>Enter a User ID above to load and manage their location tracking settings.</p>
        </div>
    `;
}

function renderLoadedUsers() {
    let html = '';

    state.loadedUsers.forEach((data, userId) => {
        html += renderUserPanel(userId, data);
    });

    return html;
}

function renderUserPanel(userId, data) {
    const settings = data.settings || {};
    const activeMode = getActiveModeName(settings);
    const effectiveInterval = getEffectiveInterval(settings);

    return `
        <div class="user-panel" data-user-id="${escapeHtml(userId)}">
            <div class="user-panel-header">
                <div>
                    <span class="user-panel-id">${escapeHtml(userId)}</span>
                    ${data.error ? `<span class="badge badge-danger ml-2">Error</span>` : ''}
                    ${settings.isDefault ? `<span class="badge badge-warning ml-2">Using Defaults</span>` : ''}
                </div>
                <div class="user-panel-status">
                    <span class="badge ${settings.trackingEnabled !== false ? 'badge-success' : 'badge-danger'}">
                        ${settings.trackingEnabled !== false ? 'Tracking ON' : 'Tracking OFF'}
                    </span>
                    <span class="badge badge-primary">${activeMode}</span>
                    ${effectiveInterval ? `<span class="badge badge-info">${formatDuration(effectiveInterval)}</span>` : ''}
                    <button class="btn btn-sm btn-secondary" onclick="removeUser('${escapeHtml(userId)}')">
                        ${icons.x} Remove
                    </button>
                </div>
            </div>

            ${data.error ? `
                <div class="user-panel-body">
                    <div class="alert alert-danger mb-0">
                        <strong>Error loading settings:</strong> ${escapeHtml(data.error)}
                    </div>
                </div>
            ` : `
                <div class="user-panel-body">
                    <!-- Mode Toggle Cards -->
                    <h4 class="mb-2">Tracking Modes</h4>
                    <div class="mode-toggle-grid">
                        ${renderModeToggle(userId, 'Normal Tracking', 'normal', true, settings.updateInterval || 3600, 'Default tracking mode (1 hour interval)')}
                        ${renderModeToggle(userId, 'Force Check', 'force', settings.forceCheckEnabled, settings.forceCheckInterval || 300, 'Override with 5 minute checks', settings.forceCheckExpiresAt)}
                        ${renderModeToggle(userId, 'Realtime', 'realtime', settings.realtimeModeEnabled, settings.realtimeInterval || 10, 'Real-time 10 second updates', settings.realtimeModeExpiresAt)}
                        ${renderModeToggle(userId, 'Emergency', 'emergency', settings.emergencyModeEnabled, settings.emergencyInterval || 30, 'Emergency 30 second tracking', settings.emergencyModeExpiresAt)}
                    </div>

                    <!-- Normal Interval Only -->
                    <h4 class="mb-2 mt-4">Normal Tracking Interval</h4>
                    <div class="settings-grid single-setting">
                        ${renderSettingInput(userId, 'updateInterval', 'Update Interval', settings.updateInterval || 3600, 'seconds', 'Interval between location updates in normal mode')}
                    </div>
                </div>

                <div class="user-panel-actions">
                    <button class="btn btn-primary" onclick="saveUserSettings('${escapeHtml(userId)}')">
                        ${icons.save} Save Changes
                    </button>
                    <button class="btn btn-success" onclick="forceSyncUser('${escapeHtml(userId)}')">
                        ${icons.broadcast} Sync Now
                    </button>
                    <button class="btn btn-secondary" onclick="refreshUser('${escapeHtml(userId)}')">
                        ${icons.refresh} Refresh
                    </button>
                    <button class="btn btn-danger" onclick="resetUserSettings('${escapeHtml(userId)}')">
                        Reset to Defaults
                    </button>
                </div>
            `}
        </div>
    `;
}

function renderModeToggle(userId, name, mode, isActive, interval, description, expiresAt = null) {
    const timeRemaining = expiresAt ? getTimeRemaining(expiresAt) : null;
    const isNormal = mode === 'normal';

    return `
        <div class="mode-toggle-card ${isActive ? 'active' : ''}" data-mode="${mode}">
            <div class="mode-toggle-header">
                <div class="mode-toggle-info">
                    <div class="mode-icon ${mode}">
                        ${mode === 'normal' ? icons.location : mode === 'force' ? icons.clock : mode === 'realtime' ? icons.zap : icons.alert}
                    </div>
                    <div class="mode-toggle-text">
                        <span class="mode-toggle-name">${name}</span>
                        <span class="mode-toggle-desc">${description}</span>
                    </div>
                </div>
                ${!isNormal ? `
                    <label class="toggle-switch-large ${isActive ? 'on' : 'off'}">
                        <input type="checkbox"
                               ${isActive ? 'checked' : ''}
                               onchange="toggleMode('${escapeHtml(userId)}', '${mode}', this.checked, ${interval})">
                        <span class="toggle-slider-large"></span>
                    </label>
                ` : `
                    <span class="badge badge-info">Default</span>
                `}
            </div>
            <div class="mode-toggle-details">
                <div class="mode-detail-row">
                    <span class="mode-detail-label">Interval:</span>
                    <span class="mode-detail-value">${formatDuration(interval)}</span>
                </div>
                ${timeRemaining && isActive ? `
                    <div class="mode-detail-row">
                        <span class="mode-detail-label">Auto-off in:</span>
                        <span class="mode-detail-value text-warning">${timeRemaining}</span>
                    </div>
                ` : ''}
            </div>
        </div>
    `;
}

function renderSettingInput(userId, key, label, value, unit, description) {
    return `
        <div class="setting-item">
            <div class="setting-header">
                <span class="setting-label">${label}</span>
                <span class="setting-value">${value} ${unit}</span>
            </div>
            <p class="setting-description">${description}</p>
            <div class="d-flex gap-1">
                <input type="number"
                       class="form-input setting-input"
                       data-user-id="${escapeHtml(userId)}"
                       data-setting-key="${key}"
                       value="${value}"
                       min="0">
                <span class="form-hint" style="white-space: nowrap; align-self: center;">${unit}</span>
            </div>
        </div>
    `;
}

// ============================================================================
// BROADCAST PAGE
// ============================================================================
function renderBroadcastPage() {
    const defaults = state.defaults || {};

    return `
        <div class="alert alert-warning mb-3">
            <span>${icons.alert}</span>
            <div>
                <strong>Caution: Broadcast Settings</strong><br>
                Broadcasting will update settings for ALL users in the system. Use with care.
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div>
                    <h3 class="card-title">Broadcast Settings to All Users</h3>
                    <p class="card-subtitle">Update specific settings for every user at once</p>
                </div>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <label class="form-label">Reason for Broadcast</label>
                    <input type="text" id="broadcast-reason" class="form-input" placeholder="e.g., System maintenance, Policy update">
                    <p class="form-hint">This will be recorded in the change history for all affected users.</p>
                </div>

                <h4 class="mt-4 mb-2">Select Settings to Broadcast</h4>
                <div class="settings-grid">
                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Update Interval</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-updateInterval-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-updateInterval" class="form-input" value="${defaults.updateInterval || 7200}" min="1">
                        <p class="form-hint">seconds</p>
                    </div>

                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Force Check Interval</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-forceCheckInterval-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-forceCheckInterval" class="form-input" value="${defaults.forceCheckInterval || 300}" min="1">
                        <p class="form-hint">seconds</p>
                    </div>

                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Realtime Interval</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-realtimeInterval-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-realtimeInterval" class="form-input" value="${defaults.realtimeInterval || 10}" min="1">
                        <p class="form-hint">seconds</p>
                    </div>

                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Emergency Interval</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-emergencyInterval-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-emergencyInterval" class="form-input" value="${defaults.emergencyInterval || 30}" min="1">
                        <p class="form-hint">seconds</p>
                    </div>

                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Battery Threshold</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-batteryThreshold-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-batteryThreshold" class="form-input" value="${defaults.batteryThreshold || 20}" min="0" max="100">
                        <p class="form-hint">percent</p>
                    </div>

                    <div class="setting-item">
                        <div class="d-flex align-center justify-between mb-1">
                            <label class="form-label mb-0">Batch Upload Size</label>
                            <label class="toggle-switch">
                                <input type="checkbox" id="broadcast-batchUploadSize-enabled">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <input type="number" id="broadcast-batchUploadSize" class="form-input" value="${defaults.batchUploadSize || 10}" min="1">
                        <p class="form-hint">locations</p>
                    </div>
                </div>
            </div>
            <div class="card-footer">
                <button class="btn btn-warning btn-lg" id="broadcast-btn">
                    ${icons.broadcast} Broadcast Selected Settings
                </button>
            </div>
        </div>
    `;
}

// ============================================================================
// DEFAULTS PAGE
// ============================================================================
function renderDefaultsPage() {
    const defaults = state.defaults || {};

    return `
        <div class="alert alert-info mb-3">
            <span>${icons.alert}</span>
            <div>
                <strong>Default Settings Reference</strong><br>
                These are the default values that new users receive. These values are defined in the backend.
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <h3 class="card-title">System Default Settings</h3>
            </div>
            <div class="card-body">
                ${state.defaults ? `
                    <div class="settings-grid">
                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Update Interval</span>
                                <span class="setting-value">${formatDuration(defaults.updateInterval)}</span>
                            </div>
                            <p class="setting-description">Default interval for normal tracking mode</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Force Check Interval</span>
                                <span class="setting-value">${formatDuration(defaults.forceCheckInterval)}</span>
                            </div>
                            <p class="setting-description">Default interval for force check mode</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Realtime Interval</span>
                                <span class="setting-value">${formatDuration(defaults.realtimeInterval)}</span>
                            </div>
                            <p class="setting-description">Default interval for realtime mode</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Emergency Interval</span>
                                <span class="setting-value">${formatDuration(defaults.emergencyInterval)}</span>
                            </div>
                            <p class="setting-description">Default interval for emergency mode</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Force Check Duration</span>
                                <span class="setting-value">${formatDuration(defaults.forceCheckDuration)}</span>
                            </div>
                            <p class="setting-description">How long force check stays active</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Realtime Mode Duration</span>
                                <span class="setting-value">${formatDuration(defaults.realtimeModeDuration)}</span>
                            </div>
                            <p class="setting-description">How long realtime mode stays active</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Emergency Mode Duration</span>
                                <span class="setting-value">${formatDuration(defaults.emergencyModeDuration)}</span>
                            </div>
                            <p class="setting-description">How long emergency mode stays active</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Battery Threshold</span>
                                <span class="setting-value">${defaults.batteryThreshold}%</span>
                            </div>
                            <p class="setting-description">Minimum battery for tracking</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Movement Threshold</span>
                                <span class="setting-value">${defaults.movementThreshold} meters</span>
                            </div>
                            <p class="setting-description">Minimum movement distance</p>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <span class="setting-label">Batch Upload Size</span>
                                <span class="setting-value">${defaults.batchUploadSize} locations</span>
                            </div>
                            <p class="setting-description">Locations per batch upload</p>
                        </div>
                    </div>
                ` : `
                    <div class="empty-state">
                        <p>Loading defaults...</p>
                    </div>
                `}
            </div>
        </div>
    `;
}

// ============================================================================
// EMAIL TEMPLATES CONFIGURATION
// ============================================================================
const emailTemplates = {
    verification: {
        name: 'Email Verification',
        sender: 'constructconnect-verify@pakistantech.me',
        subject: '🔐 {code} is your verification code',
        defaultBody: `Hi {firstName},

Welcome to ConstructConnect! We're excited to have you on board.

To complete your registration and start finding amazing job opportunities, please verify your email address.

Your Verification Code: {code}

⏱️ This code expires in 15 minutes

📝 How to verify:
1. Open the ConstructConnect app
2. Enter the 6-digit code shown above
3. Start exploring opportunities!

🔒 Security tip: Never share this code with anyone. Our team will never ask for your verification code.

If you didn't create an account with ConstructConnect, please ignore this email.`
    },
    welcome: {
        name: 'Welcome Email',
        sender: 'constructconnect-welcome@pakistantech.me',
        subject: '🎉 Welcome to ConstructConnect, {firstName}!',
        defaultBody: `Hi {firstName} {lastName},

Congratulations! Your email has been verified and your account is now fully activated. You're now part of a thriving community of skilled professionals and eager buyers.

🚀 What you can do now:

💼 Browse & Apply to Jobs - Find opportunities that match your skills and location
⭐ Build Your Reputation - Complete jobs, get reviews, and level up your profile
💰 Earn & Withdraw - Get paid for your work and withdraw funds easily
💬 Connect with Buyers - Chat directly with clients about their projects

💡 Pro Tips:
• Complete your profile to stand out
• Respond to messages within 1 hour for better visibility
• Keep your skills updated to match more jobs

We're here to help you succeed. If you have any questions, our support team is always ready to help!`
    },
    passwordReset: {
        name: 'Password Reset',
        sender: 'constructconnect-security@pakistantech.me',
        subject: '🔐 Reset your ConstructConnect password',
        defaultBody: `Hi {firstName},

We received a request to reset your password for your ConstructConnect account. No worries, it happens to the best of us!

Click the button below to reset your password:
[Reset Password Link: {resetLink}]

⏱️ This link will expire in 60 minutes for security reasons.

🛡️ Security Notice:
• If you didn't request this, please ignore this email
• Your password won't change until you create a new one
• Never share this link with anyone
• Our team will never ask for your password

Need help? Contact our support team.`
    },
    inactivity: {
        name: 'Inactivity Reminder (12 Hours)',
        sender: 'constructconnect@pakistantech.me',
        subject: '👋 We haven\'t seen you lately, {firstName}!',
        defaultBody: `Hi {firstName},

We noticed you haven't applied to any jobs or responded to messages in the last 12 hours. Buyers are actively looking for skilled workers like you!

🔥 Hot Right Now: {newJobsCount}+ New Jobs Posted Today

🌟 Here's what you might be missing:

💼 Fresh Job Opportunities - New projects posted daily matching your skills
💬 Buyers Reaching Out - Clients may be waiting to discuss projects with you
📈 Grow Your Reputation - Stay active to maintain your response rate and visibility

Your next big opportunity could be waiting. Open the app and see what's new!

We believe in you,
The ConstructConnect Team`
    },
    unreadMessages: {
        name: 'Unread Messages (2+ Hours)',
        sender: 'constructconnect-notifications@pakistantech.me',
        subject: '💬 You have {unreadCount} unread messages waiting!',
        defaultBody: `Hi {firstName},

You have {unreadCount} unread message(s) waiting for you from {senderNames}. Don't keep them waiting!

⚡ Why fast responses matter:
✅ Higher response rate = Better visibility
✅ Buyers prefer quick responders
✅ More conversations = More opportunities

📱 Open the app now to read and respond to your messages.

Your quick response could lead to your next big opportunity!`
    },
    reengagement: {
        name: 'Re-engagement (7 Days Inactive)',
        sender: 'constructconnect@pakistantech.me',
        subject: '🥺 We really miss you, {firstName}!',
        defaultBody: `Hi {firstName},

It's been a while since we've seen you on ConstructConnect. We hope everything is going well! While you were away, a lot has been happening...

📊 Here's What You Missed:
• {missedJobsCount}+ New Jobs Posted
• {missedMessagesCount} Unread Messages
• {daysSinceActive} Days Away

🌟 Special For You:
Your profile is still active & ready! Jump back in and continue where you left off.

🚀 Great reasons to come back:
✅ Your skills are in demand right now
✅ New buyers joined looking for workers like you
✅ Your profile rank may decrease with inactivity
✅ Exclusive job opportunities waiting

We'd love to see you back!`
    }
};

// ============================================================================
// EMAIL PAGE
// ============================================================================
function renderEmailPage() {
    const { userId, userInfo, selectedTemplate, senderEmail, subject, bodyText } = state.emailPage;
    const templates = Object.entries(emailTemplates);
    const currentTemplate = selectedTemplate ? emailTemplates[selectedTemplate] : null;

    return `
        <div class="email-page">
            <!-- User Lookup Section -->
            <div class="card mb-3">
                <div class="card-header">
                    <h3 class="card-title">${icons.users} User Lookup</h3>
                </div>
                <div class="card-body">
                    <div class="form-group">
                        <label class="form-label">User ID</label>
                        <div class="input-group">
                            <input type="text"
                                   id="email-user-id"
                                   class="form-input"
                                   placeholder="Enter User ID..."
                                   value="${escapeHtml(userId || '')}">
                            <button class="btn btn-primary" id="lookup-user-btn">
                                ${icons.search} Lookup
                            </button>
                        </div>
                    </div>

                    ${userInfo ? `
                        <div class="user-info-card">
                            <div class="user-info-row">
                                <span class="user-info-label">Full Name:</span>
                                <span class="user-info-value">${escapeHtml(userInfo.firstName || '')} ${escapeHtml(userInfo.lastName || '')}</span>
                            </div>
                            <div class="user-info-row">
                                <span class="user-info-label">Email:</span>
                                <span class="user-info-value">${escapeHtml(userInfo.email || 'No email')}</span>
                            </div>
                            <div class="user-info-row">
                                <span class="user-info-label">Role:</span>
                                <span class="user-info-value">${escapeHtml(userInfo.role || 'Unknown')}</span>
                            </div>
                        </div>
                    ` : userId ? `
                        <div class="alert alert-warning mt-2">
                            <span>${icons.alert}</span>
                            <span>No user found with this ID or lookup not performed yet.</span>
                        </div>
                    ` : ''}
                </div>
            </div>

            <!-- Email Template Selection -->
            <div class="card mb-3">
                <div class="card-header">
                    <h3 class="card-title">${icons.email} Select Email Template</h3>
                </div>
                <div class="card-body">
                    <div class="template-grid">
                        ${templates.map(([key, template]) => `
                            <button class="template-card ${selectedTemplate === key ? 'active' : ''}" data-template="${key}">
                                <div class="template-name">${template.name}</div>
                                <div class="template-sender">${template.sender}</div>
                            </button>
                        `).join('')}
                    </div>
                </div>
            </div>

            <!-- Email Composer -->
            ${selectedTemplate ? `
            <div class="card mb-3">
                <div class="card-header">
                    <h3 class="card-title">${icons.edit} Compose Email</h3>
                </div>
                <div class="card-body">
                    <div class="form-group">
                        <label class="form-label">From (Sender Email)</label>
                        <input type="email"
                               id="email-sender"
                               class="form-input"
                               value="${escapeHtml(senderEmail || currentTemplate?.sender || '')}"
                               placeholder="sender@domain.com">
                        <p class="form-hint">Default: ${currentTemplate?.sender || 'N/A'}</p>
                    </div>

                    <div class="form-group">
                        <label class="form-label">To (Recipient Email)</label>
                        <input type="email"
                               id="email-recipient"
                               class="form-input"
                               value="${escapeHtml(userInfo?.email || '')}"
                               placeholder="recipient@example.com"
                               ${userInfo?.email ? '' : 'disabled'}>
                        ${!userInfo?.email ? '<p class="form-hint text-warning">Lookup a user first to get their email</p>' : ''}
                    </div>

                    <div class="form-group">
                        <label class="form-label">Subject</label>
                        <input type="text"
                               id="email-subject"
                               class="form-input"
                               value="${escapeHtml(subject || currentTemplate?.subject || '')}"
                               placeholder="Email subject...">
                        <p class="form-hint">Use {firstName}, {lastName}, {code}, etc. for placeholders</p>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Email Body (Text Content)</label>
                        <textarea id="email-body"
                                  class="form-textarea"
                                  rows="12"
                                  placeholder="Email content...">${escapeHtml(bodyText || currentTemplate?.defaultBody || '')}</textarea>
                        <p class="form-hint">Edit the text content. The email will be formatted with proper HTML styling automatically.</p>
                    </div>

                    <div class="d-flex gap-2">
                        <button class="btn btn-secondary" id="reset-email-btn">
                            ${icons.refresh} Reset to Default
                        </button>
                        <button class="btn btn-primary" id="preview-email-btn">
                            ${icons.search} Preview Email
                        </button>
                        <button class="btn btn-success" id="send-email-btn" ${!userInfo?.email ? 'disabled' : ''}>
                            ${icons.send} Send Email
                        </button>
                    </div>
                </div>
            </div>

            <!-- Email Preview -->
            <div class="card" id="email-preview-card" style="display: none;">
                <div class="card-header">
                    <h3 class="card-title">${icons.email} Email Preview</h3>
                </div>
                <div class="card-body">
                    <div id="email-preview-content" class="email-preview"></div>
                </div>
            </div>
            ` : `
            <div class="alert alert-info">
                <span>${icons.alert}</span>
                <div>
                    <strong>Select a Template</strong><br>
                    Choose an email template above to start composing.
                </div>
            </div>
            `}
        </div>
    `;
}

// ============================================================================
// EMAIL PAGE EVENT LISTENERS
// ============================================================================
function attachEmailPageListeners() {
    // User Lookup
    document.getElementById('lookup-user-btn')?.addEventListener('click', handleUserLookup);
    document.getElementById('email-user-id')?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') handleUserLookup();
    });

    // Template Selection
    document.querySelectorAll('.template-card').forEach(card => {
        card.addEventListener('click', (e) => {
            const templateKey = e.currentTarget.dataset.template;
            state.emailPage.selectedTemplate = templateKey;
            state.emailPage.senderEmail = emailTemplates[templateKey].sender;
            state.emailPage.subject = emailTemplates[templateKey].subject;
            state.emailPage.bodyText = emailTemplates[templateKey].defaultBody;
            saveState();
            document.getElementById('page-content').innerHTML = renderEmailPage();
            attachEmailPageListeners();
        });
    });

    // Reset to Default
    document.getElementById('reset-email-btn')?.addEventListener('click', () => {
        const template = emailTemplates[state.emailPage.selectedTemplate];
        if (template) {
            state.emailPage.senderEmail = template.sender;
            state.emailPage.subject = template.subject;
            state.emailPage.bodyText = template.defaultBody;
            saveState();
            document.getElementById('page-content').innerHTML = renderEmailPage();
            attachEmailPageListeners();
            toast.success('Reset to default template');
        }
    });

    // Save edits on input change
    document.getElementById('email-sender')?.addEventListener('input', (e) => {
        state.emailPage.senderEmail = e.target.value;
        saveState();
    });
    document.getElementById('email-subject')?.addEventListener('input', (e) => {
        state.emailPage.subject = e.target.value;
        saveState();
    });
    document.getElementById('email-body')?.addEventListener('input', (e) => {
        state.emailPage.bodyText = e.target.value;
        saveState();
    });

    // Preview Email
    document.getElementById('preview-email-btn')?.addEventListener('click', handleEmailPreview);

    // Send Email
    document.getElementById('send-email-btn')?.addEventListener('click', handleSendEmail);
}

async function handleUserLookup() {
    const userIdInput = document.getElementById('email-user-id');
    const userId = userIdInput?.value?.trim();

    if (!userId) {
        toast.error('Please enter a User ID');
        return;
    }

    try {
        toast.info('Looking up user...');
        const response = await api.getUserProfile(userId);

        if (response?.data) {
            state.emailPage.userId = userId;
            state.emailPage.userInfo = response.data;
            saveState();
            document.getElementById('page-content').innerHTML = renderEmailPage();
            attachEmailPageListeners();
            toast.success(`Found user: ${response.data.firstName} ${response.data.lastName}`);
        } else {
            state.emailPage.userId = userId;
            state.emailPage.userInfo = null;
            saveState();
            document.getElementById('page-content').innerHTML = renderEmailPage();
            attachEmailPageListeners();
            toast.error('User not found');
        }
    } catch (error) {
        console.error('User lookup error:', error);
        toast.error('Failed to lookup user: ' + (error.message || 'Unknown error'));
    }
}

function handleEmailPreview() {
    const { userInfo, subject, bodyText } = state.emailPage;
    const previewCard = document.getElementById('email-preview-card');
    const previewContent = document.getElementById('email-preview-content');

    if (!previewCard || !previewContent) return;

    // Generate sample data for preview
    const sampleData = {
        firstName: userInfo?.firstName || 'User',
        lastName: userInfo?.lastName || '',
        code: '123456',
        resetLink: 'https://app.example.com/reset-password?token=abc123',
        unreadCount: '3',
        newJobsCount: '25',
        senderNames: 'John Doe, Jane Smith',
        missedJobsCount: '50',
        missedMessagesCount: '5',
        daysSinceActive: '7'
    };

    // Replace ALL placeholders with sample/actual values
    let previewSubject = (subject || '')
        .replace(/{firstName}/g, sampleData.firstName)
        .replace(/{lastName}/g, sampleData.lastName)
        .replace(/{code}/g, sampleData.code)
        .replace(/{unreadCount}/g, sampleData.unreadCount)
        .replace(/{newJobsCount}/g, sampleData.newJobsCount)
        .replace(/{senderNames}/g, sampleData.senderNames)
        .replace(/{missedJobsCount}/g, sampleData.missedJobsCount)
        .replace(/{missedMessagesCount}/g, sampleData.missedMessagesCount)
        .replace(/{daysSinceActive}/g, sampleData.daysSinceActive);

    let previewBody = (bodyText || '')
        .replace(/{firstName}/g, sampleData.firstName)
        .replace(/{lastName}/g, sampleData.lastName)
        .replace(/{code}/g, sampleData.code)
        .replace(/{resetLink}/g, sampleData.resetLink)
        .replace(/{unreadCount}/g, sampleData.unreadCount)
        .replace(/{newJobsCount}/g, sampleData.newJobsCount)
        .replace(/{senderNames}/g, sampleData.senderNames)
        .replace(/{missedJobsCount}/g, sampleData.missedJobsCount)
        .replace(/{missedMessagesCount}/g, sampleData.missedMessagesCount)
        .replace(/{daysSinceActive}/g, sampleData.daysSinceActive);

    // Convert plain text to HTML
    const htmlBody = previewBody
        .split('\n\n')
        .map(paragraph => `<p style="margin: 0 0 16px 0; line-height: 1.6; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; color: #374151;">${paragraph.replace(/\n/g, '<br>')}</p>`)
        .join('');

    previewContent.innerHTML = `
        <div class="preview-header" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;">
            <strong>Subject:</strong> ${escapeHtml(previewSubject)}
        </div>
        <div class="preview-body" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; background: #ffffff;">
            ${htmlBody}
        </div>
        <p style="font-size: 12px; color: #6b7280; margin-top: 16px; font-style: italic;">
            Note: Placeholders are replaced with sample data for preview. Actual values will be fetched when sending.
        </p>
    `;

    previewCard.style.display = 'block';
    previewCard.scrollIntoView({ behavior: 'smooth' });
}

async function handleSendEmail() {
    const { userInfo, senderEmail, subject, bodyText, selectedTemplate } = state.emailPage;
    const recipientEmail = document.getElementById('email-recipient')?.value;

    if (!recipientEmail) {
        toast.error('No recipient email specified');
        return;
    }

    if (!subject || !bodyText) {
        toast.error('Please fill in subject and body');
        return;
    }

    try {
        toast.info('Preparing email...');

        // Fetch additional data for placeholders if needed
        let unreadCount = 0;
        let senderNames = '';
        let newJobsCount = 0;
        let missedJobsCount = 0;
        let missedMessagesCount = 0;
        let daysSinceActive = 0;

        // Try to fetch real data for the user
        if (userInfo?.uid || state.emailPage.userId) {
            const userId = userInfo?.uid || state.emailPage.userId;
            try {
                // Fetch unread messages count and sender names
                const inboxResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/inbox/unread-count/${userId}`, {
                    headers: { 'Authorization': `Bearer ${await getIdToken()}` }
                }).catch(() => null);

                if (inboxResponse?.ok) {
                    const inboxData = await inboxResponse.json();
                    unreadCount = inboxData.unreadCount || 0;
                    senderNames = inboxData.senderNames?.join(', ') || 'a buyer';
                }

                // Fetch jobs count
                const jobsResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/jobs/count`, {
                    headers: { 'Authorization': `Bearer ${await getIdToken()}` }
                }).catch(() => null);

                if (jobsResponse?.ok) {
                    const jobsData = await jobsResponse.json();
                    newJobsCount = jobsData.count || 0;
                    missedJobsCount = jobsData.count || 0;
                }

                // Calculate days since active from lastLogin
                if (userInfo?.lastLogin) {
                    const lastLoginDate = userInfo.lastLogin._seconds
                        ? new Date(userInfo.lastLogin._seconds * 1000)
                        : new Date(userInfo.lastLogin);
                    const now = new Date();
                    daysSinceActive = Math.floor((now - lastLoginDate) / (1000 * 60 * 60 * 24));
                }

                missedMessagesCount = unreadCount;
            } catch (e) {
                console.log('Could not fetch additional data, using defaults:', e);
            }
        }

        // If no sender names found, use a default
        if (!senderNames) senderNames = 'a potential buyer';

        // Generate verification code
        const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();

        // Replace ALL placeholders with actual values
        let finalSubject = subject
            .replace(/{firstName}/g, userInfo?.firstName || 'User')
            .replace(/{lastName}/g, userInfo?.lastName || '')
            .replace(/{code}/g, verificationCode)
            .replace(/{unreadCount}/g, unreadCount.toString())
            .replace(/{newJobsCount}/g, newJobsCount.toString())
            .replace(/{senderNames}/g, senderNames)
            .replace(/{missedJobsCount}/g, missedJobsCount.toString())
            .replace(/{missedMessagesCount}/g, missedMessagesCount.toString())
            .replace(/{daysSinceActive}/g, daysSinceActive.toString());

        let finalBody = bodyText
            .replace(/{firstName}/g, userInfo?.firstName || 'User')
            .replace(/{lastName}/g, userInfo?.lastName || '')
            .replace(/{code}/g, verificationCode)
            .replace(/{resetLink}/g, `${import.meta.env.VITE_APP_URL || 'https://app.example.com'}/reset-password`)
            .replace(/{unreadCount}/g, unreadCount.toString())
            .replace(/{newJobsCount}/g, newJobsCount.toString())
            .replace(/{senderNames}/g, senderNames)
            .replace(/{missedJobsCount}/g, missedJobsCount.toString())
            .replace(/{missedMessagesCount}/g, missedMessagesCount.toString())
            .replace(/{daysSinceActive}/g, daysSinceActive.toString());

        toast.info('Sending email...');

        const response = await api.sendAdminEmail({
            to: recipientEmail,
            from: senderEmail,
            subject: finalSubject,
            text: finalBody,
            templateType: selectedTemplate
        });

        if (response.success) {
            toast.success('Email sent successfully!');
        } else {
            toast.error('Failed to send email: ' + (response.message || 'Unknown error'));
        }
    } catch (error) {
        console.error('Send email error:', error);
        toast.error('Failed to send email: ' + (error.message || 'Unknown error'));
    }
}

// ============================================================================
// MAP PAGE
// ============================================================================
function renderMapPage() {
    // Generate date picker options
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Get current date for defaults
    const selectedDate = state.mapDateRange.start || new Date();
    const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();

    return `
        <div class="map-page">
            <!-- Map Controls Panel -->
            <div class="map-controls-panel card">
                <div class="map-controls-header">
                    <h3>${icons.map} Location Tracker</h3>
                </div>

                <div class="map-controls-body">
                    <!-- User Selection -->
                    <div class="form-group">
                        <label class="form-label">User ID</label>
                        <div class="input-group">
                            <input type="text"
                                   id="map-user-id"
                                   class="form-input"
                                   placeholder="Enter User ID..."
                                   value="${state.selectedMapUser || ''}">
                            <button class="btn btn-primary" id="load-map-user-btn">
                                ${icons.search} Load
                            </button>
                        </div>
                    </div>

                    <!-- Single Date Picker -->
                    <div class="form-group">
                        <label class="form-label">${icons.calendar} Select Date</label>
                        <div class="scroll-datetime-picker" id="date-picker">
                            <div class="scroll-picker-row">
                                <div class="scroll-picker-col">
                                    <label class="picker-label">Year</label>
                                    <select id="pick-year" class="scroll-select">
                                        ${years.map(y => `<option value="${y}" ${y === selectedDate.getFullYear() ? 'selected' : ''}>${y}</option>`).join('')}
                                    </select>
                                </div>
                                <div class="scroll-picker-col">
                                    <label class="picker-label">Month</label>
                                    <select id="pick-month" class="scroll-select">
                                        ${months.map((m, i) => `<option value="${i}" ${i === selectedDate.getMonth() ? 'selected' : ''}>${m}</option>`).join('')}
                                    </select>
                                </div>
                                <div class="scroll-picker-col">
                                    <label class="picker-label">Day</label>
                                    <select id="pick-day" class="scroll-select">
                                        ${Array.from({length: daysInMonth}, (_, i) => i + 1).map(d => `<option value="${d}" ${d === selectedDate.getDate() ? 'selected' : ''}>${d.toString().padStart(2, '0')}</option>`).join('')}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Available Locations Dropdown -->
                    <div class="form-group">
                        <div class="locations-label-row">
                            <label class="form-label">${icons.navigation} Available Locations</label>
                        </div>
                        <div class="locations-dropdown-container">
                            <div class="locations-dropdown" id="locations-dropdown">
                                <div class="dropdown-header" id="dropdown-header">
                                    <span class="dropdown-text">Select a date and load locations</span>
                                    <span class="dropdown-arrow">${icons.chevronDown || '▼'}</span>
                                </div>
                                <div class="dropdown-content" id="dropdown-content" style="display: none;">
                                    <div class="dropdown-loading" id="dropdown-loading" style="display: none;">
                                        <div class="spinner-small"></div>
                                        <span>Loading locations...</span>
                                    </div>
                                    <div class="dropdown-list" id="dropdown-list">
                                        <!-- Location items will be inserted here -->
                                    </div>
                                    <button class="btn btn-secondary btn-sm dropdown-load-more" id="load-more-locations-btn" style="display: none;">
                                        Show More
                                    </button>
                                </div>
                            </div>
                            <div class="location-count-badge" id="location-count-badge" style="display: none;">
                                <span id="loaded-count">0</span> / <span id="total-count">0</span>
                            </div>
                        </div>
                    </div>

                    <!-- Quick Date Filters -->
                    <div class="form-group">
                        <label class="form-label">Quick Select</label>
                        <div class="quick-date-buttons">
                            <button class="btn btn-sm btn-secondary" data-days="0">Today</button>
                            <button class="btn btn-sm btn-secondary" data-days="1">Yesterday</button>
                            <button class="btn btn-sm btn-secondary" data-days="7">7 days ago</button>
                        </div>
                    </div>

                    <!-- Map Actions -->
                    <div class="form-group">
                        <div class="map-action-buttons">
                            <button class="btn btn-secondary" id="refresh-map-btn">
                                ${icons.refresh} Refresh
                            </button>
                            <button class="btn btn-secondary" id="fit-bounds-btn">
                                ${icons.navigation} Fit All
                            </button>
                            <button class="btn btn-secondary" id="clear-map-btn">
                                ${icons.x} Clear
                            </button>
                        </div>
                        <button class="btn btn-success btn-block map-fitall-more-btn" id="fitall-load-more-btn" style="display: none;" disabled>
                            Show More Fit All Locations
                        </button>
                    </div>
                </div>

                <!-- Location Stats -->
                <div class="map-stats-section" id="map-stats">
                    <h4>Statistics</h4>
                    <div class="stats-grid-mini">
                        <div class="stat-item-mini">
                            <span class="stat-label">Total Points</span>
                            <span class="stat-value" id="stat-total-points">${state.mapLocations.length}</span>
                        </div>
                        <div class="stat-item-mini">
                            <span class="stat-label">Distance</span>
                            <span class="stat-value" id="stat-distance">0 km</span>
                        </div>
                        <div class="stat-item-mini">
                            <span class="stat-label">Duration</span>
                            <span class="stat-value" id="stat-duration">0h</span>
                        </div>
                    </div>
                </div>

                <!-- Location List -->
                <div class="location-list-section">
                    <h4>Location History</h4>
                    <div class="location-list" id="location-list">
                        ${state.mapLocations.length > 0 ?
                            state.mapLocations.slice(0, 50).map((loc, index) => `
                                <div class="location-item"
                                     data-index="${index}"
                                     onclick="jumpToLocation(${index})">
                                    <div class="location-item-icon">${icons.location}</div>
                                    <div class="location-item-content">
                                        <div class="location-item-coords">
                                            ${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}
                                        </div>
                                        <div class="location-item-time">
                                            ${formatDate(loc.timestamp || loc.createdAt || loc.clientTimestamp)}
                                        </div>
                                    </div>
                                </div>
                            `).join('') :
                            '<div class="empty-state-mini">No locations loaded. Enter a User ID and click Load.</div>'
                        }
                    </div>
                </div>
            </div>

            <!-- Map Container -->
            <div class="map-container-wrapper">
                <div class="map-view-controls" id="map-view-controls">
                    <button class="map-view-btn ${currentMapState.viewType === 'roadmap' ? 'active' : ''}" data-view="roadmap" onclick="changeMapView('roadmap')" title="Road Map">
                        🗺️ Map
                    </button>
                    <button class="map-view-btn ${currentMapState.viewType === 'satellite' ? 'active' : ''}" data-view="satellite" onclick="changeMapView('satellite')" title="Satellite View">
                        🛰️ Satellite
                    </button>
                    <button class="map-view-btn ${currentMapState.viewType === 'hybrid' ? 'active' : ''}" data-view="hybrid" onclick="changeMapView('hybrid')" title="Hybrid (Satellite + Labels)">
                        🌍 Hybrid
                    </button>
                    <button class="map-view-btn ${currentMapState.viewType === 'terrain' ? 'active' : ''}" data-view="terrain" onclick="changeMapView('terrain')" title="Terrain View">
                        ⛰️ Terrain
                    </button>
                </div>
                <div id="location-map" class="location-map">
                    <!-- Google Maps will be embedded here -->
                    <div class="google-map-placeholder" id="google-map-placeholder">
                        <div class="google-map-empty-state">
                            ${icons.map}
                            <h3>Google Maps Location Viewer</h3>
                            <p>Enter a User ID and click Load to view locations on Google Maps</p>
                            <p class="text-muted">Full Street View, Satellite, and Zoom available</p>
                        </div>
                    </div>
                    <iframe id="google-map-iframe"
                            class="google-map-iframe"
                            style="display: none;"
                            frameborder="0"
                            allowfullscreen
                            referrerpolicy="no-referrer-when-downgrade">
                    </iframe>
                    <div id="leaflet-fitall-map" class="leaflet-fitall-map" style="display: none;"></div>
                    <div class="fitall-loading-overlay" id="fitall-loading-overlay" style="display: none;">
                        <div class="spinner-small"></div>
                        <span id="fitall-loading-text">Loading fit all locations...</span>
                    </div>
                </div>

                <!-- Current Location Info Panel -->
                <div class="map-info-panel" id="map-info-panel" style="display: none;">
                    <div class="map-info-header">
                        <h4>📍 Current Location</h4>
                        <button class="btn btn-sm btn-secondary" onclick="hideMapInfo()">×</button>
                    </div>
                    <div id="map-info-details"></div>
                </div>
            </div>
        </div>
    `;
}

// ============================================================================
// MAP FUNCTIONS - GOOGLE MAPS EMBED
// ============================================================================

// Current map state for view switching — declared here so all map functions can safely access it
let currentMapState = {
    latitude: null,
    longitude: null,
    zoom: 18,
    viewType: 'roadmap' // roadmap, satellite, hybrid, terrain
};

let fitAllMapState = {
    mode: 'google',
    leafletMap: null,
    markerLayer: null,
    groupedLocations: [],
    allLoadedLocations: [],
    batchSize: 200,
    currentLimit: 0,
    nextCursor: null,
    hasMore: false,
    isLoading: false,
    requestSequence: 0,
    selectedDateKey: null,
    selectedUserId: null
};

function getSelectedDateRangeFromPicker() {
    const yearEl = document.getElementById('pick-year');
    const monthEl = document.getElementById('pick-month');
    const dayEl = document.getElementById('pick-day');

    if (!yearEl || !monthEl || !dayEl) return null;

    const year = parseInt(yearEl.value, 10);
    const month = parseInt(monthEl.value, 10);
    const day = parseInt(dayEl.value, 10);

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;

    const startDate = new Date(year, month, day, 0, 0, 0, 0);
    const endDate = new Date(year, month, day, 23, 59, 59, 999);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    return { startDate, endDate, dateKey };
}

function setFitAllLoading(isLoading, text = 'Loading fit all locations...') {
    const loadingOverlay = document.getElementById('fitall-loading-overlay');
    const loadingText = document.getElementById('fitall-loading-text');
    if (loadingText) loadingText.textContent = text;
    if (loadingOverlay) loadingOverlay.style.display = isLoading ? 'flex' : 'none';
    fitAllMapState.isLoading = isLoading;
}

function updateFitAllLoadMoreButton() {
    const btn = document.getElementById('fitall-load-more-btn');
    if (!btn) return;

    if (fitAllMapState.mode !== 'leaflet' || fitAllMapState.allLoadedLocations.length === 0) {
        btn.style.display = 'none';
        btn.disabled = true;
        btn.textContent = 'Show More Fit All Locations';
        btn.classList.remove('btn-secondary');
        btn.classList.add('btn-success');
        return;
    }

    btn.style.display = 'block';

    if (fitAllMapState.isLoading) {
        btn.disabled = true;
        btn.textContent = 'Loading...';
        btn.classList.remove('btn-success');
        btn.classList.add('btn-secondary');
        return;
    }

    if (fitAllMapState.hasMore) {
        btn.disabled = false;
        btn.textContent = 'Show More Fit All Locations';
        btn.classList.remove('btn-secondary');
        btn.classList.add('btn-success');
    } else {
        btn.disabled = true;
        btn.textContent = 'All Fit All Locations Loaded';
        btn.classList.remove('btn-success');
        btn.classList.add('btn-secondary');
    }
}

function switchToLeafletFitAllMode() {
    const placeholder = document.getElementById('google-map-placeholder');
    const iframe = document.getElementById('google-map-iframe');
    const leafletContainer = document.getElementById('leaflet-fitall-map');
    const viewControls = document.getElementById('map-view-controls');

    if (placeholder) placeholder.style.display = 'none';
    if (iframe) iframe.style.display = 'none';
    if (leafletContainer) leafletContainer.style.display = 'block';
    if (viewControls) viewControls.style.display = 'none';

    fitAllMapState.mode = 'leaflet';

    if (fitAllMapState.leafletMap) {
        setTimeout(() => fitAllMapState.leafletMap.invalidateSize(), 0);
    }

    updateFitAllLoadMoreButton();
}

function switchToGoogleSingleMode() {
    const leafletContainer = document.getElementById('leaflet-fitall-map');
    const viewControls = document.getElementById('map-view-controls');

    if (leafletContainer) leafletContainer.style.display = 'none';
    if (viewControls) viewControls.style.display = 'flex';

    fitAllMapState.mode = 'google';
    updateFitAllLoadMoreButton();
}

function ensureLeafletMap() {
    const container = document.getElementById('leaflet-fitall-map');
    if (!container) return null;

    if (!fitAllMapState.leafletMap) {
        fitAllMapState.leafletMap = L.map('leaflet-fitall-map', {
            zoomControl: true,
            preferCanvas: true
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19
        }).addTo(fitAllMapState.leafletMap);

        fitAllMapState.markerLayer = L.layerGroup().addTo(fitAllMapState.leafletMap);

        // Keep popup behavior predictable: clicking map background closes open popup.
        fitAllMapState.leafletMap.on('click', () => {
            fitAllMapState.leafletMap.closePopup();
        });
    }

    return fitAllMapState.leafletMap;
}

function groupLocationsByCoordinate(locations) {
    const sortedLocations = [...locations].sort((a, b) => getTimestamp(a) - getTimestamp(b));
    const groups = [];

    const getGroupingRadiusMeters = (location) => {
        const accuracy = Number(location?.accuracy);
        if (Number.isFinite(accuracy) && accuracy > 0 && accuracy <= 8) return 3;
        return 5;
    };

    sortedLocations.forEach((location) => {
        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const pointRadius = getGroupingRadiusMeters(location);

        let bestGroup = null;
        let bestDistance = Infinity;

        groups.forEach((group) => {
            const distance = calculateDistance(lat, lng, group.latitude, group.longitude);
            const allowedRadius = Math.max(pointRadius, group.radiusMeters);
            if (distance <= allowedRadius && distance < bestDistance) {
                bestDistance = distance;
                bestGroup = group;
            }
        });

        if (!bestGroup) {
            groups.push({
                key: `${lat.toFixed(6)},${lng.toFixed(6)}`,
                latitude: lat,
                longitude: lng,
                radiusMeters: pointRadius,
                visits: [location]
            });
            return;
        }

        const previousCount = bestGroup.visits.length;
        bestGroup.visits.push(location);
        bestGroup.radiusMeters = Math.max(bestGroup.radiusMeters, pointRadius);
        bestGroup.latitude = ((bestGroup.latitude * previousCount) + lat) / (previousCount + 1);
        bestGroup.longitude = ((bestGroup.longitude * previousCount) + lng) / (previousCount + 1);
    });

    return groups.map((group, index) => {
        group.key = `group-${index}`;
        group.latitude = Number(group.latitude.toFixed(6));
        group.longitude = Number(group.longitude.toFixed(6));
        group.visits.sort((a, b) => getTimestamp(a) - getTimestamp(b));
        return group;
    });
}

function buildFitAllPopupHtml(group) {
    const visitRows = group.visits.map((visit, index) => {
        const visitTime = formatDate(visit.timestamp || visit.createdAt || visit.clientTimestamp);
        const lat = Number(visit.latitude);
        const lng = Number(visit.longitude);
        const coordinateText = Number.isFinite(lat) && Number.isFinite(lng)
            ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
            : 'Unknown coordinates';

        return `
            <div class="fitall-popup-row">
                <span class="fitall-popup-visit-index">#${index + 1}</span>
                <span class="fitall-popup-visit-time">${visitTime}</span>
                <span class="fitall-popup-visit-coords">${coordinateText}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="fitall-popup-content">
            <div class="fitall-popup-title">📍 ${group.visits.length} visit${group.visits.length === 1 ? '' : 's'} within ${group.radiusMeters}m</div>
            <div class="fitall-popup-list">${visitRows}</div>
        </div>
    `;
}

function createFitAllMarkerIcon(visitCount) {
    const badge = visitCount > 1
        ? `<span class="fitall-marker-badge">${visitCount > 99 ? '99+' : visitCount}</span>`
        : '';

    return L.divIcon({
        className: 'fitall-marker-wrapper',
        html: `
            <div class="fitall-marker-pin" aria-label="${visitCount} location visit${visitCount === 1 ? '' : 's'}">
                <svg viewBox="0 0 24 36" role="img" aria-hidden="true" focusable="false">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 8.4 8.73 19.115 11.06 21.86a1.2 1.2 0 0 0 1.88 0C15.27 31.115 24 20.4 24 12 24 5.373 18.627 0 12 0Z" />
                    <circle cx="12" cy="12" r="4.2" />
                </svg>
                ${badge}
            </div>
        `,
        iconSize: [26, 38],
        iconAnchor: [13, 38],
        popupAnchor: [0, -34]
    });
}

function renderLeafletFitAllLocations(locations) {
    const leafletMap = ensureLeafletMap();
    if (!leafletMap || !fitAllMapState.markerLayer) return;

    switchToLeafletFitAllMode();
    fitAllMapState.markerLayer.clearLayers();

    const grouped = groupLocationsByCoordinate(locations);
    fitAllMapState.groupedLocations = grouped;

    if (grouped.length === 0) {
        leafletMap.setView([0, 0], 2);
        return;
    }

    const bounds = [];

    grouped.forEach((group) => {
        const marker = L.marker([group.latitude, group.longitude], {
            icon: createFitAllMarkerIcon(group.visits.length),
            keyboard: true,
            title: `${group.visits.length} location visit${group.visits.length === 1 ? '' : 's'}`
        });

        marker.bindPopup(buildFitAllPopupHtml(group), {
            maxWidth: 340,
            className: 'fitall-popup',
            autoClose: true,
            closeOnClick: true
        });

        marker.addTo(fitAllMapState.markerLayer);
        bounds.push([group.latitude, group.longitude]);
    });

    if (bounds.length === 1) {
        leafletMap.setView(bounds[0], 17);
    } else {
        leafletMap.fitBounds(bounds, {
            padding: [40, 40],
            maxZoom: 18
        });
    }
}

async function loadFitAllLocations({ append = false } = {}) {
    if (fitAllMapState.isLoading) return;

    const userId = document.getElementById('map-user-id')?.value?.trim();
    if (!userId) {
        toast.info('Enter a User ID first');
        return;
    }

    const selectedRange = getSelectedDateRangeFromPicker();
    if (!selectedRange) {
        toast.warning('Please select a date and try again.');
        return;
    }

    const contextChanged = fitAllMapState.selectedUserId !== userId || fitAllMapState.selectedDateKey !== selectedRange.dateKey;
    if (contextChanged) {
        append = false;
        fitAllMapState.currentLimit = 0;
        fitAllMapState.nextCursor = null;
        fitAllMapState.hasMore = false;
        fitAllMapState.allLoadedLocations = [];
        fitAllMapState.groupedLocations = [];
    }

    if (append && !fitAllMapState.hasMore) {
        toast.info('All fit all locations are already loaded.');
        updateFitAllLoadMoreButton();
        return;
    }

    const previousCount = fitAllMapState.allLoadedLocations.length;
    const requestedLimit = fitAllMapState.batchSize;
    const requestId = ++fitAllMapState.requestSequence;

    setFitAllLoading(true, append ? 'Loading more fit all locations...' : 'Loading fit all locations...');
    updateFitAllLoadMoreButton();

    try {
        const response = await api.getUserLocationsByDateRange(
            userId,
            selectedRange.startDate.toISOString(),
            selectedRange.endDate.toISOString(),
            requestedLimit,
            append ? fitAllMapState.nextCursor : null
        );

        if (requestId !== fitAllMapState.requestSequence) {
            return;
        }

        const responseData = response.data || {};
        const responseLocations = responseData.locations || [];

        if (!response.success || !Array.isArray(responseLocations)) {
            toast.error(response.message || 'Failed to load fit all locations.');
            return;
        }

        const incomingLocations = responseLocations.filter((location) => (
            Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude))
        ));

        const loadedLocations = append
            ? mergeLocationsByIdentity(fitAllMapState.allLoadedLocations, incomingLocations)
            : incomingLocations;

        if (loadedLocations.length === 0) {
            toast.info('No locations found for the selected date.');
            state.mapLocations = [];
            fitAllMapState.mode = 'google';
            switchToGoogleSingleMode();
            initializeMap();
            updateMapStats();
            updateLocationList();
            updateFitAllLoadMoreButton();
            return;
        }

        fitAllMapState.currentLimit = loadedLocations.length;
        fitAllMapState.selectedUserId = userId;
        fitAllMapState.selectedDateKey = selectedRange.dateKey;
        fitAllMapState.nextCursor = responseData.nextCursor || null;
        fitAllMapState.allLoadedLocations = loadedLocations;

        const grew = loadedLocations.length > previousCount;
        fitAllMapState.hasMore = Boolean(responseData.hasMore);
        if (append && !grew) {
            fitAllMapState.hasMore = false;
            fitAllMapState.nextCursor = null;
        }

        state.mapLocations = loadedLocations;
        renderLeafletFitAllLocations(loadedLocations);
        updateMapStats();
        updateLocationList();

        if (append) {
            toast.success(`Loaded ${loadedLocations.length} fit all locations`);
        } else {
            toast.success(`Showing fit all view for ${loadedLocations.length} locations`);
        }
    } catch (error) {
        console.error('Failed to load fit all locations:', error);
        toast.error('Failed to load fit all locations: ' + error.message);
    } finally {
        if (requestId === fitAllMapState.requestSequence) {
            setFitAllLoading(false);
            updateFitAllLoadMoreButton();
        }
    }
}

function initializeMap() {
    if (fitAllMapState.mode === 'leaflet') {
        switchToLeafletFitAllMode();
        return;
    }

    // Only reset to placeholder if no map is currently loaded
    // (do NOT reset if currentMapState already has coordinates — that would clobber a visible map)
    if (currentMapState.latitude !== null && currentMapState.longitude !== null) {
        // Map is already loaded — restore it
        updateMapWithCurrentView();
        return;
    }

    const placeholder = document.getElementById('google-map-placeholder');
    const iframe = document.getElementById('google-map-iframe');

    if (placeholder) placeholder.style.display = 'flex';
    if (iframe) iframe.style.display = 'none';
}

function showGoogleMapsEmbed(latitude, longitude, zoom = 18) {
    const placeholder = document.getElementById('google-map-placeholder');
    const iframe = document.getElementById('google-map-iframe');

    if (!iframe) return;

    switchToGoogleSingleMode();

    // Store current state for view switching
    currentMapState.latitude = latitude;
    currentMapState.longitude = longitude;
    currentMapState.zoom = zoom;

    // Map view type to Google Maps 't' parameter
    const viewTypeMap = {
        'roadmap': 'm',
        'satellite': 'k',
        'hybrid': 'h',
        'terrain': 'p'
    };

    const mapType = viewTypeMap[currentMapState.viewType] || 'm';

    // Build Google Maps embed URL with current view type
    const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}&t=${mapType}&z=${zoom}&ie=UTF8&iwloc=&output=embed`;

    iframe.src = mapUrl;

    if (placeholder) placeholder.style.display = 'none';
    iframe.style.display = 'block';
}

function showGoogleMapsWithPath(locations) {
    if (!locations || locations.length === 0) return;

    const placeholder = document.getElementById('google-map-placeholder');
    const iframe = document.getElementById('google-map-iframe');

    if (!iframe) return;

    switchToGoogleSingleMode();

    // For multiple locations, show the latest location with high zoom
    const latestLoc = locations[locations.length - 1];
    const zoom = locations.length === 1 ? 18 : 15;

    // Calculate center of all points
    const centerLat = locations.reduce((sum, loc) => sum + loc.latitude, 0) / locations.length;
    const centerLng = locations.reduce((sum, loc) => sum + loc.longitude, 0) / locations.length;

    // Store current state for view switching
    currentMapState.latitude = centerLat;
    currentMapState.longitude = centerLng;
    currentMapState.zoom = zoom;

    // Map view type to Google Maps 't' parameter
    const viewTypeMap = {
        'roadmap': 'm',
        'satellite': 'k',
        'hybrid': 'h',
        'terrain': 'p'
    };

    const mapType = viewTypeMap[currentMapState.viewType] || 'm';

    // Use Google Maps with center coordinates and current view type
    const mapUrl = `https://maps.google.com/maps?q=${centerLat},${centerLng}&t=${mapType}&z=${zoom}&ie=UTF8&iwloc=&output=embed`;

    iframe.src = mapUrl;

    if (placeholder) placeholder.style.display = 'none';
    iframe.style.display = 'block';
}

function clearMap() {
    // Reset locations
    state.mapLocations = [];

    // Reset map state so buttons don't try to reload after clear
    currentMapState.latitude = null;
    currentMapState.longitude = null;
    currentMapState.zoom = 18;

    // Hide iframe and show placeholder
    const placeholder = document.getElementById('google-map-placeholder');
    const iframe = document.getElementById('google-map-iframe');

    if (iframe) {
        iframe.src = '';
        iframe.style.display = 'none';
    }
    if (placeholder) placeholder.style.display = 'flex';

    fitAllMapState.mode = 'google';
    fitAllMapState.currentLimit = 0;
    fitAllMapState.nextCursor = null;
    fitAllMapState.hasMore = false;
    fitAllMapState.allLoadedLocations = [];
    fitAllMapState.groupedLocations = [];
    fitAllMapState.selectedDateKey = null;
    fitAllMapState.selectedUserId = null;

    if (fitAllMapState.markerLayer) {
        fitAllMapState.markerLayer.clearLayers();
    }

    switchToGoogleSingleMode();
    setFitAllLoading(false);

    // Update UI
    updateMapStats();
    updateLocationList();
}

// ============================================================================
// MAP VIEW CONTROLS
// ============================================================================

/**
 * Change the map view type (Roadmap, Satellite, Hybrid, Terrain)
 * @param {string} viewType - The view type: 'roadmap', 'satellite', 'hybrid', 'terrain'
 */
function changeMapView(viewType) {
    currentMapState.viewType = viewType;

    // Update active button state
    const buttons = document.querySelectorAll('.map-view-btn');
    buttons.forEach(btn => {
        btn.classList.remove('active');
        if (btn.dataset.view === viewType) {
            btn.classList.add('active');
        }
    });

    // Update the map if coordinates are loaded (strict null check — 0 is a valid coordinate)
    if (currentMapState.latitude !== null && currentMapState.longitude !== null) {
        updateMapWithCurrentView();
    } else {
        // No location loaded yet — let the user know
        toast.info('Load a location first, then switch map view');
    }
}

/**
 * Update the map iframe with current view type
 */
function updateMapWithCurrentView() {
    const iframe = document.getElementById('google-map-iframe');
    const placeholder = document.getElementById('google-map-placeholder');
    if (!iframe || currentMapState.latitude === null || currentMapState.longitude === null) return;

    switchToGoogleSingleMode();

    // Map view type to Google Maps 't' parameter
    const viewTypeMap = {
        'roadmap': 'm',    // Normal map
        'satellite': 'k',   // Satellite
        'hybrid': 'h',      // Hybrid (satellite + labels)
        'terrain': 'p'      // Terrain
    };

    const mapType = viewTypeMap[currentMapState.viewType] || 'm';
    const mapUrl = `https://maps.google.com/maps?q=${currentMapState.latitude},${currentMapState.longitude}&t=${mapType}&z=${currentMapState.zoom}&ie=UTF8&iwloc=&output=embed`;

    // Directly update src — no intermediate blank step (which caused race conditions)
    iframe.src = mapUrl;
    iframe.style.display = 'block';
    if (placeholder) placeholder.style.display = 'none';
}

// Make changeMapView globally accessible for onclick
window.changeMapView = changeMapView;

// Expose geofence helpers so inline handlers / event callbacks cannot trigger ReferenceError
// This ensures functions like clearGeofenceEdgeEffects and styleGeofencePolygonLayer are reachable
// from bundled/inline contexts and helps prevent "is not defined" runtime errors.
if (typeof window !== 'undefined') {
    window.clearGeofenceEdgeEffects = typeof clearGeofenceEdgeEffects === 'function' ? clearGeofenceEdgeEffects : undefined;
    window.styleGeofencePolygonLayer = typeof styleGeofencePolygonLayer === 'function' ? styleGeofencePolygonLayer : undefined;
    window.applyGeofenceEdgeEffects = typeof applyGeofenceEdgeEffects === 'function' ? applyGeofenceEdgeEffects : undefined;
    window.enableGeofencePencilDrawing = typeof enableGeofencePencilDrawing === 'function' ? enableGeofencePencilDrawing : undefined;
    window.disableGeofencePencilDrawing = typeof disableGeofencePencilDrawing === 'function' ? disableGeofencePencilDrawing : undefined;
}


/**
 * Update single date picker elements with a given date
 * @param {Date} date - The date to set
 */
function updateDatePicker(date) {
    const yearEl = document.getElementById('pick-year');
    const monthEl = document.getElementById('pick-month');
    const dayEl = document.getElementById('pick-day');

    if (yearEl) yearEl.value = date.getFullYear();
    if (monthEl) monthEl.value = date.getMonth();

    // Update days dropdown based on month/year
    if (dayEl && monthEl && yearEl) {
        const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const currentDay = Math.min(date.getDate(), daysInMonth);

        // Rebuild day options
        dayEl.innerHTML = '';
        for (let d = 1; d <= daysInMonth; d++) {
            const option = document.createElement('option');
            option.value = d;
            option.textContent = d.toString().padStart(2, '0');
            if (d === currentDay) option.selected = true;
            dayEl.appendChild(option);
        }
    }
}

// State for location dropdown pagination
let locationDropdownState = {
    locations: [],
    displayedCount: 0,
    batchSize: 50,
    isOpen: false,
    isLoading: false,
    requestSequence: 0,
    totalAvailable: 0
};

/**
 * Reset the location dropdown state (called when date changes)
 */
function resetLocationDropdown() {
    locationDropdownState.locations = [];
    locationDropdownState.displayedCount = 0;
    locationDropdownState.totalAvailable = 0;
    locationDropdownState.isOpen = false;

    const dropdownList = document.getElementById('dropdown-list');
    const content = document.getElementById('dropdown-content');
    const loadMoreBtn = document.getElementById('load-more-locations-btn');
    const countBadge = document.getElementById('location-count-badge');
    const arrow = document.querySelector('.dropdown-arrow');

    if (dropdownList) dropdownList.innerHTML = '';
    if (content) content.style.display = 'none';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    if (countBadge) countBadge.style.display = 'none';
    if (arrow) arrow.style.transform = 'rotate(0deg)';

    fitAllMapState.currentLimit = 0;
    fitAllMapState.nextCursor = null;
    fitAllMapState.hasMore = false;
    fitAllMapState.allLoadedLocations = [];
    fitAllMapState.groupedLocations = [];
    fitAllMapState.selectedDateKey = null;
    fitAllMapState.selectedUserId = null;
    if (fitAllMapState.markerLayer) {
        fitAllMapState.markerLayer.clearLayers();
    }
    updateFitAllLoadMoreButton();
}

/**
 * Update dropdown header to show selected date (prompts user to click)
 */
function updateDropdownHeaderForSelectedDate() {
    const dropdownHeader = document.getElementById('dropdown-header');
    if (!dropdownHeader) return;

    const year = parseInt(document.getElementById('pick-year')?.value) || new Date().getFullYear();
    const month = parseInt(document.getElementById('pick-month')?.value) || 0;
    const day = parseInt(document.getElementById('pick-day')?.value) || 1;

    const selectedDate = new Date(year, month, day);
    const dateStr = selectedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });

    dropdownHeader.querySelector('.dropdown-text').textContent = `Click to load locations for ${dateStr}`;
}

/**
 * Toggle location dropdown visibility - loads locations on first open
 */
function toggleLocationDropdown() {
    const content = document.getElementById('dropdown-content');
    const arrow = document.querySelector('.dropdown-arrow');

    if (!content) return;

    // If opening and no locations loaded yet, load them
    if (!locationDropdownState.isOpen && locationDropdownState.locations.length === 0) {
        const userId = document.getElementById('map-user-id')?.value?.trim();
        if (!userId) {
            toast.warning('Please enter a User ID first');
            return;
        }
        loadLocationsForDate();
        return; // loadLocationsForDate will open the dropdown
    }

    locationDropdownState.isOpen = !locationDropdownState.isOpen;
    content.style.display = locationDropdownState.isOpen ? 'block' : 'none';

    if (arrow) {
        arrow.style.transform = locationDropdownState.isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
    }
}

/**
 * Load locations for the selected date and populate dropdown
 */
async function loadLocationsForDate() {
    const userId = document.getElementById('map-user-id')?.value?.trim();
    if (!userId) {
        toast.warning('Please enter a User ID first');
        return;
    }

    state.selectedMapUser = userId;

    // Read from single date picker
    const year = parseInt(document.getElementById('pick-year')?.value) || new Date().getFullYear();
    const month = parseInt(document.getElementById('pick-month')?.value) || 0;
    const day = parseInt(document.getElementById('pick-day')?.value) || 1;

    // Set date range to cover the entire selected day
    const startDate = new Date(year, month, day, 0, 0, 0);
    const endDate = new Date(year, month, day, 23, 59, 59);

    state.mapDateRange.start = startDate;
    state.mapDateRange.end = endDate;

    // Show loading in dropdown
    const dropdownLoading = document.getElementById('dropdown-loading');
    const dropdownList = document.getElementById('dropdown-list');
    const dropdownHeader = document.getElementById('dropdown-header');
    const loadMoreBtn = document.getElementById('load-more-locations-btn');
    const countBadge = document.getElementById('location-count-badge');

    if (dropdownLoading) dropdownLoading.style.display = 'flex';
    if (dropdownList) dropdownList.innerHTML = '';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    if (countBadge) countBadge.style.display = 'none';

    // Open dropdown
    const content = document.getElementById('dropdown-content');
    if (content) content.style.display = 'block';
    locationDropdownState.isOpen = true;
    locationDropdownState.isLoading = true;
    const requestId = ++locationDropdownState.requestSequence;

    try {
        // Fetch locations for the selected day
        const response = await api.getUserLocationsByDateRange(
            userId,
            startDate.toISOString(),
            endDate.toISOString(),
            500 // Get up to 500 locations for the day
        );

        if (requestId !== locationDropdownState.requestSequence) {
            return;
        }

        const responseLocations = response.data?.locations || [];

        if (response.success && Array.isArray(responseLocations)) {
            locationDropdownState.locations = responseLocations;
            locationDropdownState.totalAvailable = responseLocations.length;
            locationDropdownState.displayedCount = 0;

            // Display first batch
            displayMoreLocations();

            // Update header
            if (dropdownHeader) {
                const dateStr = startDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
                dropdownHeader.querySelector('.dropdown-text').textContent =
                    `${responseLocations.length} locations on ${dateStr}`;
            }

            // Show count badge
            if (countBadge) {
                countBadge.style.display = 'flex';
                document.getElementById('total-count').textContent = locationDropdownState.totalAvailable;
            }

        } else {
            if (dropdownList) {
                dropdownList.innerHTML = `
                    <div class="dropdown-empty">
                        <span>No locations found for this date</span>
                    </div>
                `;
            }
            if (dropdownHeader) {
                dropdownHeader.querySelector('.dropdown-text').textContent = 'No locations found';
            }
        }

    } catch (error) {
        console.error('Failed to load locations:', error);
        if (dropdownList) {
            dropdownList.innerHTML = `
                <div class="dropdown-empty dropdown-error">
                    <span>Error loading locations</span>
                </div>
            `;
        }
        toast.error('Failed to load locations: ' + error.message);
    } finally {
        if (requestId === locationDropdownState.requestSequence) {
            locationDropdownState.isLoading = false;
            if (dropdownLoading) dropdownLoading.style.display = 'none';
        }
    }
}

/**
 * Display more locations in the dropdown
 */
function displayMoreLocations() {
    const dropdownList = document.getElementById('dropdown-list');
    const loadMoreBtn = document.getElementById('load-more-locations-btn');
    const loadedCountEl = document.getElementById('loaded-count');

    if (!dropdownList) return;

    const startIdx = locationDropdownState.displayedCount;
    const endIdx = Math.min(
        startIdx + locationDropdownState.batchSize,
        locationDropdownState.totalAvailable
    );

    // Create location items
    for (let i = startIdx; i < endIdx; i++) {
        const loc = locationDropdownState.locations[i];
        const item = createLocationDropdownItem(loc, i);
        dropdownList.appendChild(item);
    }

    locationDropdownState.displayedCount = endIdx;

    // Update loaded count
    if (loadedCountEl) {
        loadedCountEl.textContent = locationDropdownState.displayedCount;
    }

    // Show/hide load more button
    if (loadMoreBtn) {
        if (locationDropdownState.displayedCount < locationDropdownState.totalAvailable) {
            loadMoreBtn.style.display = 'block';
            const remaining = locationDropdownState.totalAvailable - locationDropdownState.displayedCount;
            loadMoreBtn.textContent = `Show More (${remaining} remaining)`;
        } else {
            loadMoreBtn.style.display = 'none';
        }
    }
}

/**
 * Create a dropdown item for a location
 */
function createLocationDropdownItem(location, index) {
    const item = document.createElement('div');
    item.className = 'dropdown-item';
    item.dataset.index = index;

    const timestamp = location.timestamp || location.clientTimestamp || location.createdAt;
    const date = timestamp ? new Date(timestamp._seconds ? timestamp._seconds * 1000 : timestamp) : null;
    const timeStr = date ? date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }) : 'Unknown time';

    const lat = location.latitude?.toFixed(6) || 'N/A';
    const lng = location.longitude?.toFixed(6) || 'N/A';
    const accuracy = location.accuracy ? `±${Math.round(location.accuracy)}m` : '';

    item.innerHTML = `
        <div class="dropdown-item-time">${timeStr}</div>
        <div class="dropdown-item-coords">${lat}, ${lng}</div>
        ${accuracy ? `<div class="dropdown-item-accuracy">${accuracy}</div>` : ''}
    `;

    // Click handler - select and show on map
    item.addEventListener('click', () => {
        selectLocation(location, index);
    });

    return item;
}


/**
 * Select a location from dropdown and show on map
 */
function selectLocation(location, index) {
    // Highlight selected item
    document.querySelectorAll('.dropdown-item').forEach(item => {
        item.classList.remove('selected');
    });
    const selectedItem = document.querySelector(`.dropdown-item[data-index="${index}"]`);
    if (selectedItem) selectedItem.classList.add('selected');

    // Update map with this location
    state.mapLocations = [location];
    updateMapWithLocation(location);
    updateMapStats();

    // Do NOT close dropdown - user can pick another time
}

/**
 * Update map to show a single location
 */
function updateMapWithLocation(location) {
    if (location.latitude == null || location.longitude == null) return;

    // Update currentMapState so view buttons work correctly
    currentMapState.latitude = location.latitude;
    currentMapState.longitude = location.longitude;
    currentMapState.zoom = 18;

    // Delegate to shared function so view type is respected
    updateMapWithCurrentView();
}

async function loadUserLocations() {
    // This now just triggers loading locations for the selected date
    await loadLocationsForDate();
}

function getTimestamp(location) {
    if (location.timestamp) {
        if (location.timestamp._seconds) return location.timestamp._seconds * 1000;
        return new Date(location.timestamp).getTime();
    }
    if (location.clientTimestamp) {
        if (location.clientTimestamp._seconds) return location.clientTimestamp._seconds * 1000;
        return new Date(location.clientTimestamp).getTime();
    }
    if (location.createdAt) {
        if (location.createdAt._seconds) return location.createdAt._seconds * 1000;
        return new Date(location.createdAt).getTime();
    }
    return Date.now();
}

function mergeLocationsByIdentity(existingLocations, incomingLocations) {
    const merged = [];
    const seen = new Set();

    [...existingLocations, ...incomingLocations].forEach((location) => {
        if (!location) return;

        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const stableKey = location.id || `${lat.toFixed(6)}|${lng.toFixed(6)}|${getTimestamp(location)}`;
        if (seen.has(stableKey)) return;

        seen.add(stableKey);
        merged.push(location);
    });

    merged.sort((a, b) => getTimestamp(a) - getTimestamp(b));
    return merged;
}

function updateDaysInMonth() {
    const yearEl = document.getElementById('pick-year');
    const monthEl = document.getElementById('pick-month');
    const dayEl = document.getElementById('pick-day');

    if (!yearEl || !monthEl || !dayEl) return;

    const year = parseInt(yearEl.value, 10);
    const month = parseInt(monthEl.value, 10);
    const currentDay = parseInt(dayEl.value, 10) || 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    dayEl.innerHTML = '';
    for (let d = 1; d <= daysInMonth; d++) {
        const option = document.createElement('option');
        option.value = d;
        option.textContent = d.toString().padStart(2, '0');
        if (d === Math.min(currentDay, daysInMonth)) option.selected = true;
        dayEl.appendChild(option);
    }
}

function displayLocationsOnMap() {
    if (state.mapLocations.length === 0) return;

    // Show latest location on Google Maps
    const latestLoc = state.mapLocations[state.mapLocations.length - 1];

    // Calculate zoom based on number of points and spread
    let zoom = 18; // Default high zoom for single point

    if (state.mapLocations.length > 1) {
        // Calculate the spread of locations
        const lats = state.mapLocations.map(l => l.latitude);
        const lngs = state.mapLocations.map(l => l.longitude);
        const latSpread = Math.max(...lats) - Math.min(...lats);
        const lngSpread = Math.max(...lngs) - Math.min(...lngs);
        const maxSpread = Math.max(latSpread, lngSpread);

        // Adjust zoom based on spread
        if (maxSpread > 1) zoom = 8;
        else if (maxSpread > 0.5) zoom = 10;
        else if (maxSpread > 0.1) zoom = 12;
        else if (maxSpread > 0.01) zoom = 15;
        else zoom = 17;
    }

    // Show Google Maps embed with the latest/current location
    showGoogleMapsEmbed(latestLoc.latitude, latestLoc.longitude, zoom);

    // Update stats
    updateMapStats();
    updateLocationList();
    updatePlaybackSlider();

    // Show info panel for current location
    showLocationInfo(state.mapLocations.length - 1);
}

function showLocationInfo(index) {
    const loc = state.mapLocations[index];
    if (!loc) return;

    const infoPanel = document.getElementById('map-info-panel');
    const infoDetails = document.getElementById('map-info-details');

    if (infoPanel && infoDetails) {
        const time = formatDate(loc.timestamp || loc.createdAt || loc.clientTimestamp);
        const isStart = index === 0;
        const isEnd = index === state.mapLocations.length - 1;

        let pointLabel = `Point ${index + 1}`;
        if (isStart) pointLabel = '🟢 Start Point';
        else if (isEnd) pointLabel = '🔴 End Point (Latest)';

        infoDetails.innerHTML = `
            <div class="location-info-grid">
                <div class="info-row">
                    <span class="info-label">Position:</span>
                    <span class="info-value">${pointLabel}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Latitude:</span>
                    <span class="info-value">${loc.latitude.toFixed(6)}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Longitude:</span>
                    <span class="info-value">${loc.longitude.toFixed(6)}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Time:</span>
                    <span class="info-value">${time}</span>
                </div>
                ${loc.accuracy ? `
                <div class="info-row">
                    <span class="info-label">Accuracy:</span>
                    <span class="info-value">${loc.accuracy}m</span>
                </div>` : ''}
                ${loc.speed ? `
                <div class="info-row">
                    <span class="info-label">Speed:</span>
                    <span class="info-value">${loc.speed} m/s</span>
                </div>` : ''}
                ${loc.provider ? `
                <div class="info-row">
                    <span class="info-label">Provider:</span>
                    <span class="info-value">${loc.provider}</span>
                </div>` : ''}
            </div>
            <div class="location-actions">
                <a href="https://www.google.com/maps?q=${loc.latitude},${loc.longitude}"
                   target="_blank"
                   class="btn btn-sm btn-primary">
                    🗺️ Open in Google Maps
                </a>
                <a href="https://www.google.com/maps/@${loc.latitude},${loc.longitude},3a,75y,0h,90t/data=!3m6!1e1!3m4!1s"
                   target="_blank"
                   class="btn btn-sm btn-secondary">
                    🚶 Street View
                </a>
            </div>
        `;
        infoPanel.style.display = 'block';
    }
}

// Removed Leaflet-specific functions (createCustomIcon, createCircleIcon, createLocationPopup, clearMapLayers)

function updateMapStats() {
    const totalPoints = state.mapLocations.length;
    document.getElementById('stat-total-points').textContent = totalPoints;

    // Calculate total distance
    let totalDistance = 0;
    for (let i = 1; i < state.mapLocations.length; i++) {
        const prev = state.mapLocations[i - 1];
        const curr = state.mapLocations[i];
        totalDistance += calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
    }
    document.getElementById('stat-distance').textContent =
        totalDistance > 1000 ? `${(totalDistance / 1000).toFixed(2)} km` : `${Math.round(totalDistance)} m`;

    // Calculate duration
    if (state.mapLocations.length >= 2) {
        const firstTime = getTimestamp(state.mapLocations[0]);
        const lastTime = getTimestamp(state.mapLocations[state.mapLocations.length - 1]);
        const durationMs = lastTime - firstTime;
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        document.getElementById('stat-duration').textContent = `${hours}h ${minutes}m`;
    } else {
        document.getElementById('stat-duration').textContent = '0h';
    }
}

function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
}

function updateLocationList() {
    const listContainer = document.getElementById('location-list');
    if (!listContainer) return;

    if (state.mapLocations.length === 0) {
        listContainer.innerHTML = '<div class="empty-state-mini">No locations loaded.</div>';
        return;
    }

    listContainer.innerHTML = state.mapLocations.slice(0, 50).map((loc, index) => `
        <div class="location-item"
             data-index="${index}"
             onclick="jumpToLocation(${index})">
            <div class="location-item-icon">${icons.location}</div>
            <div class="location-item-content">
                <div class="location-item-coords">
                    ${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}
                </div>
                <div class="location-item-time">
                    ${formatDate(loc.timestamp || loc.createdAt || loc.clientTimestamp)}
                </div>
            </div>
        </div>
    `).join('');
}

// Global window functions for onclick handlers
window.jumpToLocation = function(index) {
    if (state.mapLocations[index]) {
        const loc = state.mapLocations[index];
        showGoogleMapsEmbed(loc.latitude, loc.longitude, 18);
        showLocationInfo(index);

        // Highlight selected item
        document.querySelectorAll('.location-item').forEach((item, i) => {
            item.classList.toggle('active', i === index);
        });
    }
};

window.hideMapInfo = function() {
    const infoPanel = document.getElementById('map-info-panel');
    if (infoPanel) infoPanel.style.display = 'none';
};

function attachMapEventListeners() {
    // Load user button
    document.getElementById('load-map-user-btn')?.addEventListener('click', loadUserLocations);

    // Enter key on user ID input
    document.getElementById('map-user-id')?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') loadUserLocations();
    });

    // Dropdown header click - opens and loads locations for selected date
    document.getElementById('dropdown-header')?.addEventListener('click', () => {
        toggleLocationDropdown();
    });

    // When year/month/day changes, reset dropdown so it reloads on next click
    document.getElementById('pick-year')?.addEventListener('change', () => {
        updateDaysInMonth();
        resetLocationDropdown();
        updateDropdownHeaderForSelectedDate();
    });

    document.getElementById('pick-month')?.addEventListener('change', () => {
        updateDaysInMonth();
        resetLocationDropdown();
        updateDropdownHeaderForSelectedDate();
    });

    document.getElementById('pick-day')?.addEventListener('change', () => {
        resetLocationDropdown();
        updateDropdownHeaderForSelectedDate();
    });

    // Load more button
    document.getElementById('load-more-locations-btn')?.addEventListener('click', () => {
        displayMoreLocations();
    });

    // Refresh button
    document.getElementById('refresh-map-btn')?.addEventListener('click', () => {
        if (state.selectedMapUser) {
            resetLocationDropdown();
            loadLocationsForDate();
        } else {
            toast.info('Enter a User ID first');
        }
    });

    // Clear button
    document.getElementById('clear-map-btn')?.addEventListener('click', () => {
        clearMap();
        // Reset dropdown
        locationDropdownState.locations = [];
        locationDropdownState.displayedCount = 0;
        const dropdownList = document.getElementById('dropdown-list');
        const dropdownHeader = document.getElementById('dropdown-header');
        const countBadge = document.getElementById('location-count-badge');
        if (dropdownList) dropdownList.innerHTML = '';
        if (dropdownHeader) dropdownHeader.querySelector('.dropdown-text').textContent = 'Select a date and load locations';
        if (countBadge) countBadge.style.display = 'none';
        toast.info('Map cleared');
    });

    // Fit bounds button - load selected date and render grouped markers in Leaflet
    document.getElementById('fit-bounds-btn')?.addEventListener('click', async () => {
        await loadFitAllLocations({ append: false });
    });

    // Load additional fit-all locations when backend returns partial data
    document.getElementById('fitall-load-more-btn')?.addEventListener('click', async () => {
        if (!fitAllMapState.hasMore) {
            toast.info('All fit all locations are already loaded.');
            return;
        }
        await loadFitAllLocations({ append: true });
    });

    // Quick date buttons (now using days instead of hours)
    document.querySelectorAll('.quick-date-buttons button').forEach(btn => {
        btn.addEventListener('click', () => {
            const daysAgo = parseInt(btn.dataset.days);
            const targetDate = new Date();
            targetDate.setDate(targetDate.getDate() - daysAgo);

            state.mapDateRange.start = targetDate;

            // Update the single date picker
            updateDatePicker(targetDate);

            // Update active state
            document.querySelectorAll('.quick-date-buttons button').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // Keep map loading manual: quick-select updates date only.
            resetLocationDropdown();
            updateDropdownHeaderForSelectedDate();
        });
    });

}

// ============================================================================
// MODALS
// ============================================================================
function renderModals() {
    return `
        <div class="modal-overlay hidden" id="confirm-modal">
            <div class="modal">
                <div class="modal-header">
                    <h3 class="modal-title" id="confirm-modal-title">Confirm Action</h3>
                    <button class="modal-close" onclick="closeModal('confirm-modal')">&times;</button>
                </div>
                <div class="modal-body" id="confirm-modal-body">
                    Are you sure you want to proceed?
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" onclick="closeModal('confirm-modal')">Cancel</button>
                    <button class="btn btn-primary" id="confirm-modal-btn">Confirm</button>
                </div>
            </div>
        </div>

        <div class="modal-overlay hidden" id="duration-modal">
            <div class="modal">
                <div class="modal-header">
                    <h3 class="modal-title">Set Duration</h3>
                    <button class="modal-close" onclick="closeModal('duration-modal')">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="form-group">
                        <label class="form-label">Duration (seconds)</label>
                        <input type="number" id="duration-input" class="form-input" min="1" value="3600">
                        <p class="form-hint">Leave empty to use default duration</p>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Quick Select</label>
                        <select class="form-select" id="duration-quick-select">
                            <option value="">Custom</option>
                            <option value="300">5 minutes</option>
                            <option value="900">15 minutes</option>
                            <option value="1800">30 minutes</option>
                            <option value="3600">1 hour</option>
                            <option value="7200">2 hours</option>
                            <option value="14400">4 hours</option>
                            <option value="28800">8 hours</option>
                            <option value="86400">24 hours</option>
                        </select>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" onclick="closeModal('duration-modal')">Cancel</button>
                    <button class="btn btn-success" id="duration-modal-btn">Enable Mode</button>
                </div>
            </div>
        </div>
    `;
}

// ============================================================================
// EVENT LISTENERS
// ============================================================================
function attachEventListeners() {
    // Navigation
    document.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const newPage = e.currentTarget.dataset.page;


            // Stop FCM realtime listeners when navigating away from FCM page
            if (state.currentPage === 'fcm' && newPage !== 'fcm') {
                stopFCMRealtimeListeners();
            }

            // Stop geofence expiry countdown when navigating away from geofence page
            if (state.currentPage === 'geofences' && newPage !== 'geofences') {
                stopGeofenceExpiryCountdown();
            }

            // Start geofence expiry countdown when navigating to geofence page
            if (state.currentPage !== 'geofences' && newPage === 'geofences') {
                startGeofenceExpiryCountdown();
            }

            state.currentPage = newPage;
            // Update URL hash for routing
            window.location.hash = newPage;
            // Save state to localStorage
            saveState();
            renderApp();
        });
    });

    // Logout
    document.getElementById('logout-btn')?.addEventListener('click', async () => {
        await logout();
        state.user = null;
        state.loadedUsers.clear();
        // Clear persisted state on logout
        localStorage.removeItem(STORAGE_KEY);
        renderLoginPage();
    });

    // Clear Session (keep logged in but clear form data)
    document.getElementById('clear-session-btn')?.addEventListener('click', () => {
        // Reset state but keep user logged in
        state.usersPageInput = '';
        state.selectedMapUser = null;
        state.emailPage = {
            userId: '',
            userInfo: null,
            selectedTemplate: '',
            senderEmail: '',
            subject: '',
            bodyText: '',
            isEditing: false
        };
        state.mapDateRange = {
            start: new Date(Date.now() - 24 * 60 * 60 * 1000),
            end: new Date()
        };
        state.loadedUsers.clear();
        state.mapLocations = [];

        // Save cleared state
        saveState();

        // Re-render current page
        renderApp();
        toast.success('Session data cleared');
    });

    // Refresh
    document.getElementById('refresh-btn')?.addEventListener('click', () => {
        if (state.currentPage === 'users') {
            refreshAllUsers();
        } else if (state.currentPage === 'map' && state.selectedMapUser) {
            loadUserLocations();
        } else if (state.currentPage === 'email') {
            // Re-render email page
            document.getElementById('page-content').innerHTML = renderEmailPage();
            attachEmailPageListeners();
        } else {
            renderApp();
        }
    });

    // Load users
    document.getElementById('load-users-btn')?.addEventListener('click', loadUsers);
    document.getElementById('user-ids-input')?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') loadUsers();
    });
    // Save user IDs input on change
    document.getElementById('user-ids-input')?.addEventListener('input', (e) => {
        state.usersPageInput = e.target.value;
        saveState();
    });

    // Save map user ID on change
    document.getElementById('map-user-id')?.addEventListener('input', (e) => {
        state.selectedMapUser = e.target.value;
        saveState();
    });

    // Broadcast
    document.getElementById('broadcast-btn')?.addEventListener('click', handleBroadcast);

    // Duration quick select
    document.getElementById('duration-quick-select')?.addEventListener('change', (e) => {
        if (e.target.value) {
            document.getElementById('duration-input').value = e.target.value;
        }
    });

    // Initialize map if on map page
    if (state.currentPage === 'map') {
        setTimeout(() => {
            initializeMap();
            attachMapEventListeners();
        }, 50);
    }

    // Initialize email page listeners if on email page
    if (state.currentPage === 'email') {
        setTimeout(() => {
            attachEmailPageListeners();
        }, 50);
    }

    // Initialize FCM page listeners if on FCM page
    if (state.currentPage === 'fcm') {
        setTimeout(() => {
            attachFCMEventListeners();
        }, 50);
    }
}

// ============================================================================
// MOBILE MENU HANDLERS
// ============================================================================
function attachMobileMenuListeners() {
    const menuToggle = document.getElementById('mobile-menu-toggle');
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.getElementById('sidebar-overlay');

    if (menuToggle && sidebar && overlay) {
        // Toggle menu on button click
        menuToggle.addEventListener('click', () => {
            const isOpen = sidebar.classList.toggle('open');
            overlay.classList.toggle('active');
            state.mobileMenuOpen = isOpen;

            // Change icon when open
            if (isOpen) {
                menuToggle.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                `;
            } else {
                menuToggle.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                `;
            }
        });

        // Close menu when clicking overlay
        overlay.addEventListener('click', () => {
            closeMobileMenu();
        });

        // Close menu when clicking nav items on mobile
        document.querySelectorAll('.nav-item').forEach(item => {
            item.addEventListener('click', () => {
                if (window.innerWidth <= 992) {
                    closeMobileMenu();
                }
            });
        });
    }
}

function closeMobileMenu() {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.getElementById('sidebar-overlay');
    const menuToggle = document.getElementById('mobile-menu-toggle');

    state.mobileMenuOpen = false;
    if (sidebar) sidebar.classList.remove('open');
    if (overlay) overlay.classList.remove('active');
    if (menuToggle) {
        menuToggle.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
        `;
    }
}

// ============================================================================
// API HANDLERS
// ============================================================================
async function loadUsers() {
    const input = document.getElementById('user-ids-input')?.value;
    if (!input) {
        toast.warning('Please enter at least one User ID');
        return;
    }

    const userIds = parseUserIds(input);
    if (userIds.length === 0) {
        toast.warning('No valid User IDs found');
        return;
    }

    const loadBtn = document.getElementById('load-users-btn');
    loadBtn.disabled = true;
    loadBtn.innerHTML = `${icons.refresh} Loading...`;

    for (const userId of userIds) {
        if (!isValidUserId(userId)) {
            state.loadedUsers.set(userId, { error: 'Invalid User ID format' });
            continue;
        }

        try {
            const response = await api.adminGetUserSettings(userId);
            // Backend returns { success: true, data: { ...settings } }
            state.loadedUsers.set(userId, { settings: response.data || response.settings });
        } catch (error) {
            state.loadedUsers.set(userId, { error: error.message });
        }
    }

    loadBtn.disabled = false;
    loadBtn.innerHTML = `${icons.search} Load Settings`;

    document.getElementById('users-container').innerHTML = renderLoadedUsers();
    toast.success(`Loaded ${userIds.length} user(s)`);
}

async function refreshUser(userId) {
    try {
        const response = await api.adminGetUserSettings(userId);
        // Backend returns { success: true, data: { ...settings } }
        state.loadedUsers.set(userId, { settings: response.data || response.settings });
        document.getElementById('users-container').innerHTML = renderLoadedUsers();
        toast.success('Settings refreshed');
    } catch (error) {
        toast.error(`Failed to refresh: ${error.message}`);
    }
}

/**
 * Force sync user settings - triggers immediate sync to app
 * This saves all current settings and notifies the app to sync
 */
async function forceSyncUser(userId) {
    try {
        // First save current settings
        const panel = document.querySelector(`[data-user-id="${userId}"]`);
        if (panel) {
            const inputs = panel.querySelectorAll('.setting-input');
            const settings = {};
            inputs.forEach(input => {
                const key = input.dataset.settingKey;
                settings[key] = parseInt(input.value, 10);
            });

            // Update settings in backend (which writes to Firestore)
            await api.adminUpdateUserSettings(userId, settings);
        }

        // Force sync - triggers version bump and immediate notification
        await api.adminForceSyncUserSettings(userId);

        toast.success('Settings synced! App will update within seconds via realtime listener.');
        refreshUser(userId);
    } catch (error) {
        toast.error(`Failed to sync: ${error.message}`);
    }
}

window.forceSyncUser = forceSyncUser;

async function refreshAllUsers() {
    const userIds = Array.from(state.loadedUsers.keys());
    for (const userId of userIds) {
        try {
            const response = await api.adminGetUserSettings(userId);
            state.loadedUsers.set(userId, { settings: response.data || response.settings });
        } catch (error) {
            state.loadedUsers.set(userId, { error: error.message });
        }
    }
    document.getElementById('users-container').innerHTML = renderLoadedUsers();
    toast.success('All users refreshed');
}

// Global functions for onclick handlers
window.removeUser = function(userId) {
    state.loadedUsers.delete(userId);
    document.getElementById('users-container').innerHTML =
        state.loadedUsers.size > 0 ? renderLoadedUsers() : renderEmptyState();
};

window.saveUserSettings = async function(userId) {
    const panel = document.querySelector(`[data-user-id="${userId}"]`);
    const inputs = panel.querySelectorAll('.setting-input');

    const settings = {};
    inputs.forEach(input => {
        const key = input.dataset.settingKey;
        settings[key] = parseInt(input.value, 10);
    });

    try {
        await api.adminUpdateUserSettings(userId, settings);
        toast.success('Settings saved successfully');
        refreshUser(userId);
    } catch (error) {
        toast.error(`Failed to save: ${error.message}`);
    }
};

window.refreshUser = refreshUser;

window.resetUserSettings = async function(userId) {
    if (!confirm('Are you sure you want to reset this user\'s settings to defaults?')) return;

    try {
        // Get defaults and apply to user
        const defaultSettings = state.defaults;
        await api.adminUpdateUserSettings(userId, defaultSettings);
        toast.success('Settings reset to defaults');
        refreshUser(userId);
    } catch (error) {
        toast.error(`Failed to reset: ${error.message}`);
    }
};

window.enableMode = async function(userId, mode) {
    // Show duration modal
    const modal = document.getElementById('duration-modal');
    modal.classList.remove('hidden');

    const btn = document.getElementById('duration-modal-btn');
    btn.onclick = async () => {
        const duration = document.getElementById('duration-input').value;
        modal.classList.add('hidden');

        try {
            // Build settings update
            const modeSettings = {};
            if (mode === 'force') {
                modeSettings.forceCheckEnabled = true;
                if (duration) modeSettings.forceCheckDuration = parseInt(duration, 10);
            } else if (mode === 'realtime') {
                modeSettings.realtimeModeEnabled = true;
                if (duration) modeSettings.realtimeModeDuration = parseInt(duration, 10);
            } else if (mode === 'emergency') {
                modeSettings.emergencyModeEnabled = true;
                if (duration) modeSettings.emergencyModeDuration = parseInt(duration, 10);
            }

            await api.adminUpdateUserSettings(userId, modeSettings);
            toast.success(`${mode} mode enabled`);
            refreshUser(userId);
        } catch (error) {
            toast.error(`Failed to enable mode: ${error.message}`);
        }
    };
};

window.disableMode = async function(userId, mode) {
    try {
        const modeSettings = {};
        if (mode === 'force') modeSettings.forceCheckEnabled = false;
        else if (mode === 'realtime') modeSettings.realtimeModeEnabled = false;
        else if (mode === 'emergency') modeSettings.emergencyModeEnabled = false;

        await api.adminUpdateUserSettings(userId, modeSettings);
        toast.success(`${mode} mode disabled`);
        refreshUser(userId);
    } catch (error) {
        toast.error(`Failed to disable mode: ${error.message}`);
    }
};

// Toggle mode function for the new toggle switches
window.toggleMode = async function(userId, mode, isEnabled, defaultInterval) {
    if (!isEnabled) {
        // Turning OFF - just disable
        try {
            const modeSettings = {};
            if (mode === 'force') modeSettings.forceCheckEnabled = false;
            else if (mode === 'realtime') modeSettings.realtimeModeEnabled = false;
            else if (mode === 'emergency') modeSettings.emergencyModeEnabled = false;

            await api.adminUpdateUserSettings(userId, modeSettings);
            toast.success(`${getModeDisplayName(mode)} disabled`);
            refreshUser(userId);
        } catch (error) {
            toast.error(`Failed to disable: ${error.message}`);
            refreshUser(userId); // Refresh to reset toggle state
        }
    } else {
        // Turning ON - show interval selection popup
        showIntervalPopup(userId, mode, defaultInterval);
    }
};

function getModeDisplayName(mode) {
    const names = {
        'force': 'Force Check',
        'realtime': 'Realtime',
        'emergency': 'Emergency'
    };
    return names[mode] || mode;
}

function showIntervalPopup(userId, mode, defaultInterval) {
    // Create popup HTML
    const popupHTML = `
        <div id="interval-popup" class="modal-overlay">
            <div class="modal-content interval-popup-content">
                <h3 class="modal-title">Set ${getModeDisplayName(mode)} Interval</h3>
                <p class="modal-subtitle">Choose how often to track location when this mode is active</p>

                <div class="interval-options">
                    ${mode === 'force' ? `
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 60)">1 minute</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 120)">2 minutes</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 300)">5 minutes</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 600)">10 minutes</button>
                    ` : mode === 'realtime' ? `
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 5)">5 seconds</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 10)">10 seconds</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 15)">15 seconds</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 30)">30 seconds</button>
                    ` : `
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 10)">10 seconds</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 30)">30 seconds</button>
                        <button class="interval-option" onclick="selectInterval('${userId}', '${mode}', 60)">1 minute</button>
                    `}
                </div>

                <div class="custom-interval-row">
                    <label>Custom:</label>
                    <input type="number" id="custom-interval-input" class="form-input" value="${defaultInterval}" min="1" placeholder="seconds">
                    <span>seconds</span>
                </div>

                <div class="modal-actions">
                    <button class="btn btn-secondary" onclick="cancelIntervalPopup('${userId}')">Cancel</button>
                    <button class="btn btn-success" onclick="confirmInterval('${userId}', '${mode}')">Enable ${getModeDisplayName(mode)}</button>
                </div>
            </div>
        </div>
    `;

    // Remove existing popup if any
    const existingPopup = document.getElementById('interval-popup');
    if (existingPopup) existingPopup.remove();

    // Add popup to body
    document.body.insertAdjacentHTML('beforeend', popupHTML);
}

window.selectInterval = function(userId, mode, seconds) {
    // Update custom input
    document.getElementById('custom-interval-input').value = seconds;

    // Update selected state
    document.querySelectorAll('.interval-option').forEach(btn => btn.classList.remove('selected'));
    event.target.classList.add('selected');
};

window.cancelIntervalPopup = function(userId) {
    const popup = document.getElementById('interval-popup');
    if (popup) popup.remove();

    // Refresh to reset toggle state
    refreshUser(userId);
};

window.confirmInterval = async function(userId, mode) {
    const interval = parseInt(document.getElementById('custom-interval-input').value, 10);

    if (isNaN(interval) || interval < 1) {
        toast.error('Please enter a valid interval');
        return;
    }

    // Close popup
    const popup = document.getElementById('interval-popup');
    if (popup) popup.remove();

    try {
        const modeSettings = {};
        if (mode === 'force') {
            modeSettings.forceCheckEnabled = true;
            modeSettings.forceCheckInterval = interval;
        } else if (mode === 'realtime') {
            modeSettings.realtimeModeEnabled = true;
            modeSettings.realtimeInterval = interval;
        } else if (mode === 'emergency') {
            modeSettings.emergencyModeEnabled = true;
            modeSettings.emergencyInterval = interval;
        }

        await api.adminUpdateUserSettings(userId, modeSettings);
        toast.success(`${getModeDisplayName(mode)} enabled with ${formatDuration(interval)} interval`);
        refreshUser(userId);
    } catch (error) {
        toast.error(`Failed to enable: ${error.message}`);
        refreshUser(userId);
    }
};

window.closeModal = function(modalId) {
    document.getElementById(modalId).classList.add('hidden');
};

async function handleBroadcast() {
    const reason = document.getElementById('broadcast-reason')?.value || 'Admin broadcast';

    const settings = {};
    const settingKeys = ['updateInterval', 'forceCheckInterval', 'realtimeInterval', 'emergencyInterval', 'batteryThreshold', 'batchUploadSize'];

    let hasSettings = false;
    for (const key of settingKeys) {
        const enabled = document.getElementById(`broadcast-${key}-enabled`)?.checked;
        if (enabled) {
            const value = parseInt(document.getElementById(`broadcast-${key}`)?.value, 10);
            if (!isNaN(value)) {
                settings[key] = value;
                hasSettings = true;
            }
        }
    }

    if (!hasSettings) {
        toast.warning('Please enable at least one setting to broadcast');
        return;
    }

    if (!confirm(`Are you sure you want to broadcast these settings to ALL users?\n\nReason: ${reason}`)) {
        return;
    }

    try {
        const result = await api.adminBroadcastSettings(settings, reason);
        toast.success(`Broadcast complete! Updated ${result.updatedCount || 'all'} users.`);
    } catch (error) {
        toast.error(`Broadcast failed: ${error.message}`);
    }
}

// ============================================================================
// FCM / NOTIFICATIONS PAGE - HELPER FUNCTIONS
// ============================================================================

/**
 * Convert failure reason code to human readable text
 */
function getReadableFailureReason(reason) {
    const reasonMap = {
        'NOT_LOGGED_IN': 'User not logged in to the app',
        'NO_FINE_LOCATION_PERMISSION': 'App location permission not granted (needs "Allow" in app settings)',
        'NO_LOCATION_PERMISSION': 'App location permission not granted',
        'NO_BACKGROUND_LOCATION_PERMISSION': 'Background location permission not granted (needs "Allow all the time")',
        'NO_BACKGROUND_PERMISSION': 'Background location permission not granted',
        'LOCATION_SERVICES_DISABLED': 'Device GPS/Location services are turned OFF',
        'LOCATION_DISABLED': 'Device location is disabled',
        'SERVICE_START_FAILED': 'Failed to start the location tracking service',
        'TRACKING_DISABLED': 'User has disabled tracking in app settings',
        'SERVICE_NOT_RUNNING': 'Location tracking service is not running',
        'NO_NETWORK': 'Device has no network connection',
        'EXCEPTION': 'An error occurred while processing',
        'confirmed_after_6h_inactivity': 'Confirmed after 6 hours of inactivity',
        'user_came_online': 'User came back online',
        'device_back_online': 'Device came back online'
    };
    return reasonMap[reason] || reason;
}

/**
 * Render detailed permission breakdown section
 */
/**
 * Lightweight DOM update for backend monitor countdowns (called every 1 second).
 * Updates only the countdown elements without re-rendering the entire page.
 */
function updateBackendMonitorCountdowns() {
    const presenceData = state.fcmPage.presence || {};
    const recoveryData = state.fcmPage.recoveryStatus || {};
    const isDeviceOnline = presenceData.online === true;
    const lastSeenTimestamp = presenceData.lastSeen;
    const isRecovering = recoveryData.active === true;
    const GRACE_PERIOD_MS = 7 * 60 * 1000;
    const now = Date.now();

    // Update grace period countdown
    const graceCountdownEl = document.querySelector('.grace-countdown');
    const graceBarFillEl = document.querySelector('.grace-bar-fill');
    if (graceCountdownEl && !isDeviceOnline && lastSeenTimestamp) {
        const offlineSinceMs = now - lastSeenTimestamp;
        const remainingMs = Math.max(0, GRACE_PERIOD_MS - offlineSinceMs);
        const percent = Math.min(100, Math.round((offlineSinceMs / GRACE_PERIOD_MS) * 100));
        const remainMin = Math.floor(remainingMs / 60000);
        const remainSec = Math.ceil((remainingMs % 60000) / 1000);
        graceCountdownEl.textContent = `${remainMin}m ${remainSec}s remaining`;
        graceCountdownEl.style.color = percent > 80 ? '#ef4444' : '#f59e0b';
        if (graceBarFillEl) {
            graceBarFillEl.style.width = `${percent}%`;
            graceBarFillEl.style.background = percent > 80 ? '#ef4444' : percent > 50 ? '#f59e0b' : '#3b82f6';
        }
        // If grace period expired, trigger a full re-render to transition to recovery phase
        if (remainingMs <= 0) {
            renderApp();
        }
    }

    // Update next FCM countdown
    const nextFireAt = recoveryData.nextFireAt;
    if (isRecovering && nextFireAt) {
        // Find all elements that might display the next FCM countdown
        const nextFcmEls = document.querySelectorAll('.bm-value');
        nextFcmEls.forEach(el => {
            if (el.previousElementSibling && el.previousElementSibling.textContent.includes('Next FCM')) {
                const diffMs = nextFireAt - now;
                if (diffMs <= 0) {
                    el.textContent = 'Sending now...';
                } else {
                    const diffSec = Math.ceil(diffMs / 1000);
                    if (diffSec < 60) el.textContent = `${diffSec}s`;
                    else if (diffSec < 3600) el.textContent = `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
                    else el.textContent = `${Math.floor(diffSec / 3600)}h ${Math.floor((diffSec % 3600) / 60)}m`;
                }
            }
        });
    }
}

/**
 * Render the Backend Monitor section — a real-time, step-by-step view of
 * what the backend presence monitor and recovery scheduler are doing.
 */
function renderBackendMonitor(isDeviceOnline, lastSeenTimestamp, recoveryData, isRecovering) {
    const now = Date.now();
    const GRACE_PERIOD_MS = 7 * 60 * 1000; // Must match backend

    // ── Determine the backend's current phase ──
    let phase = 'unknown';
    let phaseIcon = '❓';
    let phaseColor = '#94a3b8';
    let phaseLabel = 'Unknown';
    let phaseDetail = '';

    // Grace period calculation
    let graceElapsedMs = 0;
    let graceRemainingMs = 0;
    let gracePercent = 0;
    const offlineSinceMs = lastSeenTimestamp ? (now - lastSeenTimestamp) : 0;

    if (isDeviceOnline) {
        phase = 'online';
        phaseIcon = '🟢';
        phaseColor = '#22c55e';
        phaseLabel = 'Device Online';
        phaseDetail = 'Firebase presence connected. Backend is idle — no action needed.';
    } else if (!isDeviceOnline && !isRecovering && lastSeenTimestamp && offlineSinceMs < GRACE_PERIOD_MS) {
        phase = 'grace_period';
        phaseIcon = '⏳';
        phaseColor = '#f59e0b';
        phaseLabel = 'Grace Period (Waiting)';
        graceElapsedMs = offlineSinceMs;
        graceRemainingMs = GRACE_PERIOD_MS - graceElapsedMs;
        gracePercent = Math.min(100, Math.round((graceElapsedMs / GRACE_PERIOD_MS) * 100));
        const remainSec = Math.max(0, Math.ceil(graceRemainingMs / 1000));
        const remainMin = Math.floor(remainSec / 60);
        const remainSecRem = remainSec % 60;
        phaseDetail = `Device went offline. Backend is waiting ${remainMin}m ${remainSecRem}s before starting recovery. If device reconnects, timer resets.`;
    } else if (!isDeviceOnline && isRecovering) {
        phase = 'recovering';
        phaseIcon = '🚨';
        phaseColor = '#ef4444';
        phaseLabel = 'Recovery Active';
        phaseDetail = 'Backend is sending escalating FCM wake messages to bring the device back online.';
    } else if (!isDeviceOnline && !isRecovering && lastSeenTimestamp && offlineSinceMs >= GRACE_PERIOD_MS) {
        phase = 'offline_post_grace';
        phaseIcon = '🔴';
        phaseColor = '#dc2626';
        phaseLabel = 'Offline — Recovery Pending';
        phaseDetail = 'Grace period expired. Recovery should start momentarily.';
    } else if (!isDeviceOnline && !isRecovering && recoveryData.status === 'cancelled') {
        phase = 'cancelled';
        phaseIcon = '✅';
        phaseColor = '#22c55e';
        phaseLabel = 'Recovery Cancelled';
        phaseDetail = 'Device came back online. Recovery was cancelled.';
    } else if (!isDeviceOnline && !lastSeenTimestamp) {
        phase = 'never_seen';
        phaseIcon = '⚪';
        phaseColor = '#94a3b8';
        phaseLabel = 'Never Connected';
        phaseDetail = 'No presence data received yet. Device has not connected to Firebase Realtime Database.';
    } else {
        phase = 'offline';
        phaseIcon = '🔴';
        phaseColor = '#94a3b8';
        phaseLabel = 'Offline';
        phaseDetail = 'Device is offline.';
    }

    // ── Recovery step details ──
    const SCHEDULE_LABELS = [
        'Immediate', '+1 min', '+3 min', '+30 min', '+30 min', '+30 min', '+30 min', '+30 min',
        '+30 min', '+30 min', '+30 min', '+30 min', '+30 min', '+30 min', '+30 min'
    ];
    const MAX_STEPS = 15;
    const currentStep = recoveryData.currentStep || 0;
    const currentCycle = recoveryData.currentCycle || 0;
    const totalFcmsSent = recoveryData.totalFcmsSent || 0;
    const startedAt = recoveryData.startedAt;
    const lastFcmSentAt = recoveryData.lastFcmSentAt;
    const nextFireAt = recoveryData.nextFireAt;
    const triggeredBy = recoveryData.triggeredBy || 'unknown';

    // Time until next FCM
    let nextFcmIn = '';
    if (isRecovering && nextFireAt) {
        const diffMs = nextFireAt - now;
        if (diffMs <= 0) {
            nextFcmIn = 'Sending now...';
        } else {
            const diffSec = Math.ceil(diffMs / 1000);
            if (diffSec < 60) nextFcmIn = `${diffSec}s`;
            else if (diffSec < 3600) nextFcmIn = `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
            else nextFcmIn = `${Math.floor(diffSec / 3600)}h ${Math.floor((diffSec % 3600) / 60)}m`;
        }
    }

    // Recovery duration
    let recoveryDuration = '';
    if (isRecovering && startedAt) {
        const durMs = now - startedAt;
        const durMin = Math.floor(durMs / 60000);
        if (durMin < 60) recoveryDuration = `${durMin} min`;
        else recoveryDuration = `${Math.floor(durMin / 60)}h ${durMin % 60}m`;
    }

    // ── Build timeline steps for recovery (ONLY during active recovery) ──
    let recoveryTimeline = '';
    if (isRecovering) {
        const stepsCompleted = totalFcmsSent;
        recoveryTimeline = `
            <div class="backend-monitor-timeline">
                <div class="timeline-header">
                    <span class="timeline-title">📋 FCM Schedule — Cycle ${currentCycle + 1}</span>
                    <span class="timeline-progress">${stepsCompleted}/${MAX_STEPS} sent</span>
                </div>
                <div class="timeline-steps">
                    ${SCHEDULE_LABELS.map((label, i) => {
                        let stepClass = 'timeline-step-pending';
                        let stepIcon = '○';
                        if (i < stepsCompleted || (i === currentStep && stepsCompleted > 0 && i < stepsCompleted)) {
                            stepClass = 'timeline-step-done';
                            stepIcon = '●';
                        } else if (i === currentStep && isRecovering) {
                            // The currentStep stored in Firestore is the LAST EXECUTED step
                            // So the NEXT to execute is currentStep + 1 (if stepsCompleted > 0)
                            if (stepsCompleted > i) {
                                stepClass = 'timeline-step-done';
                                stepIcon = '●';
                            } else {
                                stepClass = 'timeline-step-active';
                                stepIcon = '◉';
                            }
                        }
                        return `<span class="timeline-step ${stepClass}" title="Step ${i + 1}: ${label}">${stepIcon}</span>`;
                    }).join('')}
                </div>
            </div>`;
    }

    // ── Build the section ──
    return `
        <div class="card mb-4 backend-monitor-card">
            <div class="card-header" style="border-left: 4px solid ${phaseColor};">
                <h3 class="card-title">🖥️ Backend Monitor</h3>
                <span class="backend-phase-badge" style="background: ${phaseColor}15; color: ${phaseColor}; border: 1px solid ${phaseColor}40;">
                    ${phaseIcon} ${phaseLabel}
                </span>
            </div>
            <div class="card-body">
                <!-- Phase Description -->
                <div class="backend-phase-description" style="border-left: 3px solid ${phaseColor}; padding-left: 12px; margin-bottom: 16px;">
                    <p style="margin: 0; color: #64748b; font-size: 0.9rem;">${phaseDetail}</p>
                </div>

                <!-- Status Grid -->
                <div class="backend-monitor-grid">
                    <!-- Presence Status -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">📡 Presence</div>
                        <div class="bm-value" style="color: ${isDeviceOnline ? '#22c55e' : '#ef4444'}">
                            ${isDeviceOnline ? '● Online' : '● Offline'}
                        </div>
                    </div>

                    <!-- Last Seen -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">🕐 Last Seen</div>
                        <div class="bm-value">
                            ${isDeviceOnline ? '<span style="color:#22c55e">Now</span>' : (lastSeenTimestamp ? formatRelativeTime(new Date(lastSeenTimestamp)) : 'Never')}
                        </div>
                    </div>

                    <!-- Recovery Status -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">🔄 Recovery</div>
                        <div class="bm-value" style="color: ${isRecovering ? '#ef4444' : isDeviceOnline ? '#94a3b8' : recoveryData.status === 'cancelled' ? '#22c55e' : '#94a3b8'}">
                            ${isRecovering ? '● Active' : isDeviceOnline ? '○ Inactive' : recoveryData.status === 'cancelled' ? '● Cancelled' : recoveryData.status === 'completed' ? '● Completed' : '○ Inactive'}
                        </div>
                    </div>

                    <!-- FCMs Sent (only show count when recovering or recently cancelled while offline) -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">📨 FCMs Sent</div>
                        <div class="bm-value">${!isDeviceOnline && totalFcmsSent > 0 ? totalFcmsSent : isRecovering && totalFcmsSent > 0 ? totalFcmsSent : '—'}</div>
                    </div>

                    ${isRecovering ? `
                    <!-- Next FCM Countdown -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">⏱️ Next FCM</div>
                        <div class="bm-value" style="color: #f59e0b; font-weight: 600;">${nextFcmIn || '—'}</div>
                    </div>

                    <!-- Recovery Duration -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">⏳ Duration</div>
                        <div class="bm-value">${recoveryDuration || '—'}</div>
                    </div>

                    <!-- Current Step -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">📊 Step</div>
                        <div class="bm-value">${currentStep + 1} / ${MAX_STEPS}</div>
                    </div>

                    <!-- Triggered By -->
                    <div class="backend-monitor-item">
                        <div class="bm-label">🔧 Trigger</div>
                        <div class="bm-value">${triggeredBy === 'presence_monitor' ? 'Auto (Presence)' : triggeredBy}</div>
                    </div>
                    ` : ''}
                </div>

                ${phase === 'grace_period' ? `
                <!-- Grace Period Progress Bar -->
                <div class="backend-grace-period">
                    <div class="grace-header">
                        <span>⏳ 7-Minute Grace Period</span>
                        <span class="grace-countdown" style="color: ${gracePercent > 80 ? '#ef4444' : '#f59e0b'}; font-weight: 600;">
                            ${Math.floor(graceRemainingMs / 60000)}m ${Math.ceil((graceRemainingMs % 60000) / 1000)}s remaining
                        </span>
                    </div>
                    <div class="grace-bar-track">
                        <div class="grace-bar-fill" style="width: ${gracePercent}%; background: ${gracePercent > 80 ? '#ef4444' : gracePercent > 50 ? '#f59e0b' : '#3b82f6'};"></div>
                    </div>
                    <div class="grace-labels">
                        <span>0m</span>
                        <span>3.5m</span>
                        <span>7m → Recovery</span>
                    </div>
                </div>
                ` : ''}

                ${recoveryTimeline}

                ${isRecovering && lastFcmSentAt ? `
                <div class="backend-last-action" style="margin-top: 12px; padding: 8px 12px; background: #fef2f2; border-radius: 6px; font-size: 0.85rem; color: #991b1b;">
                    📤 Last FCM sent ${formatRelativeTime(new Date(lastFcmSentAt))} • Next in <strong>${nextFcmIn}</strong>
                </div>
                ` : ''}

                ${!isDeviceOnline && recoveryData.status === 'cancelled' && recoveryData.cancelledAt ? `
                <div class="backend-last-action" style="margin-top: 12px; padding: 8px 12px; background: #f0fdf4; border-radius: 6px; font-size: 0.85rem; color: #166534;">
                    ✅ Recovery cancelled ${formatRelativeTime(new Date(recoveryData.cancelledAt))} — device came back online. Total FCMs sent: ${totalFcmsSent}
                </div>
                ` : ''}
            </div>
        </div>`;
}

function renderPermissionBreakdown(status) {
    const permState = status.lastPermissionState || status.lastFcmResponse?.permissionState || {};
    const deviceState = status.lastDeviceState || status.deviceInfo || {};

    // Get permission values - check ALL possible field names from different sources
    // The Android app uses various field names depending on the context
    // ALSO check status directly which comes from realtime Firebase listener
    const hasFine = status?.hasFineLocation ??
                    permState?.hasFineLocation ??
                    permState?.hasFineLocationPermission ??
                    deviceState?.hasFinePermission ??
                    deviceState?.hasLocationPermission ??
                    deviceState?.hasFineLocation ?? null;

    const hasBackground = status?.hasBackgroundLocation ??
                          permState?.hasBackgroundLocation ??
                          permState?.hasBackgroundLocationPermission ??
                          deviceState?.hasBackgroundPermission ??
                          deviceState?.hasBackgroundLocation ?? null;

    const locationServicesOn = status?.locationServicesEnabled ??
                               permState?.locationServicesEnabled ??
                               permState?.isLocationEnabled ??
                               deviceState?.locationEnabled ??
                               deviceState?.locationServicesEnabled ?? null;

    const accessibilityEnabled = status?.accessibilityEnabled ?? permState?.accessibilityEnabled ?? status?.lastPermissionState?.accessibilityEnabled ?? deviceState?.accessibilityEnabled ?? null;

    const canTrack = status?.canTrackLocation ?? permState?.canTrackLocation ?? (hasFine && hasBackground && locationServicesOn);
    const missingPerm = status?.missingPermission ?? permState?.missingPermission;

    // If all permissions are unknown, don't show the section
    if (hasFine === null && hasBackground === null && locationServicesOn === null && accessibilityEnabled === null) {
        return ''; // No permission data available
    }

    return `
        <div class="permission-breakdown mt-3">
            <h5 class="mb-2">🔒 Permission Details</h5>
            <div class="permission-grid">
                <div class="permission-item ${hasFine === true ? 'perm-granted' : hasFine === false ? 'perm-denied' : 'perm-unknown'}">
                    <span class="perm-icon">${hasFine === true ? '✅' : hasFine === false ? '❌' : '❓'}</span>
                    <span class="perm-name">App Location Permission</span>
                    <span class="perm-status">${hasFine === true ? 'Granted' : hasFine === false ? 'Denied' : 'Unknown'}</span>
                </div>
                <div class="permission-item ${hasBackground === true ? 'perm-granted' : hasBackground === false ? 'perm-denied' : 'perm-unknown'}">
                    <span class="perm-icon">${hasBackground === true ? '✅' : hasBackground === false ? '❌' : '❓'}</span>
                    <span class="perm-name">Background Location</span>
                    <span class="perm-status">${hasBackground === true ? 'Granted (All Time)' : hasBackground === false ? 'Denied' : 'Unknown'}</span>
                </div>
                <div class="permission-item ${locationServicesOn === true ? 'perm-granted' : locationServicesOn === false ? 'perm-denied' : 'perm-unknown'}">
                    <span class="perm-icon">${locationServicesOn === true ? '✅' : locationServicesOn === false ? '❌' : '❓'}</span>
                    <span class="perm-name">Device GPS/Location</span>
                    <span class="perm-status">${locationServicesOn === true ? 'Enabled' : locationServicesOn === false ? 'Disabled (Toggle OFF)' : 'Unknown'}</span>
                </div>
                <div class="permission-item ${accessibilityEnabled === true ? 'perm-granted' : accessibilityEnabled === false ? 'perm-denied' : 'perm-unknown'}">
                    <span class="perm-icon">${accessibilityEnabled === true ? '🟢' : accessibilityEnabled === false ? '❌' : '❓'}</span>
                    <span class="perm-name">Accessibility</span>
                    <span class="perm-status">${accessibilityEnabled === true ? 'Active' : accessibilityEnabled === false ? 'Disabled' : 'Unknown'}</span>
                </div>
            </div>
            ${missingPerm && missingPerm !== 'none' ? `<div class="alert alert-danger mt-2"><strong>⚠️ Missing:</strong> ${getReadableFailureReason('NO_' + missingPerm)}</div>` : ''}
            ${canTrack === true ? `<div class="alert alert-success mt-2">✅ <strong>Can Track:</strong> All permissions granted and location enabled</div>` : ''}
            ${canTrack === false ? `<div class="alert alert-warning mt-2">⚠️ <strong>Cannot Track:</strong> Missing required permission or setting</div>` : ''}
        </div>
    `;
}

function renderProtectionSignals(status) {
    const uninstallState = status.uninstallState || {};
    const deviceAdminState = status.deviceAdminState || {};
    const isUninstalled = uninstallState.active === true || status.isUninstalled === true;
    const isDeviceAdminEnabled = deviceAdminState.active === true || status.isDeviceAdminEnabled === true;
    const accessibilityActive = status.accessibilityEnabled === true || (status.lastPermissionState && status.lastPermissionState.accessibilityEnabled) === true || false;

    if (!isUninstalled && !isDeviceAdminEnabled && !accessibilityActive) {
        return '';
    }

    const rows = [];

    if (isDeviceAdminEnabled) {
        rows.push({
            icon: '🛡️',
            label: 'Device Admin',
            value: 'Enabled',
            meta: deviceAdminState.enabledAt ? `Enabled ${formatRelativeTime(deviceAdminState.enabledAt)}` : 'Enabled'
        });
    }

    // Accessibility active indicator
    if (accessibilityActive) {
        rows.push({
            icon: '🟢',
            label: 'Accessibility',
            value: 'Active',
            meta: status.accessibilityGrantedAt ? `Granted ${formatRelativeTime(status.accessibilityGrantedAt)}` : 'Active'
        });
    }

    if (isUninstalled) {
        rows.push({
            icon: '📴',
            label: 'Uninstall Detection',
            value: 'Uninstall detected',
            meta: uninstallState.confirmedAt ? `Detected ${formatRelativeTime(uninstallState.confirmedAt)}` : 'Detected'
        });
    }

    return `
        <div class="card mt-3">
            <div class="card-header">
                <h3 class="card-title">${icons.shield} Device Protection Signals</h3>
            </div>
            <div class="card-body">
                <div class="permission-grid">
                    ${rows.map((row) => `
                        <div class="permission-item perm-granted">
                            <span class="perm-icon">${row.icon}</span>
                            <span class="perm-name">${escapeHtml(row.label)}</span>
                            <span class="perm-status">
                                ${escapeHtml(row.value)}<br>
                                <small class="text-muted">${escapeHtml(row.meta)}</small>
                            </span>
                        </div>
                    `).join('')}
                </div>
            </div>
        </div>
    `;
}

/**
 * Open location in Google Maps
 */
window.openLocationInMaps = function(lat, lng) {
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, '_blank');
};

/**
 * Convert failure reason code to human-readable text
 */
function getFailureReasonText(reason) {
    const reasonMap = {
        'NO_FINE_LOCATION_PERMISSION': 'Fine location permission not granted',
        'NO_BACKGROUND_LOCATION_PERMISSION': 'Background location permission not granted',
        'LOCATION_SERVICES_DISABLED': 'Location services disabled on device',
        'NO_NETWORK': 'No network connection',
        'SERVICE_NOT_RUNNING': 'Location service not running',
        'GPS_DISABLED': 'GPS is disabled',
        'BATTERY_OPTIMIZATION': 'Battery optimization is blocking service',
        'confirmed_after_6h_inactivity': 'Confirmed after 6 hours of inactivity',
        'user_came_online': 'User came back online',
        'device_back_online': 'Device came back online',
        'UNKNOWN': 'Unknown issue'
    };
    return reasonMap[reason] || reason?.replace(/_/g, ' ').toLowerCase() || 'Unknown reason';
}

// ============================================================================
// FCM / NOTIFICATIONS PAGE
// ============================================================================
function renderFCMPage() {
    const { userId, deviceStatus, wakeHistory, responseLogs, isLoading, instantWakeEnabled, lastRefreshTime, fcmStatusUnsubscribe } = state.fcmPage;

    // Load saved userId from localStorage if state is empty
    const savedUserId = localStorage.getItem('fcm_page_user_id') || '';
    const displayUserId = userId || savedUserId;

    // Check if realtime listeners are active
    const isRealtimeActive = fcmStatusUnsubscribe !== null;
    const lastRefreshDisplay = lastRefreshTime ? formatRelativeTime(lastRefreshTime) : 'Never';

    return `
        <div class="fcm-page">
            <!-- User ID Input Section -->
            <div class="card mb-4">
                <div class="card-header">
                    <h3 class="card-title">${icons.search} Load User Device</h3>
                    ${isRealtimeActive ? `
                        <div class="realtime-indicator">
                            <span class="realtime-dot"></span>
                            <span class="realtime-text">Live Updates</span>
                            <span class="text-muted" style="margin-left: 8px; font-size: 11px;">Last: ${lastRefreshDisplay}</span>
                        </div>
                    ` : ''}
                </div>
                <div class="card-body">
                    <div class="form-row">
                        <div class="form-group" style="flex: 1;">
                            <label class="form-label">User ID</label>
                            <input type="text"
                                   id="fcm-user-id"
                                   class="form-input"
                                   placeholder="Enter User ID (e.g., JD8nWy6yYtfchsy9V4hfnaJTLIp2)"
                                   value="${escapeHtml(displayUserId)}">
                        </div>
                        <div class="form-group" style="align-self: flex-end;">
                            <button class="btn btn-primary" id="fcm-load-user-btn" ${isLoading ? 'disabled' : ''}>
                                ${isLoading ? '<span class="spinner"></span>' : icons.search}
                                Load User
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            ${deviceStatus ? renderDeviceStatusSection(deviceStatus) : renderNoUserSelected()}
        </div>
    `;
}

function renderNoUserSelected() {
    return `
        <div class="empty-state">
            <div class="empty-state-icon">${icons.phone}</div>
            <h3>No User Selected</h3>
            <p>Enter a User ID above and click "Load User" to view device status and FCM controls.</p>
        </div>
    `;
}

function renderDeviceStatusSection(status) {
    const { instantWakeEnabled, responseLogs, wakeHistory } = state.fcmPage;

    // Determine device state - use both backend calculation AND device-reported status
    const dataIsActive = status.isActive; // Based on location upload time
    const isUnreachable = status.isUnreachable;
    const uninstallState = status.uninstallState || {};
    const isUninstalled = uninstallState.active === true || status.isUninstalled === true;
    const lastDataTime = status.lastLocationUpload ? new Date(status.lastLocationUpload) : null;
    const silenceHours = status.silenceDurationHours ? parseFloat(status.silenceDurationHours) : null;

    // Get device-reported status (more accurate than just checking location upload time)
    const lastDeviceStatus = status.lastDeviceStatus || {};
    const lastServiceState = status.lastServiceState || {};
    const lastPermissionState = status.lastPermissionState || {};

    // Check what the device actually reported
    const deviceReportedStatus = lastDeviceStatus.statusType;
    const deviceIsTracking = lastServiceState.isTracking === true;
    const serviceIsRunning = lastServiceState.serviceRunning === true;
    const trackingPaused = lastServiceState.trackingPaused === true ||
                           deviceReportedStatus === 'TRACKING_PAUSED' ||
                           deviceReportedStatus === 'SERVICE_STOPPED';

    // Get specific failure reason from device
    const deviceFailureReason = lastDeviceStatus.failureReason ||
                                lastServiceState.failureReason ||
                                status.lastFcmResponse?.failureReason;

    // Permission states from device
    const locationEnabled = lastPermissionState.isLocationEnabled ??
                           status.lastDeviceState?.locationEnabled;
    const hasBackgroundPermission = lastPermissionState.hasBackgroundLocation ??
                                    status.lastDeviceState?.hasBackgroundPermission;
    const hasFinePermission = lastPermissionState.hasFineLocation ??
                              status.lastDeviceState?.hasFinePermission;

    // Network state from device + realtime connection check
    const networkConnectedFromDevice = status.lastDeviceState?.networkAvailable ??
                             lastDeviceStatus.networkConnected ??
                             status.networkConnected;

    // ═══════════════════════════════════════════════════════════════════════════
    // PRESENCE-BASED ONLINE/OFFLINE DETECTION
    // ═══════════════════════════════════════════════════════════════════════════
    // The admin page reads presence/{userId} from Firebase Realtime Database.
    // This is the AUTHORITATIVE online/offline signal — set by the Android app
    // and updated server-side by Firebase onDisconnect(). No staleness logic needed.
    // ═══════════════════════════════════════════════════════════════════════════
    const presenceData = state.fcmPage.presence || {};
    const isDeviceOnline = presenceData.online === true;
    const lastSeenTimestamp = presenceData.lastSeen;

    const parseTimestampMs = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value.getTime();
        if (typeof value === 'number') return value > 1e12 ? value : value * 1000;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? null : parsed;
    };

    const telemetryTimes = [
        parseTimestampMs(status.updatedAt),
        parseTimestampMs(status.lastResponseTime),
        parseTimestampMs(status.lastLocationUpload),
        parseTimestampMs(lastSeenTimestamp)
    ].filter((ts) => ts !== null);

    const latestTelemetryMs = telemetryTimes.length > 0 ? Math.max(...telemetryTimes) : null;
    const hasFreshTelemetry = latestTelemetryMs !== null && (Date.now() - latestTelemetryMs) <= (5 * 60 * 1000);

    const realtimeTrackingState = status.trackingState;
    const realtimeIsActive = status.isActive === true;
    const realtimeServiceRunning = status.lastServiceState?.serviceRunning === true || status.lastServiceState?.isTracking === true;

    // If presence is online and any active signal is recent/true, do not let a stale network flag force offline.
    const hasStrongActiveSignal = isDeviceOnline && (
        realtimeTrackingState === 'active' ||
        realtimeIsActive ||
        realtimeServiceRunning ||
        deviceIsTracking ||
        serviceIsRunning ||
        dataIsActive === true ||
        hasFreshTelemetry
    );

    // Recovery state from backend (via Firestore fcmRecoveryJobs listener)
    const recoveryData = state.fcmPage.recoveryStatus || {};
    const isRecovering = recoveryData.active === true;

    // Check if we have device-reported status data
    const hasRealtimeData = status.trackingState !== undefined ||
                            lastServiceState.serviceRunning !== undefined ||
                            lastServiceState.isTracking !== undefined;
    const hasAnyData = hasRealtimeData || status.updatedAt || status.lastResponseTime || lastDeviceStatus;

    // Determine active state — purely from device-reported data + presence
    let isActive = false;
    let statusReason = '';
    let isWaitingForRealtime = !hasAnyData;

    if (isUninstalled) {
        isActive = false;
        statusReason = uninstallState.reason
            ? `Device marked as uninstalled: ${getReadableFailureReason(uninstallState.reason)}`
            : 'Device marked as uninstalled';
    } else if (isWaitingForRealtime) {
        isActive = false;
        statusReason = 'Checking device status...';
    } else if (!isDeviceOnline) {
        isActive = false;
        if (isRecovering) {
            statusReason = `Device offline — recovery in progress (${recoveryData.totalFcmsSent || 0} FCMs sent)`;
        } else if (lastSeenTimestamp) {
            const offlineMinutes = Math.floor((Date.now() - lastSeenTimestamp) / 60000);
            if (offlineMinutes < 7) {
                statusReason = `Device went offline ${offlineMinutes}m ago — waiting before recovery`;
            } else {
                statusReason = `Device offline for ${offlineMinutes}m`;
            }
        } else {
            statusReason = 'Device offline';
        }
    } else if (isUnreachable) {
        isActive = false;
        statusReason = 'Device unreachable for 24+ hours';
    } else if (hasStrongActiveSignal) {
        isActive = true;
        statusReason = '';
    } else if (trackingPaused && deviceFailureReason) {
        isActive = false;
        statusReason = getFailureReasonText(deviceFailureReason);
    } else if (locationEnabled === false) {
        isActive = false;
        statusReason = 'Location services disabled on device';
    } else if (hasBackgroundPermission === false) {
        isActive = false;
        statusReason = 'Background location permission not granted';
    } else if (hasFinePermission === false) {
        isActive = false;
        statusReason = 'Location permission not granted';
    } else if (networkConnectedFromDevice === false) {
        isActive = false;
        statusReason = 'Network disconnected on device';
    } else {
        isActive = false;
        statusReason = 'Tracking service not running';
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // STATUS STYLING — based on presence + device state. No auto-recovery triggers.
    // ═══════════════════════════════════════════════════════════════════════════
    let statusClass = 'device-status-unknown';
    let statusText = 'Checking Data...';
    let statusIcon = '🔍';

    if (isUninstalled) {
        statusClass = 'device-status-uninstalled';
        statusText = 'Uninstalled';
        statusIcon = '📴';
        isActive = false;
    } else if (isUnreachable) {
        statusClass = 'device-status-unreachable';
        statusText = 'Unreachable';
        statusIcon = icons.wifiOff;
    } else if (!isDeviceOnline && isRecovering) {
        statusClass = 'device-status-offline';
        statusText = `Recovering (${recoveryData.totalFcmsSent || 0} FCMs)`;
        statusIcon = icons.wifiOff;
        isActive = false;
    } else if (!isDeviceOnline) {
        statusClass = 'device-status-offline';
        statusText = 'Offline';
        statusIcon = icons.wifiOff;
        isActive = false;
    } else if (hasStrongActiveSignal) {
        statusClass = 'device-status-active';
        statusText = 'Active & Tracking';
        statusIcon = icons.check;
        isActive = true;
    } else if (networkConnectedFromDevice === false) {
        statusClass = 'device-status-offline';
        statusText = 'Network Offline';
        statusIcon = icons.wifiOff;
    } else if (realtimeTrackingState === 'waiting_location') {
        statusClass = 'device-status-warning';
        statusText = 'Waiting for Location';
        statusIcon = '⏳';
        isActive = false;
    } else if (realtimeTrackingState === 'waiting_permission') {
        statusClass = 'device-status-offline';
        statusText = 'Waiting for Permission';
        statusIcon = '🔒';
        isActive = false;
    } else if (realtimeTrackingState === 'paused' || realtimeTrackingState === 'stopped') {
        statusClass = 'device-status-offline';
        statusText = 'Tracking Paused';
        statusIcon = '⏸️';
        isActive = false;
    } else if (locationEnabled === false) {
        statusClass = 'device-status-offline';
        statusText = 'Location Disabled';
        statusIcon = '📍';
        isActive = false;
    } else if (hasBackgroundPermission === false || hasFinePermission === false) {
        statusClass = 'device-status-offline';
        statusText = 'Permission Missing';
        statusIcon = '🔒';
        isActive = false;
    } else if (hasRealtimeData && realtimeServiceRunning && !realtimeIsActive) {
        statusClass = 'device-status-warning';
        statusText = 'Service Running';
        statusIcon = '⏳';
        isActive = false;
    } else if (isWaitingForRealtime) {
        statusClass = 'device-status-warning';
        statusText = 'Loading...';
        statusIcon = icons.refresh;
    }

    // Extract device info from multiple possible sources
    const deviceInfo = status.lastDeviceState || status.deviceInfo || {};
    const lastDeviceInfo = status.lastDeviceInfo || status.lastFcmResponse?.deviceInfo || {};

    // Get detailed device info - prioritize dedicated device info sources
    const deviceModel = lastDeviceInfo.model || deviceInfo.model || 'Unknown Device';
    const deviceManufacturer = lastDeviceInfo.manufacturer || deviceInfo.manufacturer || '';
    const androidVersion = lastDeviceInfo.androidVersion || deviceInfo.androidVersion || lastDeviceInfo.sdkInt || '';
    const appVersion = lastDeviceInfo.appVersion || deviceInfo.appVersion || '';
    const batteryLevel = deviceInfo.batteryLevel !== undefined ? deviceInfo.batteryLevel : (lastDeviceInfo.batteryLevel || null);
    const isCharging = deviceInfo.isCharging || lastDeviceInfo.isCharging || false;
    const networkAvailable = deviceInfo.networkAvailable !== undefined ? deviceInfo.networkAvailable : (lastDeviceInfo.networkAvailable || null);
    // Use existing locationEnabled and hasBackgroundPermission from earlier in function
    // These are already defined from lastPermissionState

    // Calculate last seen from presence data (authoritative) or fallback to Firestore timestamps
    const lastSeenTime = isDeviceOnline ? null :
        (lastSeenTimestamp ? new Date(lastSeenTimestamp) :
            (status.updatedAt ? new Date(status.updatedAt) : null));
    const communicationSilenceHours = lastSeenTime ?
        ((Date.now() - lastSeenTime.getTime()) / (60 * 60 * 1000)) : null;

    // Format device display name
    const deviceDisplayName = deviceManufacturer ? `${deviceManufacturer} ${deviceModel}` : deviceModel;
    const canSendWake = instantWakeEnabled && !isUninstalled;


    return `
        <!-- Device Status Card -->
        <div class="card mb-4">
            <div class="card-header">
                <h3 class="card-title">${icons.phone} Device Status</h3>
                <button class="btn btn-secondary btn-sm" id="fcm-refresh-btn">
                    ${icons.refresh} Refresh
                </button>
            </div>
            <div class="card-body">
                <div class="device-status-container">
                    <!-- Premium Modern Device Visual -->
                    <div class="device-visual-modern ${statusClass}">
                        <div class="device-outer-frame">
                            <!-- Antenna Lines for Realism -->
                            <div class="device-antenna-top"></div>
                            <div class="device-antenna-bottom"></div>
                            <div class="device-inner-frame">
                                <div class="device-screen-modern">
                                    <!-- Premium Status Bar with Icons -->
                                    <div class="device-statusbar">
                                        <span class="statusbar-time">${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                                        <div class="statusbar-icons">
                                            <!-- Signal Strength Bars -->
                                            <div class="signal-bars">
                                                <div class="signal-bar"></div>
                                                <div class="signal-bar"></div>
                                                <div class="signal-bar" style="${networkAvailable ? '' : 'opacity: 0.3'}"></div>
                                                <div class="signal-bar" style="${networkAvailable ? '' : 'opacity: 0.3'}"></div>
                                            </div>
                                            <!-- Battery Icon -->
                                            <div class="battery-icon">
                                                <div class="battery-body">
                                                    <div class="battery-level" style="width: ${batteryLevel || 80}%; background: ${batteryLevel && batteryLevel <= 20 ? '#EF4444' : isCharging ? '#22C55E' : '#22C55E'}"></div>
                                                </div>
                                                <div class="battery-cap"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Main Content -->
                                    <div class="device-content">
                                        <div class="device-status-badge">
                                            <span class="status-icon-modern">${statusIcon}</span>
                                            <span class="status-text-modern">${statusText}</span>
                                        </div>
                                        <div class="device-app-icon-modern">
                                            <img src="/favicon.svg" alt="App">
                                        </div>
                                        <div class="device-app-name">ConstructConnect</div>
                                    </div>
                                    <!-- Bottom Nav Indicator -->
                                    <div class="device-navbar">
                                        <div class="navbar-indicator"></div>
                                    </div>
                                </div>
                                <!-- Dynamic Island -->
                                <div class="device-dynamic-island"></div>
                            </div>
                            <!-- Side Buttons -->
                            <div class="device-mute"></div>
                            <div class="device-volume-up"></div>
                            <div class="device-volume-down"></div>
                            <div class="device-power"></div>
                        </div>
                        <!-- Premium Pulse Rings (multiple for smoother effect) -->
                        <div class="device-pulse-ring-modern ${isActive ? 'active' : ''}"></div>
                        <div class="device-pulse-ring-modern delay ${isActive ? 'active' : ''}"></div>
                        <div class="device-pulse-ring-modern delay-2 ${isActive ? 'active' : ''}"></div>
                    </div>
                    <!-- Device Info Panel -->
                    <div class="device-details-modern">
                        <!-- Device Identity Section -->
                        <div class="device-name-row">
                            <div class="device-icon-large">📱</div>
                            <div class="device-name-text">
                                <h4>${escapeHtml(deviceDisplayName)}</h4>
                                <span class="device-model">${deviceManufacturer ? escapeHtml(deviceManufacturer) : 'Unknown Device'}</span>
                            </div>
                        </div>
                        <div class="device-meta-badges">
                            ${isUninstalled ? `<span class="meta-badge badge-danger"><span class="badge-icon">📴</span> Uninstalled</span>` : ''}
                            ${androidVersion ? `<span class="meta-badge"><span class="badge-icon">🤖</span> Android ${androidVersion}</span>` : ''}
                            ${appVersion ? `<span class="meta-badge"><span class="badge-icon">📦</span> v${appVersion}</span>` : ''}
                            ${isActive ? `<span class="meta-badge badge-success"><span class="badge-icon">✅</span> Active</span>` :
                                status.trackingPaused ? `<span class="meta-badge badge-warning"><span class="badge-icon">⏸️</span> Paused</span>` :
                                `<span class="meta-badge badge-danger"><span class="badge-icon">⚠️</span> Inactive</span>`}
                        </div>
                        <!-- Device Stats Grid -->
                        <div class="device-stats-grid">
                            <div class="device-stat-item">
                                <span class="stat-label">${icons.clock} Last Data</span>
                                <span class="stat-value ${!lastDataTime ? 'text-muted' : ''}">${lastDataTime ? formatRelativeTime(lastDataTime) : 'Never'}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">${icons.signal} Last Seen</span>
                                <span class="stat-value ${!isDeviceOnline && communicationSilenceHours !== null && communicationSilenceHours >= 1 ? 'text-danger' : ''}">${isDeviceOnline ? '<span style="color:#22c55e">Online now</span>' : (communicationSilenceHours !== null ? (communicationSilenceHours < 0.1 ? 'Just now' : communicationSilenceHours.toFixed(1) + ' hrs ago') : 'Never')}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">🔋 Battery</span>
                                <span class="stat-value">${batteryLevel !== null ? batteryLevel + '%' + (isCharging ? ' ⚡' : '') : 'Unknown'}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">📡 Network</span>
                                <span class="stat-value ${networkAvailable === true ? 'text-success' : networkAvailable === false ? 'text-danger' : ''}">${networkAvailable === true ? 'Online' : networkAvailable === false ? 'Offline' : 'Unknown'}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">📍 GPS Services</span>
                                <span class="stat-value ${locationEnabled === true ? 'text-success' : locationEnabled === false ? 'text-danger' : ''}">${locationEnabled === true ? 'Enabled' : locationEnabled === false ? 'Disabled' : 'Unknown'}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">🔐 Background</span>
                                <span class="stat-value ${hasBackgroundPermission === true ? 'text-success' : hasBackgroundPermission === false ? 'text-danger' : ''}">${hasBackgroundPermission === true ? 'Granted' : hasBackgroundPermission === false ? 'Denied' : 'Unknown'}</span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">🗺️ Last Location</span>
                                <span class="stat-value">
                                    ${status.lastLocation ? `<button class="btn btn-xs btn-primary" onclick="openLocationInMaps(${status.lastLocation.latitude}, ${status.lastLocation.longitude})">View</button>` : 'No data'}
                                </span>
                            </div>
                            <div class="device-stat-item">
                                <span class="stat-label">${icons.zap} Last Active</span>
                                <span class="stat-value">${isDeviceOnline ? '<span style="color:#22c55e">Active now</span>' : (lastSeenTimestamp ? formatRelativeTime(new Date(lastSeenTimestamp)) : (status.updatedAt ? formatRelativeTime(new Date(status.updatedAt)) : (status.lastResponseTime ? formatRelativeTime(new Date(status.lastResponseTime)) : (lastDataTime ? formatRelativeTime(lastDataTime) : 'Never'))))}</span>
                            </div>
                        </div>
                        <!-- Detailed Permission Breakdown -->
                        ${renderPermissionBreakdown(status)}
                        ${renderProtectionSignals(status)}
                        <!-- Last FCM Response Status -->
                        ${status.lastFcmResponse ? `
                        <div class="alert ${status.lastFcmResponse.status === 'SUCCESS' ? 'alert-success' : 'alert-warning'} mt-3">
                            <strong>📥 Last Response: ${status.lastFcmResponse.type || 'Wake'}</strong><br>
                            Status: ${status.lastFcmResponse.status} •
                            Service: ${status.lastFcmResponse.serviceRunning ? '✅' : '❌'}
                            ${status.lastFcmResponse.failureReason ? `<br>⚠️ ${getReadableFailureReason(status.lastFcmResponse.failureReason)}` : ''}
                            ${status.lastFcmResponse.message ? `<br>💬 ${escapeHtml(status.lastFcmResponse.message)}` : ''}
                        </div>
                        ` : ''}
                    </div>
                </div>
                <!-- Status Reason OUTSIDE the phone visual -->
                ${statusReason ? `<div class="device-status-reason mt-3">${escapeHtml(statusReason)}</div>` : ''}
            </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════════════════ -->
        <!-- BACKEND MONITOR — Real-time view of what the backend is doing      -->
        <!-- ═══════════════════════════════════════════════════════════════════ -->
        ${renderBackendMonitor(isDeviceOnline, lastSeenTimestamp, recoveryData, isRecovering)}

        <!-- FCM Controls Card -->
        <div class="card mb-4">
            <div class="card-header">
                <h3 class="card-title">${icons.bell} FCM Wake Controls</h3>
            </div>
            <div class="card-body">
                <!-- Send Button -->
                <div class="fcm-send-section" style="max-width: 100%;">
                    <button class="btn btn-primary btn-lg fcm-send-btn" id="manual-wake-btn" onclick="sendManualWakeFCM()" style="width: 100%; max-width: 500px;" ${canSendWake ? '' : 'disabled'}>
                        ${icons.send} Send Wake FCM Now
                    </button>
                    <p class="fcm-send-hint">
                        ${isUninstalled ? '📴 Wake FCMs are blocked while the device is marked uninstalled.' : '📤 Will send a single WAKE_SERVICE FCM to the device'}
                    </p>
                </div>

                <!-- Success Rate Stats -->
                <div class="fcm-stats-section">
                    <h4 class="stats-title">📊 FCM Statistics</h4>
                    ${(() => {
                        // Compute FCM stats from multiple sources
                        const wakeHistory = state.fcmPage.wakeHistory || [];
                        const responseLogs = state.fcmPage.responseLogs || [];
                        const notifications = state.fcmPage.notifications || [];

                        // Count sent FCMs from wake history
                        const totalSent = status.fcmStats?.totalSent || wakeHistory.length || 0;

                        // Count successful responses
                        const successFromHistory = wakeHistory.filter(w =>
                            w.status === 'success' || w.response?.status === 'SUCCESS'
                        ).length;

                        const successFromResponses = responseLogs.filter(r =>
                            r.status === 'SUCCESS' || r.serviceRunning === true
                        ).length;

                        const successFromNotifications = notifications.filter(n =>
                            n.type === 'FCM_WAKE_SUCCESS' ||
                            n.type === 'FCM_WAKE_RECEIVED' ||
                            n.statusType === 'FCM_WAKE_SUCCESS'
                        ).length;

                        // Use the highest count (avoid undercounting)
                        const totalResponded = status.fcmStats?.totalResponded ||
                            Math.max(successFromHistory, successFromResponses, successFromNotifications);

                        // Calculate success rate
                        const successRate = status.fcmStats?.successRate ||
                            (totalSent > 0 ? Math.round((totalResponded / totalSent) * 100) : 0);

                        return `
                    <div class="fcm-stats-grid">
                        <div class="fcm-stat-card">
                            <span class="stat-number">${totalSent}</span>
                            <span class="stat-label">Total Sent</span>
                        </div>
                        <div class="fcm-stat-card">
                            <span class="stat-number">${totalResponded}</span>
                            <span class="stat-label">Responses</span>
                        </div>
                        <div class="fcm-stat-card ${successRate >= 80 ? 'success' : 'warning'}">
                            <span class="stat-number">${successRate}%</span>
                            <span class="stat-label">Success Rate</span>
                        </div>
                    </div>`;
                    })()}
                </div>
            </div>
        </div>

        <!-- Response/Notification Log -->
        <div class="card">
            <div class="card-header">
                <h3 class="card-title">${icons.history} Response & Notification Log</h3>
            </div>
            <div class="card-body">
                ${renderResponseLogs(responseLogs, status.wakeHistory)}
            </div>
        </div>
    `;
}

function renderResponseLogs(responseLogs, wakeHistory) {
    // Combine all logs: responseLogs, wakeHistory, notifications, and statusLogs
    const notifications = state.fcmPage.notifications || [];
    const statusLogs = state.fcmPage.statusLogs || [];

    const allLogs = [
        ...(responseLogs || []).map(l => ({...l, source: 'response'})),
        ...(wakeHistory || []).map(l => ({...l, source: 'wake'})),
        ...(notifications || []).map(l => ({...l, source: 'notification', type: l.type || 'NOTIFICATION'})),
        ...(statusLogs || []).map(l => ({...l, source: 'status', type: l.statusType || 'STATUS_UPDATE'}))
    ].sort((a, b) => {
        const timeA = a.receivedAt || a.timestamp || a.sentAt || a.createdAt;
        const timeB = b.receivedAt || b.timestamp || b.sentAt || b.createdAt;
        return new Date(timeB) - new Date(timeA);
    }).slice(0, 50);

    if (allLogs.length === 0) {
        return `
            <div class="empty-state small">
                <p>No FCM activity or notifications recorded yet.</p>
                <p class="text-muted">Device status updates will appear here when the device reports changes.</p>
            </div>
        `;
    }

    return `
        <div class="log-list">
            ${allLogs.map(log => {
                const time = log.receivedAt || log.timestamp || log.sentAt || log.createdAt;

                // Determine log type based on source and type
                const isStatusNotification = log.source === 'notification' || log.source === 'status';
                const isSettingsResponse = log.type === 'SETTINGS_RESPONSE';
                const isSettingsSent = log.type === 'SETTINGS_UPDATED';
                const isWakeResponse = log.type === 'WAKE_RESPONSE' || (log.response && !log.type);
                const isSent = log.status === 'sent' || (!log.receivedAt && log.sentAt);

                // Get appropriate icon and title based on status type
                let typeIcon = '📤';
                let typeTitle = 'FCM Sent';

                if (isStatusNotification) {
                    // Handle device status notifications
                    const statusType = log.type || log.statusType || 'STATUS';
                    switch (statusType) {
                        case 'SERVICE_STARTED':
                            typeIcon = '🚀';
                            typeTitle = 'Service Started';
                            break;
                        case 'SERVICE_STOPPED':
                            typeIcon = '🛑';
                            typeTitle = 'Service Stopped';
                            break;
                        case 'TRACKING_RESUMED':
                            typeIcon = '▶️';
                            typeTitle = 'Tracking Resumed';
                            break;
                        case 'TRACKING_PAUSED':
                            typeIcon = '⏸️';
                            typeTitle = 'Tracking Paused';
                            break;
                        case 'FCM_WAKE_RECEIVED':
                            typeIcon = '📥';
                            typeTitle = 'FCM Wake Received';
                            break;
                        case 'FCM_WAKE_SUCCESS':
                            typeIcon = '✅';
                            typeTitle = 'FCM Wake Success';
                            break;
                        case 'FCM_WAKE_FAILED':
                            typeIcon = '❌';
                            typeTitle = 'FCM Wake Failed';
                            break;
                        case 'FCM_WAKE_SENT':
                        case 'FCM_SENT':
                            typeIcon = '📤';
                            typeTitle = 'Single Wake FCM Sent';
                            break;
                        case 'FCM_CYCLE_STARTED':
                            typeIcon = '🔄';
                            typeTitle = '12-Hour Wake Cycle Started';
                            break;
                        case 'SETTINGS_RESPONSE':
                            typeIcon = '⚙️✅';
                            typeTitle = 'Settings Applied';
                            break;
                        case 'SETTINGS_CHANGED':
                            typeIcon = '⚙️';
                            typeTitle = 'Settings Changed';
                            break;
                        case 'PERMISSION_CHANGED':
                            typeIcon = '🔐';
                            typeTitle = 'Permission Changed';
                            break;
                        case 'LOCATION_DISABLED':
                            typeIcon = '📍❌';
                            typeTitle = 'Location Disabled';
                            break;
                        case 'LOCATION_ENABLED':
                            typeIcon = '📍✅';
                            typeTitle = 'Location Enabled';
                            break;
                        case 'NETWORK_LOST':
                            typeIcon = '📡❌';
                            typeTitle = 'Network Lost';
                            break;
                        case 'NETWORK_RESTORED':
                            typeIcon = '📡✅';
                            typeTitle = 'Network Restored';
                            break;
                        case 'HEARTBEAT':
                            typeIcon = '💓';
                            typeTitle = 'Heartbeat';
                            break;
                        case 'APP_OPENED':
                            typeIcon = '📱';
                            typeTitle = 'App Opened';
                            break;
                        case 'APP_CLOSED':
                            typeIcon = '📴';
                            typeTitle = 'App Closed';
                            break;
                        case 'FCM_SENT':
                            typeIcon = '📤';
                            typeTitle = 'Wake FCM Sent';
                            break;
                        case 'FCM_FAILED':
                            typeIcon = '❌';
                            typeTitle = 'Wake FCM Failed';
                            break;
                        case 'FCM_ERROR':
                            typeIcon = '⚠️';
                            typeTitle = 'Wake FCM Error';
                            break;
                        default:
                            typeIcon = '📊';
                            typeTitle = statusType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                    }
                } else if (isSettingsResponse) {
                    typeIcon = '⚙️';
                    typeTitle = 'Settings Response';
                } else if (isSettingsSent) {
                    typeIcon = '📤';
                    typeTitle = 'Settings FCM Sent';
                } else if (isWakeResponse) {
                    typeIcon = '📥';
                    typeTitle = 'Wake Response';
                } else if (isSent) {
                    typeIcon = '📤';
                    typeTitle = log.type === 'SETTINGS_UPDATED' ? 'Settings FCM Sent' : 'Wake FCM Sent';
                }

                // Determine status styling
                // IMPORTANT: Device-reported status updates should show as SUCCESS, not PENDING
                // These are RESPONSES from the device, not outgoing requests
                const isDeviceResponse = [
                    'FCM_WAKE_SUCCESS', 'FCM_WAKE_RECEIVED', 'TRACKING_RESUMED', 'TRACKING_PAUSED',
                    'SERVICE_STARTED', 'SERVICE_STOPPED', 'LOCATION_ENABLED', 'LOCATION_DISABLED',
                    'NETWORK_RESTORED', 'NETWORK_LOST', 'SETTINGS_RESPONSE', 'HEARTBEAT',
                    'APP_OPENED', 'APP_CLOSED'
                ].includes(log.type || log.statusType);

                const isDeviceFailure = ['FCM_WAKE_FAILED', 'FCM_FAILED', 'FCM_ERROR'].includes(log.type || log.statusType);

                let statusClass = 'log-pending';
                let statusIcon = icons.clock;
                let statusText = 'Pending';

                if (isDeviceResponse) {
                    // Device sent us data - this is a SUCCESS response
                    statusClass = 'log-success';
                    statusIcon = icons.check;
                    statusText = 'Success';
                } else if (isDeviceFailure) {
                    // Device reported failure
                    statusClass = 'log-error';
                    statusIcon = icons.x;
                    statusText = 'Failed';
                } else if (log.status === 'success' || log.status === 'SUCCESS') {
                    statusClass = 'log-success';
                    statusIcon = icons.check;
                    statusText = 'Success';
                } else if (log.status === 'failed' || log.status === 'FAILED') {
                    statusClass = 'log-error';
                    statusIcon = icons.x;
                    statusText = 'Failed';
                } else if (log.status === 'sent') {
                    statusClass = 'log-pending';
                    statusIcon = icons.send;
                    statusText = 'Sent (Awaiting Response)';
                } else if (log.status === 'no_response') {
                    statusClass = 'log-warning';
                    statusIcon = icons.alert;
                    statusText = 'No Response';
                }

                // Build device state info
                const deviceState = log.deviceState || log.response?.deviceState;
                const permissionState = log.permissionState || log.response?.permissionState;
                let deviceStateHtml = '';
                if (deviceState || permissionState) {
                    const locEnabled = deviceState?.locationEnabled ?? permissionState?.locationServicesEnabled;
                    const bgPerm = permissionState?.hasBackgroundLocationPermission ?? deviceState?.hasBackgroundPermission;
                    const finePerm = permissionState?.hasFineLocationPermission ?? deviceState?.hasLocationPermission;
                    const network = deviceState?.networkAvailable;
                    const battery = deviceState?.batteryLevel;

                    deviceStateHtml = `
                        <div class="log-device-state">
                            <span>📱 GPS: ${locEnabled === true ? '✅ On' : locEnabled === false ? '❌ Off' : '❓ Unknown'}</span>
                            <span>•</span>
                            <span>🔐 App: ${finePerm === true ? '✅ Granted' : finePerm === false ? '❌ Denied' : '❓ Unknown'}</span>
                            <span>•</span>
                            <span>🔄 BG: ${bgPerm === true ? '✅ Granted' : bgPerm === false ? '❌ Denied' : '❓ Unknown'}</span>
                            <span>•</span>
                            <span>🔋 ${battery !== undefined ? battery + '%' : '? %'}</span>
                            <span>•</span>
                            <span>📡 ${network === true ? '✅ Connected' : network === false ? '❌ Offline' : '❓ Unknown'}</span>
                        </div>
                    `;
                }

                // Build applied settings info
                let appliedSettingsHtml = '';
                if (log.appliedSettings) {
                    const settings = log.appliedSettings;
                    appliedSettingsHtml = `
                        <div class="log-device-state">
                            <span>⚙️ Interval: ${settings.newInterval ? Math.round(settings.newInterval / 1000) + 's' : 'N/A'}</span>
                            <span>•</span>
                            <span>${settings.emergencyMode ? '🔴 Emergency ON' : '⚪ Emergency OFF'}</span>
                            <span>•</span>
                            <span>${settings.realtimeMode ? '🟢 Realtime ON' : '⚪ Realtime OFF'}</span>
                        </div>
                    `;
                }

                // Get readable failure reason
                const failureReasonText = log.failureReason ? getReadableFailureReason(log.failureReason) : null;

                return `
                    <div class="log-item ${statusClass}">
                        <div class="log-icon">${statusIcon}</div>
                        <div class="log-content">
                            <div class="log-header">
                                <span class="log-type">${typeIcon} ${typeTitle}</span>
                                <span class="log-status">${statusText}</span>
                                <span class="log-time">${time ? formatRelativeTime(new Date(time)) : 'Unknown'}</span>
                            </div>
                            ${log.reason ? `<div class="log-reason">Reason: ${escapeHtml(log.reason)}</div>` : ''}
                            ${failureReasonText ? `<div class="log-error-msg">⚠️ ${escapeHtml(failureReasonText)}</div>` : ''}
                            ${log.message ? `<div class="log-message">${escapeHtml(log.message)}</div>` : ''}
                            ${log.wasQueued ? `<div class="log-message"><em>📤 Response was queued (sent when network returned)</em></div>` : ''}
                            ${deviceStateHtml}
                            ${appliedSettingsHtml}
                        </div>
                    </div>
                `;
            }).join('')}
        </div>
    `;
}

// FCM Page Event Handlers
window.setFCMMode = function(mode) {
    // No-op: cycle mode removed. Always single FCM.
};

window.handleInstantWakeToggle = function(enabled) {
    // Toggle just enables/disables the option - doesn't send FCM automatically
    // FCM is only sent when user clicks the "Send Wake FCM Now" button
    state.fcmPage.instantWakeEnabled = enabled;

    if (enabled) {
        toast.info('Instant Wake enabled. Click "Send Wake FCM Now" to send.');
    } else {
        toast.info('Instant Wake disabled.');
    }
};

window.sendManualWakeFCM = async function() {
    const userId = state.fcmPage.userId;
    const uninstallState = state.fcmPage.deviceStatus?.uninstallState || {};

    if (!userId) {
        toast.error('No user selected');
        return;
    }

    if (uninstallState.active === true || state.fcmPage.deviceStatus?.isUninstalled === true) {
        toast.error('This device is marked as uninstalled. Clear the uninstall state before sending FCMs.');
        return;
    }

    if (!confirm('Send a single Wake FCM to this device?\n\nThis will send one wake message to bring the device online.')) {
        return;
    }

    try {
        toast.info('Sending Wake FCM...');

        // Always single mode — no cycle
        const result = await api.adminWakeUser(userId, 'admin_manual_wake', false);

        if (result.success) {
            toast.success('Single Wake FCM sent successfully!');

            // Add to responseLogs for tracking
            state.fcmPage.responseLogs.unshift({
                type: 'wake',
                timestamp: new Date().toISOString(),
                reason: 'admin_instant_wake',
                status: 'pending',
                requestId: result.data?.requestId,
                cycleMode: false
            });

            // Also add to notifications so it shows in the notification log
            state.fcmPage.notifications.unshift({
                id: `fcm_sent_${Date.now()}`,
                type: 'FCM_SENT',
                title: 'Wake FCM Sent',
                message: 'Single wake FCM sent to device',
                status: 'sent',
                reason: 'admin_instant_wake',
                createdAt: new Date(),
                userId: userId
            });

            // Refresh status
            await loadFCMUserStatus(userId);
        } else {
            toast.error(result.message || 'Failed to send FCM');

            // Log failed attempt
            state.fcmPage.notifications.unshift({
                id: `fcm_failed_${Date.now()}`,
                type: 'FCM_FAILED',
                title: 'Wake FCM Failed',
                message: result.message || 'Failed to send FCM',
                status: 'failed',
                createdAt: new Date(),
                userId: userId
            });
            renderApp();
        }
    } catch (error) {
        toast.error(`Error: ${error.message}`);

        // Log error
        state.fcmPage.notifications.unshift({
            id: `fcm_error_${Date.now()}`,
            type: 'FCM_ERROR',
            title: 'Wake FCM Error',
            message: error.message,
            status: 'error',
            createdAt: new Date(),
            userId: userId
        });
        renderApp();
    }
};

window.cancelWakeCycle = async function() {
    const userId = state.fcmPage.userId;
    if (!userId) return;

    if (!confirm('Cancel the active wake cycle for this user?')) {
        return;
    }

    try {
        const result = await api.cancelWakeCycle(userId);
        if (result.success) {
            toast.success('Wake cycle cancelled');
            await loadFCMUserStatus(userId);
        } else {
            toast.error(result.message || 'Failed to cancel');
        }
    } catch (error) {
        toast.error(`Error: ${error.message}`);
    }
};

async function loadFCMUserStatus(userId) {
    // Clean up previous listeners
    stopFCMRealtimeListeners();

    state.fcmPage.isLoading = true;
    state.fcmPage.userId = userId;
    state.fcmPage.lastRefreshTime = new Date();

    // Save userId to localStorage for persistence
    localStorage.setItem('fcm_page_user_id', userId);

    // IMPORTANT: DO NOT reset deviceStatus to null - preserve previous state during refresh
    // This prevents flickering and maintains current device image state
    // Only update when new data arrives

    renderApp();

    try {
        // Get initial user wake status from backend API
        const statusResult = await api.getFCMUserStatus(userId);

        if (statusResult.success) {
            state.fcmPage.deviceStatus = statusResult.data;

            // Response logs are now included in status data
            state.fcmPage.responseLogs = statusResult.data?.responseLogs || [];

            // Notifications from device status reports
            state.fcmPage.notifications = statusResult.data?.notifications || [];

            // Status logs from device
            state.fcmPage.statusLogs = statusResult.data?.statusLogs || [];

            // Get wake history
            try {
                const historyResult = await api.getFCMWakeHistory(userId);
                if (historyResult.success) {
                    state.fcmPage.wakeHistory = historyResult.data?.history || [];
                }
            } catch (e) {
                console.error('Failed to load wake history:', e);
            }

            toast.success('User data loaded - realtime updates enabled');

            // Start realtime listeners for live updates
            startFCMRealtimeListeners(userId);
        } else {
            toast.error(statusResult.message || 'Failed to load user status');
            // Keep previous state on error - don't reset to null
        }
    } catch (error) {
        toast.error(`Error: ${error.message}`);
        // Keep previous state on error - don't reset to null
    } finally {
        state.fcmPage.isLoading = false;
        renderApp();
    }
}

/**
 * Start realtime listeners for FCM page
 * Uses 4 listeners:
 * 1. Presence (Realtime DB) — authoritative online/offline
 * 2. Device Status (Firestore) — detailed device info (display-only)
 * 3. Recovery Status (Firestore) — recovery state from backend (display-only)
 * 4. Notifications (Firestore) — event log (display-only)
 */
function startFCMRealtimeListeners(userId) {
    // Clean up any existing listeners first
    stopFCMRealtimeListeners();

    console.log('📡 Starting FCM realtime listeners for:', userId);

    // Only initialize presence/recovery if not already set (first load).
    // On refresh, keep previous values to prevent UI flicker — listeners overwrite immediately.
    if (!state.fcmPage.presence || state.fcmPage.presence.online === undefined) {
        state.fcmPage.presence = { online: false, lastSeen: null };
    }
    if (!state.fcmPage.recoveryStatus || state.fcmPage.recoveryStatus.status === undefined) {
        state.fcmPage.recoveryStatus = { active: false, status: 'none' };
    }

    // 1. PRESENCE LISTENER — Firebase Realtime Database presence/{userId}
    const presenceUnsubscribe = subscribeToPresence(
        userId,
        (presenceData) => {
            if (state.fcmPage.userId === userId) {
                console.log('📡 Presence update:', presenceData);
                state.fcmPage.presence = presenceData;
                renderApp();
            }
        },
        (error) => {
            console.error('Presence listener error:', error);
        }
    );

    // 2. DEVICE STATUS LISTENER — Firestore locationTrackingSettings/{userId} (display-only)
    const statusUnsubscribe = subscribeToDeviceStatus(
        userId,
        (statusData) => {
            if (statusData && state.fcmPage.userId === userId) {
                console.log('📡 Device status update:', statusData.trackingState);

                if (state.fcmPage.deviceStatus) {
                    state.fcmPage.deviceStatus = {
                        ...state.fcmPage.deviceStatus,
                        trackingState: statusData.trackingState,
                        trackingStateMessage: statusData.trackingStateMessage,
                        isActive: statusData.isActive,


                        lastServiceState: {
                            isTracking: statusData.serviceRunning,
                            serviceRunning: statusData.serviceRunning,
                            trackingPaused: statusData.trackingState === 'paused' || statusData.trackingState === 'waiting_location' || statusData.trackingState === 'waiting_permission'
                        },

                        lastPermissionState: {
                            hasFineLocation: statusData.hasFineLocation,
                            hasBackgroundLocation: statusData.hasBackgroundLocation,
                            isLocationEnabled: statusData.locationServicesEnabled,
                            accessibilityEnabled: statusData.accessibilityEnabled
                        },

                        lastDeviceState: {
                            batteryLevel: statusData.batteryLevel,
                            isCharging: statusData.batteryCharging,
                            networkAvailable: statusData.networkConnected,
                            networkType: statusData.networkType,
                            locationEnabled: statusData.locationServicesEnabled,
                            hasBackgroundPermission: statusData.hasBackgroundLocation,
                            hasFinePermission: statusData.hasFineLocation
                        },

                        lastDeviceInfo: {
                            model: statusData.deviceModel,
                            manufacturer: statusData.deviceManufacturer,
                            androidVersion: statusData.androidVersion,
                            sdkInt: statusData.sdkVersion,
                            appVersion: statusData.appVersion,
                            deviceName: statusData.deviceName
                        },

                        lastDeviceStatus: {
                            statusType: statusData.lastStatusType,
                            message: statusData.lastStatusMessage,
                            failureReason: statusData.lastFailureReason
                        },

                        uploadSuccessRate: statusData.uploadSuccessRate,
                        fcmResponseRate: statusData.fcmResponseRate,
                        locationCount24h: statusData.locationCount24h,
                        isUninstalled: statusData.isUninstalled,
                        uninstallState: statusData.uninstallState,
                        uninstallUpdatedAt: statusData.uninstallUpdatedAt,
                        isDeviceAdminEnabled: statusData.isDeviceAdminEnabled,
                        deviceAdminEnabled: statusData.deviceAdminEnabled,
                        deviceAdminState: statusData.deviceAdminState,
                        deviceAdminUpdatedAt: statusData.deviceAdminUpdatedAt,
                        accessibilityEnabled: statusData.accessibilityEnabled,

                        lastResponseTime: statusData.updatedAt,
                        updatedAt: statusData.updatedAt,

                        lastFcmResponse: state.fcmPage.deviceStatus.lastFcmResponse,
                        fcmWakeState: state.fcmPage.deviceStatus.fcmWakeState
                    };
                } else {
                    state.fcmPage.deviceStatus = {
                        trackingState: statusData.trackingState,
                        trackingStateMessage: statusData.trackingStateMessage,
                        isActive: statusData.isActive,
                        lastServiceState: {
                            isTracking: statusData.serviceRunning,
                            serviceRunning: statusData.serviceRunning,
                            trackingPaused: statusData.trackingState === 'paused' || statusData.trackingState === 'waiting_location' || statusData.trackingState === 'waiting_permission'
                        },
                        lastPermissionState: {
                            hasFineLocation: statusData.hasFineLocation,
                            hasBackgroundLocation: statusData.hasBackgroundLocation,
                            isLocationEnabled: statusData.locationServicesEnabled,
                            accessibilityEnabled: statusData.accessibilityEnabled
                        },
                        lastDeviceState: {
                            batteryLevel: statusData.batteryLevel,
                            isCharging: statusData.batteryCharging,
                            networkAvailable: statusData.networkConnected,
                            networkType: statusData.networkType,
                            locationEnabled: statusData.locationServicesEnabled,
                            hasBackgroundPermission: statusData.hasBackgroundLocation,
                            hasFinePermission: statusData.hasFineLocation
                        },
                        lastDeviceInfo: {
                            model: statusData.deviceModel,
                            manufacturer: statusData.deviceManufacturer,
                            androidVersion: statusData.androidVersion,
                            sdkInt: statusData.sdkVersion,
                            appVersion: statusData.appVersion,
                            deviceName: statusData.deviceName
                        },
                        lastDeviceStatus: {
                            statusType: statusData.lastStatusType,
                            message: statusData.lastStatusMessage,
                            failureReason: statusData.lastFailureReason
                        },
                        uploadSuccessRate: statusData.uploadSuccessRate,
                        fcmResponseRate: statusData.fcmResponseRate,
                        locationCount24h: statusData.locationCount24h,
                        isUninstalled: statusData.isUninstalled,
                        uninstallState: statusData.uninstallState,
                        uninstallUpdatedAt: statusData.uninstallUpdatedAt,
                        isDeviceAdminEnabled: statusData.isDeviceAdminEnabled,
                        deviceAdminEnabled: statusData.deviceAdminEnabled,
                        deviceAdminState: statusData.deviceAdminState,
                        deviceAdminUpdatedAt: statusData.deviceAdminUpdatedAt,
                        accessibilityEnabled: statusData.accessibilityEnabled,
                        lastResponseTime: statusData.updatedAt,
                        updatedAt: statusData.updatedAt
                    };
                }

                state.fcmPage.lastRefreshTime = new Date();
                renderApp();
            }
        },
        (error) => {
            console.error('FCM status listener error:', error);
        }
    );

    // 3. RECOVERY STATUS LISTENER — Firestore fcmRecoveryJobs/{userId} (display-only)
    const recoveryUnsubscribe = subscribeToRecoveryStatus(
        userId,
        (recoveryData) => {
            if (state.fcmPage.userId === userId) {
                console.log('📡 Recovery status update:', recoveryData);
                state.fcmPage.recoveryStatus = recoveryData;
                renderApp();
            }
        },
        (error) => {
            console.error('Recovery status listener error:', error);
        }
    );

    // 4. NOTIFICATIONS LISTENER — Firestore deviceNotifications (display-only)
    const notificationsUnsubscribe = subscribeToDeviceNotifications(
        userId,
        (notifications) => {
            if (state.fcmPage.userId === userId) {
                state.fcmPage.notifications = notifications;
                state.fcmPage.lastRefreshTime = new Date();
                renderApp();
            }
        },
        (error) => {
            console.error('FCM notifications listener error:', error);
        }
    );

    // Store unsubscribe functions
    state.fcmPage.fcmStatusUnsubscribe = () => {
        presenceUnsubscribe();
        statusUnsubscribe();
        recoveryUnsubscribe();
        notificationsUnsubscribe();
    };

    // Start 1-second ticker for backend monitor countdowns (grace period, next FCM)
    if (state.fcmPage.backendMonitorInterval) {
        clearInterval(state.fcmPage.backendMonitorInterval);
    }
    state.fcmPage.backendMonitorInterval = setInterval(() => {
        updateBackendMonitorCountdowns();
    }, 1000);
}

/**
 * Stop realtime listeners for FCM page
 */
function stopFCMRealtimeListeners() {
    if (state.fcmPage.fcmStatusUnsubscribe) {
        console.log('🛑 Stopping FCM realtime listeners');
        state.fcmPage.fcmStatusUnsubscribe();
        state.fcmPage.fcmStatusUnsubscribe = null;
    }
    // Stop the backend monitor ticker
    if (state.fcmPage.backendMonitorInterval) {
        clearInterval(state.fcmPage.backendMonitorInterval);
        state.fcmPage.backendMonitorInterval = null;
    }
    // NOTE: Do NOT reset presence/recovery state here.
    // Keep last known values so the UI doesn't flicker during refresh.
    // The new listeners will overwrite them immediately on reconnect.
}

// Attach FCM page event listeners ---
function attachFCMEventListeners() {
    const loadBtn = document.getElementById('fcm-load-user-btn');
    const userIdInput = document.getElementById('fcm-user-id');
    const refreshBtn = document.getElementById('fcm-refresh-btn');

    if (loadBtn && userIdInput) {
        loadBtn.addEventListener('click', () => {
            const userId = userIdInput.value.trim();
            if (!userId) {
                toast.warning('Please enter a User ID');
                return;
            }
            loadFCMUserStatus(userId);
        });

        userIdInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                loadBtn.click();
            }
        });
    }

    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
            if (state.fcmPage.userId) {
                loadFCMUserStatus(state.fcmPage.userId);
            }
        });
    }
}
